import React, { useState, useEffect, useCallback, useMemo } from 'react';

// ============ STRATEGIC CONSTANTS ============

const MILESTONES = [
  { name: 'Side Hustle', icon: '🌱', threshold: 0, color: '#84cc16' },
  { name: 'Small Business', icon: '🏠', threshold: 25000, color: '#22c55e' },
  { name: 'Growing Agency', icon: '🏢', threshold: 100000, color: '#3b82f6' },
  { name: 'Established Firm', icon: '🌟', threshold: 300000, color: '#8b5cf6' },
  { name: 'Industry Leader', icon: '🚀', threshold: 700000, color: '#f59e0b' },
  { name: 'Millionaire', icon: '💎', threshold: 1000000, color: '#ec4899' },
];

// 15 Achievement Milestones
const ACHIEVEMENTS = [
  { id: 'first_lead', name: 'First Contact', desc: 'Generate your first lead', icon: '📞', check: (s) => s.totalLeadsGenerated >= 1 },
  { id: 'first_client', name: 'First Client', desc: 'Close your first client', icon: '🤝', check: (s) => s.totalClientsWon >= 1 },
  { id: 'first_hire', name: 'Team Builder', desc: 'Hire your first employee', icon: '👤', check: (s) => s.totalHires >= 1 },
  { id: 'retain_3months', name: 'Keeper', desc: 'Retain a client for 3 months', icon: '💕', check: (s) => s.longestClientRetention >= 3 },
  { id: 'five_clients', name: 'Growing Portfolio', desc: 'Have 5 active clients', icon: '📊', check: (s) => s.maxClients >= 5 },
  { id: 'ten_pitches', name: 'Sales Machine', desc: 'Complete 10 pitches', icon: '🎯', check: (s) => s.totalPitches >= 10 },
  { id: 'fifty_close', name: 'Closer', desc: '50% pitch close rate (min 6 pitches)', icon: '✅', check: (s) => s.totalPitches >= 6 && (s.totalClientsWon / s.totalPitches) >= 0.5 },
  { id: 'first_10k', name: 'Five Figures', desc: 'Reach $10,000 cash', icon: '💵', check: (s) => s.peakCash >= 10000 },
  { id: 'first_50k', name: 'Momentum', desc: 'Reach $50,000 cash', icon: '💰', check: (s) => s.peakCash >= 50000 },
  { id: 'team_of_five', name: 'Department Head', desc: 'Have 5 employees at once', icon: '👥', check: (s) => s.maxEmployees >= 5 },
  { id: 'retain_6months', name: 'Loyalty', desc: 'Retain a client for 6 months', icon: '❤️', check: (s) => s.longestClientRetention >= 6 },
  { id: 'perfect_pitch', name: 'Perfect Pitch', desc: 'Close with 100% conviction', icon: '🏆', check: (s) => s.perfectPitches >= 1 },
  { id: 'hundred_k', name: 'Six Figures', desc: 'Reach $100,000 cash', icon: '🏆', check: (s) => s.peakCash >= 100000 },
  { id: 'five_hundred_k', name: 'Half Million', desc: 'Reach $500,000 cash', icon: '💎', check: (s) => s.peakCash >= 500000 },
  { id: 'millionaire', name: 'Marketing Millionaire', desc: 'Reach $1,000,000 cash', icon: '👑', check: (s) => s.peakCash >= 1000000 },
];

// Market conditions that rotate and affect strategy
const MARKET_CONDITIONS = [
  { id: 'boom', name: 'Economic Boom', icon: '📈', desc: 'Budgets up 30%, competition fierce', budgetMult: 1.3, leadDecay: 1.3, rivalAggression: 1.4 },
  { id: 'recession', name: 'Recession', icon: '📉', desc: 'Budgets down 25%, clients cautious', budgetMult: 0.75, leadDecay: 0.8, rivalAggression: 0.7 },
  { id: 'disruption', name: 'Market Disruption', icon: '💥', desc: 'New tech! Early adopters thrive', budgetMult: 1.1, leadDecay: 1.5, rivalAggression: 1.0, techBonus: 1.5 },
  { id: 'stable', name: 'Stable Market', icon: '⚖️', desc: 'Normal conditions, reputation matters', budgetMult: 1.0, leadDecay: 1.0, rivalAggression: 1.0, repBonus: 1.3 },
  { id: 'competitive', name: 'Price War', icon: '⚔️', desc: 'Rivals undercut aggressively', budgetMult: 0.9, leadDecay: 1.2, rivalAggression: 1.8, priceMatters: true },
];

// Industries with strategic depth
const INDUSTRIES = [
  { id: 'restaurant', name: 'Restaurant', icon: '🍕', avgBudget: 1800, volatility: 0.4, seasonality: [1.2, 1.0, 0.9, 1.3], loyalty: 0.6 },
  { id: 'dental', name: 'Dental', icon: '🦷', avgBudget: 3500, volatility: 0.15, seasonality: [1.0, 1.0, 1.0, 1.0], loyalty: 0.85 },
  { id: 'legal', name: 'Law Firm', icon: '⚖️', avgBudget: 6000, volatility: 0.2, seasonality: [1.1, 1.0, 0.9, 1.0], loyalty: 0.9 },
  { id: 'realestate', name: 'Real Estate', icon: '🏠', avgBudget: 5000, volatility: 0.5, seasonality: [0.8, 1.3, 1.2, 0.7], loyalty: 0.5 },
  { id: 'fitness', name: 'Fitness', icon: '💪', avgBudget: 2200, volatility: 0.35, seasonality: [1.4, 1.0, 0.8, 1.1], loyalty: 0.55 },
  { id: 'tech', name: 'Tech Startup', icon: '💻', avgBudget: 7000, volatility: 0.6, seasonality: [1.0, 1.1, 1.1, 0.9], loyalty: 0.4 },
  { id: 'retail', name: 'Retail', icon: '🛍️', avgBudget: 2000, volatility: 0.45, seasonality: [0.7, 0.8, 0.9, 1.5], loyalty: 0.5 },
  { id: 'medical', name: 'Medical', icon: '🏥', avgBudget: 5500, volatility: 0.1, seasonality: [1.0, 1.0, 1.0, 1.0], loyalty: 0.95 },
  { id: 'construction', name: 'Construction', icon: '🏗️', avgBudget: 4000, volatility: 0.3, seasonality: [0.7, 1.2, 1.3, 0.8], loyalty: 0.7 },
  { id: 'finance', name: 'Finance', icon: '💰', avgBudget: 8000, volatility: 0.25, seasonality: [1.1, 1.0, 0.9, 1.1], loyalty: 0.8 },
];

// Specializations you can choose (pick 2-3)
const SPECIALIZATIONS = [
  { id: 'local', name: 'Local SEO', icon: '📍', industries: ['restaurant', 'dental', 'fitness', 'retail'], bonus: 1.4, desc: '+40% effectiveness for local businesses' },
  { id: 'b2b', name: 'B2B Marketing', icon: '🏢', industries: ['legal', 'tech', 'finance', 'construction'], bonus: 1.35, desc: '+35% for B2B clients' },
  { id: 'social', name: 'Social Media', icon: '📱', industries: ['restaurant', 'fitness', 'retail'], bonus: 1.5, desc: '+50% for social-heavy industries' },
  { id: 'content', name: 'Content Marketing', icon: '✍️', industries: ['tech', 'medical', 'finance'], bonus: 1.45, desc: '+45% for content-driven industries' },
  { id: 'ppc', name: 'Paid Advertising', icon: '📺', industries: ['legal', 'realestate', 'medical'], bonus: 1.4, desc: '+40% for high-budget PPC clients' },
  { id: 'brand', name: 'Brand Strategy', icon: '🎨', industries: ['tech', 'retail', 'fitness'], bonus: 1.35, desc: '+35% for brand-focused work' },
];

// Lead temperature system
const LEAD_TEMPS = [
  { temp: 'hot', icon: '🔥', name: 'Hot', decay: 0, conversionMod: 1.3, color: '#ef4444' },
  { temp: 'warm', icon: '☀️', name: 'Warm', decay: 1, conversionMod: 1.0, color: '#f59e0b' },
  { temp: 'cool', icon: '❄️', name: 'Cool', decay: 2, conversionMod: 0.7, color: '#3b82f6' },
  { temp: 'cold', icon: '🧊', name: 'Cold', decay: 3, conversionMod: 0.35, color: '#64748b' },
];

// Campaign types for clients
const CAMPAIGN_TYPES = [
  { id: 'awareness', name: 'Brand Awareness', icon: '📣', cost: 0.15, risk: 0.2, minResult: 0.6, maxResult: 1.2, duration: 2, industries: ['retail', 'fitness', 'restaurant'] },
  { id: 'leadgen', name: 'Lead Generation', icon: '🎯', cost: 0.2, risk: 0.35, minResult: 0.5, maxResult: 1.5, duration: 1, industries: ['legal', 'realestate', 'medical'] },
  { id: 'retention', name: 'Customer Retention', icon: '❤️', cost: 0.1, risk: 0.15, minResult: 0.8, maxResult: 1.1, duration: 3, industries: ['dental', 'medical', 'finance'] },
  { id: 'launch', name: 'Product Launch', icon: '🚀', cost: 0.35, risk: 0.5, minResult: 0.3, maxResult: 2.0, duration: 1, industries: ['tech', 'retail'] },
  { id: 'local', name: 'Local Domination', icon: '📍', cost: 0.18, risk: 0.25, minResult: 0.7, maxResult: 1.3, duration: 2, industries: ['restaurant', 'dental', 'fitness', 'construction'] },
  { id: 'digital', name: 'Digital Transformation', icon: '💻', cost: 0.25, risk: 0.4, minResult: 0.4, maxResult: 1.6, duration: 3, industries: ['finance', 'medical', 'legal'] },
];

// Client personalities affect strategy
const PERSONALITIES = [
  { type: 'Hands-Off', icon: '🧘', difficulty: 0.7, patience: 4, demandGrowth: 0.05, tip: 'Trusts you, but expects results' },
  { type: 'Micromanager', icon: '🔬', difficulty: 1.4, patience: 2, demandGrowth: 0.15, tip: 'Needs constant updates' },
  { type: 'Data-Driven', icon: '📊', difficulty: 1.2, patience: 3, demandGrowth: 0.1, tip: 'Show metrics or lose them' },
  { type: 'Impatient', icon: '⏰', difficulty: 1.3, patience: 1, demandGrowth: 0.2, tip: 'Wants results NOW' },
  { type: 'Budget-Hawk', icon: '💵', difficulty: 1.5, patience: 3, demandGrowth: 0.08, tip: 'Constantly negotiates' },
  { type: 'Visionary', icon: '✨', difficulty: 0.9, patience: 4, demandGrowth: 0.12, tip: 'Big ideas, flexible on details' },
  { type: 'Skeptic', icon: '🤨', difficulty: 1.6, patience: 2, demandGrowth: 0.1, tip: 'Hard to win, loyal once convinced' },
  { type: 'Networker', icon: '🤝', difficulty: 0.8, patience: 3, demandGrowth: 0.05, tip: 'Easy win, great referrals' },
];

// OVERHAULED Employee system with specific attribute bonuses
const EMPLOYEE_TYPES = [
  { 
    role: 'Operations Manager', icon: '⚙️', skills: ['efficiency', 'planning'], 
    cost: 4000, salary: 1600, desc: '+1 action per month',
    bonusActions: 1, autoClose: 0, clientBonus: 0, campaignBonus: 0, leadBonus: 0,
  },
  { 
    role: 'Sales Closer', icon: '🎯', skills: ['pitch', 'closing'], 
    cost: 5000, salary: 1800, desc: '15% auto-close on warm+ leads',
    bonusActions: 0, autoClose: 15, clientBonus: 0, campaignBonus: 0, leadBonus: 0,
  },
  { 
    role: 'Account Manager', icon: '❤️', skills: ['retention', 'upsell'], 
    cost: 3500, salary: 1400, desc: '+20% client satisfaction',
    bonusActions: 0, autoClose: 0, clientBonus: 20, campaignBonus: 0, leadBonus: 0,
  },
  { 
    role: 'Campaign Strategist', icon: '📈', skills: ['strategy', 'optimization'], 
    cost: 4500, salary: 1700, desc: '+25% campaign success rate',
    bonusActions: 0, autoClose: 0, clientBonus: 0, campaignBonus: 25, leadBonus: 0,
  },
  { 
    role: 'Lead Generator', icon: '📣', skills: ['outreach', 'nurture'], 
    cost: 3000, salary: 1200, desc: '+1 lead per prospecting action',
    bonusActions: 0, autoClose: 0, clientBonus: 0, campaignBonus: 0, leadBonus: 1,
  },
  { 
    role: 'Executive Assistant', icon: '📋', skills: ['admin', 'support'], 
    cost: 2500, salary: 1000, desc: '+2 actions per month',
    bonusActions: 2, autoClose: 0, clientBonus: 0, campaignBonus: 0, leadBonus: 0,
  },
];

const EMPLOYEE_PERSONALITIES = [
  { type: 'Star', icon: '⭐', prodMult: 1.4, costMult: 1.5, turnoverRisk: 0.15 },
  { type: 'Reliable', icon: '🛡️', prodMult: 1.0, costMult: 1.0, turnoverRisk: 0.03 },
  { type: 'Growing', icon: '🌱', prodMult: 0.7, costMult: 0.8, turnoverRisk: 0.05 },
  { type: 'Veteran', icon: '🎖️', prodMult: 1.2, costMult: 1.3, turnoverRisk: 0.02 },
];

// Rival strategies
const RIVALS = [
  { id: 'shark', name: 'Apex Marketing', icon: '🦈', strategy: 'aggressive', desc: 'Undercuts prices, steals leads' },
  { id: 'boutique', name: 'Prestige Partners', icon: '💎', strategy: 'premium', desc: 'Targets only big fish' },
  { id: 'machine', name: 'Scale Systems', icon: '🤖', strategy: 'volume', desc: 'Mass market, thin margins' },
];

// Expanded names for networking game
const NETWORKING_NAMES = [
  'Sarah', 'Mike', 'Jennifer', 'David', 'Emma', 'James', 'Lisa', 'Robert', 
  'Maria', 'Thomas', 'Amanda', 'Chris', 'Rachel', 'Kevin', 'Nicole', 'Brandon',
  'Ashley', 'Daniel', 'Jessica', 'Matthew', 'Stephanie', 'Andrew', 'Megan', 'Joshua',
  'Lauren', 'Ryan', 'Emily', 'Tyler', 'Samantha', 'Jacob', 'Olivia', 'Ethan'
];

// Ad puzzle elements
const AD_PLATFORMS = [
  { id: 'facebook', name: 'Facebook', icon: '📘', audience: 'broad' },
  { id: 'google', name: 'Google Ads', icon: '🔍', audience: 'intent' },
  { id: 'instagram', name: 'Instagram', icon: '📸', audience: 'visual' },
  { id: 'linkedin', name: 'LinkedIn', icon: '💼', audience: 'b2b' },
  { id: 'tiktok', name: 'TikTok', icon: '🎵', audience: 'young' },
  { id: 'youtube', name: 'YouTube', icon: '▶️', audience: 'video' },
];

const AD_OBJECTIVES = [
  { id: 'awareness', name: 'Brand Awareness', best: ['facebook', 'instagram', 'youtube'] },
  { id: 'traffic', name: 'Website Traffic', best: ['google', 'facebook'] },
  { id: 'leads', name: 'Lead Generation', best: ['facebook', 'linkedin', 'google'] },
  { id: 'sales', name: 'Direct Sales', best: ['google', 'facebook'] },
  { id: 'engagement', name: 'Engagement', best: ['instagram', 'tiktok'] },
];

const AD_AUDIENCES = [
  { id: 'broad', name: 'Broad Audience', best: ['facebook', 'youtube'] },
  { id: 'retarget', name: 'Retargeting', best: ['facebook', 'google'] },
  { id: 'lookalike', name: 'Lookalike', best: ['facebook', 'instagram'] },
  { id: 'interest', name: 'Interest-Based', best: ['facebook', 'instagram', 'tiktok'] },
  { id: 'keyword', name: 'Keyword Targeting', best: ['google'] },
  { id: 'professional', name: 'Professional', best: ['linkedin'] },
];

const AD_POWERUPS = [
  { id: 'hint', name: 'Hint', icon: '💡', desc: 'Reveals correct answer', uses: 2 },
  { id: 'skip', name: 'Skip', icon: '⏭️', desc: 'Skip this question', uses: 1 },
  { id: 'double', name: 'Double', icon: '2️⃣', desc: 'Double next correct', uses: 1 },
];

// Employee training options
const TRAINING_TYPES = [
  { id: 'skills', name: 'Skills Workshop', icon: '📚', cost: 800, effectivenessBoost: 10, moraleBoost: 5, desc: '+10% effectiveness' },
  { id: 'leadership', name: 'Leadership Course', icon: '👔', cost: 1500, effectivenessBoost: 15, moraleBoost: 10, desc: '+15% effectiveness' },
  { id: 'certification', name: 'Certification', icon: '🎓', cost: 3000, effectivenessBoost: 25, moraleBoost: 15, desc: '+25% effectiveness' },
];

// Bonus options for employees
const BONUS_TYPES = [
  { id: 'small', name: 'Spot Bonus', icon: '💵', cost: 300, moraleBoost: 15, desc: 'Quick morale boost' },
  { id: 'medium', name: 'Performance Bonus', icon: '💰', cost: 800, moraleBoost: 30, desc: 'Solid recognition' },
  { id: 'large', name: 'Retention Bonus', icon: '🏆', cost: 2000, moraleBoost: 50, loyaltyMonths: 3, desc: '+3 months loyalty' },
];

// Client demands that appear randomly
const CLIENT_DEMANDS = [
  { id: 'meeting', name: 'Strategy Meeting', icon: '📅', actionCost: 1, satisfactionGain: 10, ignoreEffect: -15, chance: 0.3, personalities: ['Micromanager', 'Hands-Off'] },
  { id: 'report', name: 'Detailed Report', icon: '📊', actionCost: 1, satisfactionGain: 12, ignoreEffect: -12, chance: 0.25, personalities: ['Data-Driven', 'Micromanager'] },
  { id: 'discount', name: 'Budget Reduction', icon: '💸', budgetCut: 0.15, satisfactionGain: 15, ignoreEffect: -25, chance: 0.2, personalities: ['Budget-Hawk'] },
  { id: 'rush', name: 'Rush Project', icon: '⚡', actionCost: 2, cashCost: 200, satisfactionGain: 18, ignoreEffect: -30, chance: 0.15, personalities: ['Impatient'] },
  { id: 'pivot', name: 'Strategy Change', icon: '🔄', actionCost: 1, successChance: 0.6, satisfactionGain: 20, failEffect: -25, ignoreEffect: -20, chance: 0.2, personalities: ['Visionary'] },
  { id: 'complaint', name: 'Service Complaint', icon: '😤', actionCost: 1, cashCost: 300, satisfactionGain: 20, ignoreEffect: -40, chance: 0.15, personalities: ['Skeptic'] },
];

// Campaign events that require decisions
const CAMPAIGN_EVENTS = [
  { id: 'underperform', name: 'Underperforming Ads', icon: '📉', chance: 0.25,
    options: [
      { text: 'Increase budget (+$500)', cost: 500, successChance: 0.7, successEffect: 12, failEffect: -10 },
      { text: 'Optimize targeting (1⚡)', actionCost: 1, successChance: 0.6, successEffect: 15, failEffect: -8 },
      { text: 'Ride it out', successChance: 0.35, successEffect: 5, failEffect: -20 },
    ]
  },
  { id: 'competitor', name: 'Competitor Attack', icon: '⚔️', chance: 0.2,
    options: [
      { text: 'Counter-campaign (+$800)', cost: 800, successChance: 0.65, successEffect: 15, failEffect: -8 },
      { text: 'Differentiate (1⚡)', actionCost: 1, successChance: 0.7, successEffect: 12, failEffect: -5 },
      { text: 'Ignore', successChance: 0.4, successEffect: 0, failEffect: -18 },
    ]
  },
  { id: 'viral', name: 'Viral Opportunity', icon: '🔥', chance: 0.1,
    options: [
      { text: 'Go all in (+$600, 1⚡)', cost: 600, actionCost: 1, successChance: 0.5, successEffect: 30, failEffect: -5 },
      { text: 'Careful approach', successChance: 0.8, successEffect: 12, failEffect: 0 },
      { text: 'Pass', successChance: 1.0, successEffect: 0, failEffect: 0 },
    ]
  },
  { id: 'fatigue', name: 'Creative Fatigue', icon: '😴', chance: 0.3,
    options: [
      { text: 'New creatives (+$400)', cost: 400, successChance: 0.85, successEffect: 12, failEffect: -5 },
      { text: 'A/B test (1⚡)', actionCost: 1, successChance: 0.7, successEffect: 10, failEffect: -8 },
      { text: 'Keep running', successChance: 0.25, successEffect: 0, failEffect: -22 },
    ]
  },
  { id: 'algorithm', name: 'Algorithm Change', icon: '🤖', chance: 0.15,
    options: [
      { text: 'Adapt quickly (+$300, 1⚡)', cost: 300, actionCost: 1, successChance: 0.75, successEffect: 15, failEffect: -8 },
      { text: 'Wait and see', successChance: 0.4, successEffect: 5, failEffect: -25 },
      { text: 'Diversify (+$500)', cost: 500, successChance: 0.8, successEffect: 10, failEffect: -5 },
    ]
  },
];

const FIRST_NAMES = ['Sarah', 'Mike', 'Jennifer', 'David', 'Emma', 'James', 'Lisa', 'Robert', 'Maria', 'Thomas', 'Amanda', 'Chris', 'Rachel', 'Kevin', 'Nicole', 'Brandon'];
const LAST_NAMES = ['Chen', 'Smith', 'Johnson', 'Williams', 'Garcia', 'Miller', 'Davis', 'Martinez', 'Anderson', 'Taylor', 'Wilson', 'Moore', 'Jackson', 'White'];
const BUSINESS_SUFFIXES = ['& Co', 'Group', 'Solutions', 'Partners', 'LLC', 'Inc', 'Services', 'Pro', 'Plus', 'Hub'];

// ============ HELPERS ============

const pick = arr => arr[Math.floor(Math.random() * arr.length)];
const randInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;
const randFloat = (min, max) => min + Math.random() * (max - min);
const shuffle = arr => [...arr].sort(() => Math.random() - 0.5);

const getMilestone = (cash) => {
  for (let i = MILESTONES.length - 1; i >= 0; i--) {
    if (cash >= MILESTONES[i].threshold) return { current: MILESTONES[i], next: MILESTONES[i + 1] || null, index: i };
  }
  return { current: MILESTONES[0], next: MILESTONES[1], index: 0 };
};

const getQuarter = (month) => Math.floor((month - 1) % 12 / 3);

const genLead = (rep, specializations, market) => {
  const industry = pick(INDUSTRIES);
  const personality = pick(PERSONALITIES);
  const firstName = pick(FIRST_NAMES);
  
  // Budget affected by market, seasonality, and randomness
  const quarter = getQuarter(1);
  const seasonMult = industry.seasonality[quarter];
  const marketMult = market?.budgetMult || 1;
  const volatility = 1 + (Math.random() - 0.5) * industry.volatility;
  const baseBudget = industry.avgBudget * seasonMult * marketMult * volatility;
  
  // Check if we specialize in this industry
  const isSpecialized = specializations.some(s => 
    SPECIALIZATIONS.find(spec => spec.id === s)?.industries.includes(industry.id)
  );
  
  return {
    id: `l_${Date.now()}_${Math.random()}`,
    name: `${firstName} ${pick(LAST_NAMES)}`,
    businessName: `${firstName}'s ${industry.name} ${pick(BUSINESS_SUFFIXES)}`,
    firstName,
    industry: industry.id,
    industryData: industry,
    personality: personality.type,
    personalityData: personality,
    budget: Math.floor(baseBudget),
    temp: Math.random() < 0.3 ? 'hot' : Math.random() < 0.6 ? 'warm' : 'cool',
    tempAge: 0,
    source: 'organic',
    isSpecialized,
    needs: generateClientNeeds(industry),
    createdMonth: 1,
  };
};

const generateClientNeeds = (industry) => {
  const allNeeds = ['leads', 'awareness', 'retention', 'reputation', 'sales', 'traffic'];
  const count = randInt(2, 3);
  const shuffled = allNeeds.sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
};

const genEmployee = () => {
  const type = pick(EMPLOYEE_TYPES);
  const pers = pick(EMPLOYEE_PERSONALITIES);
  return {
    id: `e_${Date.now()}_${Math.random()}`,
    name: `${pick(FIRST_NAMES)} ${pick(LAST_NAMES)}`,
    ...type,
    personality: pers.type,
    personalityData: pers,
    cost: Math.floor(type.cost * pers.costMult),
    salary: Math.floor(type.salary * pers.costMult),
    productivity: pers.prodMult,
    morale: 100,
    level: 1,
    monthsEmployed: 0,
    trainingLevel: 0, // 0-100, increases effectiveness
    loyaltyMonths: 0, // Months where they won't quit
    // Apply productivity multiplier to bonuses
    effectiveBonusActions: Math.floor(type.bonusActions * pers.prodMult),
    effectiveAutoClose: Math.floor(type.autoClose * pers.prodMult),
    effectiveClientBonus: Math.floor(type.clientBonus * pers.prodMult),
    effectiveCampaignBonus: Math.floor(type.campaignBonus * pers.prodMult),
    effectiveLeadBonus: Math.floor(type.leadBonus * pers.prodMult),
  };
};

// ============ COMPONENTS ============

const ProgressBar = ({ value, max, color = '#3b82f6', h = 8, glow = false, segments = 0 }) => (
  <div style={{ height: h, background: '#e2e8f0', borderRadius: h/2, overflow: 'hidden', position: 'relative' }}>
    <div style={{ 
      height: '100%', 
      width: `${Math.min(100, (value/max)*100)}%`, 
      background: color, 
      borderRadius: h/2, 
      transition: 'width 0.5s ease',
      boxShadow: glow ? `0 0 8px ${color}` : 'none',
    }} />
    {segments > 0 && Array.from({ length: segments - 1 }).map((_, i) => (
      <div key={i} style={{
        position: 'absolute',
        left: `${((i + 1) / segments) * 100}%`,
        top: 0,
        bottom: 0,
        width: '2px',
        background: 'rgba(255,255,255,0.5)',
      }} />
    ))}
  </div>
);

const FloatingNumber = ({ value, x, y, color }) => (
  <div style={{
    position: 'fixed', left: x, top: y, color: color || '#22c55e',
    fontWeight: '800', fontSize: '18px', pointerEvents: 'none',
    animation: 'floatUp 1.2s ease-out forwards', zIndex: 9999,
    textShadow: '0 2px 4px rgba(0,0,0,0.2)',
  }}>{value}</div>
);

const TempBadge = ({ temp }) => {
  const data = LEAD_TEMPS.find(t => t.temp === temp) || LEAD_TEMPS[1];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '2px',
      padding: '2px 6px', borderRadius: '8px', fontSize: '10px', fontWeight: '700',
      background: `${data.color}20`, color: data.color,
    }}>
      {data.icon} {data.name}
    </span>
  );
};

// ============ NETWORKING MEMORY GAME ============

const NetworkingGame = ({ onComplete, employees }) => {
  const [phase, setPhase] = useState('intro'); // intro, memorize, quiz, result
  const [people, setPeople] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [score, setScore] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [timer, setTimer] = useState(10);
  const [options, setOptions] = useState([]);
  
  const peopleCount = 4;
  const questionCount = 4;
  const memorizeTime = 10;
  
  useEffect(() => {
    if (phase === 'intro') {
      const selectedNames = shuffle(NETWORKING_NAMES).slice(0, peopleCount);
      const newPeople = selectedNames.map((name, i) => ({
        id: i, name,
        role: pick(['CEO', 'Marketing Director', 'Owner', 'Founder', 'CMO', 'VP Sales']),
        company: `${pick(['Bright', 'Peak', 'Nova', 'Prime', 'Elite', 'Global'])} ${pick(['Tech', 'Solutions', 'Media', 'Group', 'Co'])}`,
        avatar: ['👨‍💼', '👩‍💼', '👨‍💻', '👩‍💻', '🧑‍💼', '👴', '👵'][i % 7],
      }));
      setPeople(newPeople);
    }
  }, [phase]);
  
  useEffect(() => {
    if (phase === 'memorize') {
      setTimer(memorizeTime);
      const interval = setInterval(() => {
        setTimer(t => {
          if (t <= 1) { clearInterval(interval); setPhase('quiz'); setupQuestion(0); return 0; }
          return t - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [phase]);
  
  const setupQuestion = (qNum) => {
    const correctPerson = people[qNum % people.length];
    const wrongNames = shuffle(NETWORKING_NAMES.filter(n => !people.some(p => p.name === n))).slice(0, 3);
    setOptions(shuffle([correctPerson.name, ...wrongNames]));
    setCurrentQuestion(qNum);
    setSelectedAnswer(null);
  };
  
  const handleAnswer = (answer) => {
    if (selectedAnswer !== null) return;
    const correctPerson = people[currentQuestion % people.length];
    const isCorrect = answer === correctPerson.name;
    setSelectedAnswer({ answer, isCorrect });
    if (isCorrect) setScore(s => s + 1);
    setTimeout(() => {
      if (currentQuestion < questionCount - 1) setupQuestion(currentQuestion + 1);
      else setPhase('result');
    }, 1200);
  };
  
  if (phase === 'intro') {
    return (
      <div style={modal.container}>
        <div style={{ fontSize: '48px', marginBottom: '12px', textAlign: 'center' }}>🤝</div>
        <h2 style={modal.title}>Networking Event</h2>
        <p style={modal.subtitle}>Memorize the names of people you meet!</p>
        <div style={{ background: '#f0f9ff', borderRadius: '10px', padding: '14px', marginBottom: '16px' }}>
          <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '13px', color: '#334155', lineHeight: '1.6' }}>
            <li>Meet <strong>{peopleCount} people</strong></li>
            <li>Memorize names in <strong>{memorizeTime} seconds</strong></li>
            <li>Answer {questionCount} questions</li>
            <li>More correct = more quality leads!</li>
          </ul>
        </div>
        <button onClick={() => setPhase('memorize')} style={modal.primaryBtn}>Start Networking 🎯</button>
      </div>
    );
  }
  
  if (phase === 'memorize') {
    return (
      <div style={modal.container}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={{ ...modal.title, margin: 0 }}>Memorize!</h2>
          <div style={{ fontSize: '20px', fontWeight: '800', color: timer <= 3 ? '#ef4444' : '#3b82f6' }}>{timer}s</div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
          {people.map((person, i) => (
            <div key={i} style={{ background: '#f8fafc', borderRadius: '12px', padding: '14px', textAlign: 'center', border: '2px solid #e2e8f0' }}>
              <div style={{ fontSize: '36px', marginBottom: '6px' }}>{person.avatar}</div>
              <div style={{ fontSize: '16px', fontWeight: '800', color: '#1e293b' }}>{person.name}</div>
              <div style={{ fontSize: '11px', color: '#64748b' }}>{person.role}</div>
              <div style={{ fontSize: '10px', color: '#94a3b8' }}>{person.company}</div>
            </div>
          ))}
        </div>
      </div>
    );
  }
  
  if (phase === 'quiz') {
    const correctPerson = people[currentQuestion % people.length];
    return (
      <div style={modal.container}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
          <span style={{ fontSize: '12px', color: '#64748b' }}>Question {currentQuestion + 1}/{questionCount}</span>
          <span style={{ fontSize: '14px', fontWeight: '700', color: '#22c55e' }}>{score}/{currentQuestion} correct</span>
        </div>
        <div style={{ background: '#fef3c7', borderRadius: '12px', padding: '16px', marginBottom: '16px', textAlign: 'center' }}>
          <div style={{ fontSize: '36px', marginBottom: '8px' }}>{correctPerson.avatar}</div>
          <p style={{ fontSize: '14px', color: '#92400e', margin: 0 }}>
            Who is the <strong>{correctPerson.role}</strong> at <strong>{correctPerson.company}</strong>?
          </p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
          {options.map((name, i) => {
            const isSelected = selectedAnswer?.answer === name;
            const isCorrect = name === correctPerson.name;
            const showResult = selectedAnswer !== null;
            let bg = '#f8fafc', border = '#e2e8f0', color = '#1e293b';
            if (showResult) {
              if (isCorrect) { bg = '#dcfce7'; border = '#86efac'; color = '#166534'; }
              else if (isSelected) { bg = '#fef2f2'; border = '#fca5a5'; color = '#b91c1c'; }
            }
            return (
              <button key={i} onClick={() => handleAnswer(name)} disabled={selectedAnswer !== null}
                style={{ padding: '14px', background: bg, border: `2px solid ${border}`, borderRadius: '10px', fontSize: '14px', fontWeight: '600', color }}>
                {name}{showResult && isCorrect && ' ✓'}{showResult && isSelected && !isCorrect && ' ✗'}
              </button>
            );
          })}
        </div>
      </div>
    );
  }
  
  if (phase === 'result') {
    const leadBonus = employees.reduce((sum, e) => sum + (e.effectiveLeadBonus || 0), 0);
    const baseLeads = score >= 4 ? 4 : score >= 3 ? 3 : score >= 2 ? 2 : score >= 1 ? 1 : 0;
    const totalLeads = baseLeads + (baseLeads > 0 ? leadBonus : 0);
    const quality = score >= 3 ? 'hot' : score >= 2 ? 'warm' : 'cool';
    return (
      <div style={modal.container}>
        <div style={{ fontSize: '56px', marginBottom: '12px', textAlign: 'center' }}>{score >= 3 ? '🎉' : score >= 2 ? '👍' : '😅'}</div>
        <h2 style={{ ...modal.title, color: score >= 3 ? '#22c55e' : score >= 2 ? '#f59e0b' : '#ef4444' }}>{score}/{questionCount} Correct!</h2>
        {totalLeads > 0 && (
          <div style={{ background: '#f0fdf4', borderRadius: '12px', padding: '16px', marginBottom: '16px', textAlign: 'center' }}>
            <div style={{ fontSize: '28px', fontWeight: '800', color: '#22c55e' }}>+{totalLeads} {quality} leads</div>
            {leadBonus > 0 && <div style={{ fontSize: '11px', color: '#16a34a' }}>+{leadBonus} from Lead Generator</div>}
          </div>
        )}
        <button onClick={() => onComplete(totalLeads, quality)} style={modal.primaryBtn}>Continue</button>
      </div>
    );
  }
  return null;
};

// ============ ADS PUZZLE GAME ============

const AdsPuzzleGame = ({ onComplete, employees }) => {
  const [phase, setPhase] = useState('intro');
  const [round, setRound] = useState(1);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [currentChallenge, setChallenge] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [cashLost, setCashLost] = useState(0);
  const [powerups, setPowerups] = useState({ hint: 2, skip: 1, double: 1 });
  const [doubleActive, setDoubleActive] = useState(false);
  const [hintUsed, setHintUsed] = useState(false);
  
  const totalRounds = 4;
  const baseCost = 100;
  
  const generateChallenge = () => {
    const type = pick(['platform', 'objective', 'audience']);
    if (type === 'platform') {
      const obj = pick(AD_OBJECTIVES);
      return { type: 'platform', question: `Best platform for "${obj.name}"?`, options: shuffle(AD_PLATFORMS).slice(0, 4), correctIds: obj.best, hint: `Think about ${obj.id} goals...` };
    } else if (type === 'objective') {
      const plat = pick(AD_PLATFORMS);
      const correct = AD_OBJECTIVES.filter(o => o.best.includes(plat.id)).map(o => o.id);
      return { type: 'objective', question: `What works best on ${plat.icon} ${plat.name}?`, options: shuffle(AD_OBJECTIVES).slice(0, 4), correctIds: correct, hint: `${plat.name} excels at ${plat.audience}...` };
    } else {
      const plat = pick(AD_PLATFORMS);
      const correct = AD_AUDIENCES.filter(a => a.best.includes(plat.id)).map(a => a.id);
      return { type: 'audience', question: `Best audience for ${plat.icon} ${plat.name}?`, options: shuffle(AD_AUDIENCES).slice(0, 4), correctIds: correct, hint: `${plat.name} is known for ${plat.audience}...` };
    }
  };
  
  const startGame = () => { setChallenge(generateChallenge()); setPhase('playing'); };
  
  const handleAnswer = (option) => {
    if (selectedAnswer) return;
    const isCorrect = currentChallenge.correctIds.includes(option.id);
    setSelectedAnswer({ option, isCorrect });
    if (isCorrect) { setScore(s => s + (doubleActive ? 2 : 1)); setStreak(s => s + 1); }
    else { setCashLost(l => l + baseCost * round); setStreak(0); }
    setDoubleActive(false);
    setTimeout(() => {
      setSelectedAnswer(null); setHintUsed(false);
      if (round < totalRounds) { setRound(r => r + 1); setChallenge(generateChallenge()); }
      else setPhase('result');
    }, 1500);
  };
  
  const usePowerup = (type) => {
    if (powerups[type] <= 0) return;
    setPowerups(p => ({ ...p, [type]: p[type] - 1 }));
    if (type === 'hint') setHintUsed(true);
    else if (type === 'skip') {
      if (round < totalRounds) { setRound(r => r + 1); setChallenge(generateChallenge()); }
      else setPhase('result');
    }
    else if (type === 'double') setDoubleActive(true);
  };
  
  if (phase === 'intro') {
    return (
      <div style={modal.container}>
        <div style={{ fontSize: '48px', marginBottom: '12px', textAlign: 'center' }}>📺</div>
        <h2 style={modal.title}>Ad Campaign Challenge</h2>
        <p style={modal.subtitle}>Test your digital advertising knowledge!</p>
        <div style={{ background: '#fef3c7', borderRadius: '10px', padding: '14px', marginBottom: '16px' }}>
          <div style={{ fontWeight: '700', marginBottom: '8px', color: '#92400e' }}>⚠️ Stakes:</div>
          <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '13px', color: '#78350f' }}>
            <li><strong>Correct:</strong> Earn quality leads</li>
            <li><strong>Wrong:</strong> Lose ${baseCost}-${baseCost * totalRounds} cash</li>
          </ul>
        </div>
        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
          {AD_POWERUPS.map(p => (
            <div key={p.id} style={{ flex: 1, background: '#f0f9ff', padding: '8px', borderRadius: '8px', textAlign: 'center', fontSize: '11px' }}>
              {p.icon} {p.name} (×{p.uses})
            </div>
          ))}
        </div>
        <button onClick={startGame} style={modal.primaryBtn}>Launch Campaign 🚀</button>
      </div>
    );
  }
  
  if (phase === 'playing' && currentChallenge) {
    return (
      <div style={modal.container}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
          <span style={{ fontSize: '12px', color: '#64748b' }}>Round {round}/{totalRounds}</span>
          <div style={{ display: 'flex', gap: '8px' }}>
            {streak >= 2 && <span style={{ fontSize: '11px', color: '#f59e0b' }}>🔥 {streak}</span>}
            <span style={{ fontSize: '14px', fontWeight: '700', color: '#22c55e' }}>{score} pts</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '6px', marginBottom: '12px' }}>
          {AD_POWERUPS.map(p => (
            <button key={p.id} onClick={() => usePowerup(p.id)} disabled={powerups[p.id] <= 0 || selectedAnswer || (p.id === 'hint' && hintUsed)}
              style={{ flex: 1, padding: '8px', background: powerups[p.id] > 0 ? '#fef3c7' : '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '11px', fontWeight: '600', color: powerups[p.id] > 0 ? '#92400e' : '#94a3b8' }}>
              {p.icon} {powerups[p.id]}
            </button>
          ))}
        </div>
        {doubleActive && <div style={{ background: '#f0fdf4', padding: '8px', borderRadius: '8px', textAlign: 'center', marginBottom: '12px', fontSize: '12px', color: '#166534' }}>2️⃣ Double active!</div>}
        <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '16px', marginBottom: '14px', textAlign: 'center', borderLeft: '4px solid #3b82f6' }}>
          <p style={{ fontSize: '15px', fontWeight: '600', color: '#1e293b', margin: 0 }}>{currentChallenge.question}</p>
          {hintUsed && <p style={{ fontSize: '12px', color: '#64748b', margin: '8px 0 0 0' }}>💡 {currentChallenge.hint}</p>}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
          {currentChallenge.options.map((option, i) => {
            const isSelected = selectedAnswer?.option.id === option.id;
            const isCorrect = currentChallenge.correctIds.includes(option.id);
            const showResult = selectedAnswer !== null;
            let bg = '#f8fafc', border = '#e2e8f0', color = '#1e293b';
            if (showResult) {
              if (isCorrect) { bg = '#dcfce7'; border = '#86efac'; color = '#166534'; }
              else if (isSelected) { bg = '#fef2f2'; border = '#fca5a5'; color = '#b91c1c'; }
            }
            return (
              <button key={option.id} onClick={() => handleAnswer(option)} disabled={selectedAnswer !== null}
                style={{ padding: '14px 10px', background: bg, border: `2px solid ${border}`, borderRadius: '10px', fontSize: '12px', fontWeight: '600', color }}>
                <span style={{ fontSize: '20px', display: 'block', marginBottom: '4px' }}>{option.icon}</span>
                {option.name}
              </button>
            );
          })}
        </div>
        {cashLost > 0 && <div style={{ marginTop: '12px', padding: '8px', background: '#fef2f2', borderRadius: '8px', textAlign: 'center', fontSize: '12px', color: '#b91c1c' }}>💸 Wasted: ${cashLost}</div>}
      </div>
    );
  }
  
  if (phase === 'result') {
    const leadBonus = employees.reduce((sum, e) => sum + (e.effectiveLeadBonus || 0), 0);
    const baseLeads = score >= 4 ? 5 : score >= 3 ? 4 : score >= 2 ? 3 : score >= 1 ? 2 : 0;
    const totalLeads = baseLeads + leadBonus;
    const quality = score >= 3 ? 'hot' : score >= 2 ? 'warm' : 'cool';
    return (
      <div style={modal.container}>
        <div style={{ fontSize: '56px', marginBottom: '12px', textAlign: 'center' }}>{score >= 3 ? '🎯' : score >= 2 ? '📊' : '💸'}</div>
        <h2 style={{ ...modal.title, color: score >= 3 ? '#22c55e' : score >= 2 ? '#f59e0b' : '#ef4444' }}>
          Campaign {score >= 3 ? 'Success!' : score >= 2 ? 'Okay' : 'Struggled'}
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
          <div style={{ background: totalLeads > 0 ? '#f0fdf4' : '#f8fafc', borderRadius: '10px', padding: '14px', textAlign: 'center' }}>
            <div style={{ fontSize: '11px', color: '#64748b' }}>Leads</div>
            <div style={{ fontSize: '24px', fontWeight: '800', color: totalLeads > 0 ? '#22c55e' : '#94a3b8' }}>+{totalLeads}</div>
          </div>
          <div style={{ background: cashLost > 0 ? '#fef2f2' : '#f0fdf4', borderRadius: '10px', padding: '14px', textAlign: 'center' }}>
            <div style={{ fontSize: '11px', color: '#64748b' }}>Lost</div>
            <div style={{ fontSize: '24px', fontWeight: '800', color: cashLost > 0 ? '#ef4444' : '#22c55e' }}>${cashLost}</div>
          </div>
        </div>
        <button onClick={() => onComplete(totalLeads, quality, cashLost)} style={modal.primaryBtn}>Continue</button>
      </div>
    );
  }
  return null;
};

// ============ SPECIALIZATION PICKER ============

const SpecializationPicker = ({ onSelect }) => {
  const [selected, setSelected] = useState([]);
  
  const toggle = (id) => {
    if (selected.includes(id)) {
      setSelected(selected.filter(s => s !== id));
    } else if (selected.length < 2) {
      setSelected([...selected, id]);
    }
  };
  
  return (
    <div style={modal.container}>
      <h2 style={modal.title}>🎯 Choose Your Specializations</h2>
      <p style={modal.subtitle}>Pick 2 areas of expertise. You'll excel in these industries but struggle in others.</p>
      
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '16px' }}>
        {SPECIALIZATIONS.map(spec => (
          <button
            key={spec.id}
            onClick={() => toggle(spec.id)}
            style={{
              ...modal.specCard,
              borderColor: selected.includes(spec.id) ? '#3b82f6' : '#e2e8f0',
              background: selected.includes(spec.id) ? '#eff6ff' : 'white',
            }}
          >
            <span style={{ fontSize: '24px' }}>{spec.icon}</span>
            <div style={{ fontWeight: '700', fontSize: '12px' }}>{spec.name}</div>
            <div style={{ fontSize: '9px', color: '#64748b' }}>{spec.desc}</div>
          </button>
        ))}
      </div>
      
      <button
        onClick={() => onSelect(selected)}
        disabled={selected.length !== 2}
        style={{ ...modal.primaryBtn, opacity: selected.length === 2 ? 1 : 0.5 }}
      >
        Start Agency →
      </button>
    </div>
  );
};

// ============ CAMPAIGN PLANNER ============

const CampaignPlanner = ({ client, onSubmit, onCancel, specializations, employees }) => {
  const [campaign, setCampaign] = useState(null);
  const [budget, setBudget] = useState(50);
  const [approach, setApproach] = useState('balanced');
  
  const approaches = [
    { id: 'safe', name: 'Conservative', icon: '🛡️', riskMod: 0.5, rewardMod: 0.7 },
    { id: 'balanced', name: 'Balanced', icon: '⚖️', riskMod: 1.0, rewardMod: 1.0 },
    { id: 'aggressive', name: 'Aggressive', icon: '🔥', riskMod: 1.5, rewardMod: 1.4 },
  ];
  
  const relevantCampaigns = CAMPAIGN_TYPES.filter(c => 
    c.industries.includes(client.industry) || Math.random() < 0.3
  );
  
  const getProjectedResult = () => {
    if (!campaign) return null;
    const app = approaches.find(a => a.id === approach);
    const budgetMod = budget / 50;
    const isSpecialized = specializations.some(s => 
      SPECIALIZATIONS.find(spec => spec.id === s)?.industries.includes(client.industry)
    );
    const specBonus = isSpecialized ? 1.3 : 0.8;
    const empBonus = employees.filter(e => e.skills.includes('planning') || e.skills.includes('optimization'))
      .reduce((s, e) => s + e.productivity * 0.1, 1);
    
    const baseResult = (campaign.minResult + campaign.maxResult) / 2;
    const projected = baseResult * budgetMod * app.rewardMod * specBonus * empBonus;
    const risk = campaign.risk * app.riskMod * (isSpecialized ? 0.7 : 1.2);
    
    return { projected, risk, cost: Math.floor(client.budget * campaign.cost * budgetMod) };
  };
  
  const result = getProjectedResult();
  
  return (
    <div style={modal.container}>
      <h2 style={modal.title}>📋 Plan Campaign for {client.firstName}</h2>
      <p style={modal.subtitle}>{client.businessName} • ${client.budget}/mo</p>
      
      <div style={{ marginBottom: '12px' }}>
        <div style={modal.label}>Campaign Type</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px' }}>
          {relevantCampaigns.map(c => (
            <button
              key={c.id}
              onClick={() => setCampaign(c)}
              style={{
                ...modal.option,
                borderColor: campaign?.id === c.id ? '#3b82f6' : '#e2e8f0',
                background: campaign?.id === c.id ? '#eff6ff' : 'white',
              }}
            >
              <span style={{ fontSize: '20px' }}>{c.icon}</span>
              <div style={{ fontSize: '10px', fontWeight: '600' }}>{c.name}</div>
            </button>
          ))}
        </div>
      </div>
      
      <div style={{ marginBottom: '12px' }}>
        <div style={modal.label}>Budget Allocation: {budget}%</div>
        <input
          type="range"
          min="20"
          max="100"
          value={budget}
          onChange={(e) => setBudget(Number(e.target.value))}
          style={{ width: '100%' }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#64748b' }}>
          <span>Conservative</span>
          <span>All-In</span>
        </div>
      </div>
      
      <div style={{ marginBottom: '12px' }}>
        <div style={modal.label}>Approach</div>
        <div style={{ display: 'flex', gap: '6px' }}>
          {approaches.map(a => (
            <button
              key={a.id}
              onClick={() => setApproach(a.id)}
              style={{
                ...modal.option,
                flex: 1,
                borderColor: approach === a.id ? '#3b82f6' : '#e2e8f0',
                background: approach === a.id ? '#eff6ff' : 'white',
              }}
            >
              <span>{a.icon}</span>
              <div style={{ fontSize: '10px' }}>{a.name}</div>
            </button>
          ))}
        </div>
      </div>
      
      {result && campaign && (
        <div style={{ background: '#f8fafc', borderRadius: '10px', padding: '12px', marginBottom: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
            <span style={{ fontSize: '12px', color: '#64748b' }}>Projected Performance</span>
            <span style={{ fontSize: '14px', fontWeight: '700', color: result.projected >= 1 ? '#22c55e' : '#f59e0b' }}>
              {(result.projected * 100).toFixed(0)}%
            </span>
          </div>
          <ProgressBar value={result.projected} max={1.5} color={result.projected >= 1 ? '#22c55e' : '#f59e0b'} h={8} />
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', fontSize: '11px' }}>
            <span style={{ color: '#ef4444' }}>Risk: {(result.risk * 100).toFixed(0)}%</span>
            <span style={{ color: '#64748b' }}>Cost: ${result.cost}/mo</span>
          </div>
        </div>
      )}
      
      <div style={{ display: 'flex', gap: '8px' }}>
        <button onClick={onCancel} style={modal.secondaryBtn}>Cancel</button>
        <button
          onClick={() => onSubmit({ campaign, budget, approach, ...result })}
          disabled={!campaign}
          style={{ ...modal.primaryBtn, flex: 2, opacity: campaign ? 1 : 0.5 }}
        >
          Launch Campaign
        </button>
      </div>
    </div>
  );
};

// ============ PITCH GAME ============

// Comprehensive objection/question bank with personality-matched responses

// Each scenario has ONE question with its own tailored responses
const PITCH_SCENARIOS = [
  // ============ OPENING QUESTIONS ============
  {
    category: 'opening',
    question: { text: "So tell me, what exactly does your agency do?", personalities: ['all'] },
    responses: [
      { text: "We're a full-service marketing agency. We do SEO, social media, PPC, content - basically everything you need.", score: { default: 8, best: [], worst: ['Data-Driven', 'Skeptic'] }, feedback: { good: "Solid overview.", bad: "Too generic - they wanted specifics." } },
      { text: "Great question. Before I answer, I'd love to understand your biggest marketing challenge right now. That way I can show you exactly how we'd help.", score: { default: 15, best: ['Visionary', 'Hands-Off', 'Networker'], worst: ['Impatient'] }, feedback: { good: "Smart - you're building rapport and gathering intel.", bad: "They don't have time for questions." } },
      { text: "We help businesses like yours turn marketing spend into measurable revenue. Last quarter, our clients averaged a 340% return on their marketing investment.", score: { default: 14, best: ['Data-Driven', 'Budget-Hawk'], worst: ['Networker'] }, feedback: { good: "The data resonates with them.", bad: "Felt too salesy - they prefer relationships." } },
      { text: "In short? We become your outsourced marketing department. Strategy, execution, reporting - we handle it all so you can focus on running your business.", score: { default: 13, best: ['Hands-Off', 'Impatient'], worst: ['Micromanager'] }, feedback: { good: "The simplicity appeals to them.", bad: "They want to be more involved than that." } },
    ]
  },
  {
    category: 'opening',
    question: { text: "I've got 15 minutes. What's your pitch?", personalities: ['Impatient', 'Budget-Hawk'] },
    responses: [
      { text: "Perfect. Here's the short version: we've helped 47 businesses in your industry increase revenue by an average of 35%. I'd like to do the same for you. Questions?", score: { default: 15, best: ['Impatient', 'Data-Driven'], worst: ['Visionary'] }, feedback: { good: "Direct and data-driven - exactly what they wanted.", bad: "They wanted more vision and context." } },
      { text: "I appreciate you being direct. Let me ask you one question first - what's your biggest marketing frustration right now?", score: { default: 11, best: ['Networker', 'Visionary'], worst: ['Impatient'] }, feedback: { good: "Good pivot to understanding their needs.", bad: "They said 15 minutes - don't waste it with questions." } },
      { text: "Three things: we specialize in your industry, we guarantee transparent reporting, and we've never lost a client to poor results. Want the details?", score: { default: 14, best: ['Impatient', 'Skeptic', 'Budget-Hawk'], worst: [] }, feedback: { good: "Concise and compelling.", bad: "" } },
      { text: "I could give you a pitch, but honestly? I'd rather hear about your business first. The best marketing is tailored, not templated.", score: { default: 9, best: ['Visionary'], worst: ['Impatient', 'Budget-Hawk'] }, feedback: { good: "They appreciate the thoughtfulness.", bad: "You're wasting their limited time." } },
    ]
  },
  {
    category: 'opening',
    question: { text: "I'm curious what makes you different from the dozen other agencies that contact me.", personalities: ['Skeptic', 'Data-Driven'] },
    responses: [
      { text: "Fair question - you probably get pitched constantly. The honest difference? We only take clients we're confident we can help, and we prove it with a 94% retention rate.", score: { default: 15, best: ['Skeptic', 'Data-Driven'], worst: [] }, feedback: { good: "Honest and backed by data.", bad: "" } },
      { text: "Most agencies sell services. We sell outcomes. If we don't think we can move the needle for you, we'll tell you upfront and save us both time.", score: { default: 14, best: ['Skeptic', 'Budget-Hawk'], worst: ['Networker'] }, feedback: { good: "The honesty builds trust.", bad: "They wanted more warmth in the response." } },
      { text: "We're not different in what we do - we're different in how we do it. Senior strategists on every account, weekly check-ins, and complete transparency on where every dollar goes.", score: { default: 13, best: ['Micromanager', 'Data-Driven'], worst: ['Hands-Off'] }, feedback: { good: "Specific differentiation that matters to them.", bad: "Too much detail for their style." } },
      { text: "Honestly? You'll have to judge that for yourself. But I can tell you that most of our clients came from agencies that overpromised and underdelivered.", score: { default: 11, best: ['Visionary'], worst: ['Data-Driven'] }, feedback: { good: "The confidence is appealing.", bad: "They wanted proof, not confidence." } },
    ]
  },
  {
    category: 'opening',
    question: { text: "A friend recommended you. What should I know about working with your team?", personalities: ['Networker', 'Hands-Off'] },
    responses: [
      { text: "I'm glad they had a good experience! The main thing to know is that we treat every client like a partner, not an account number. You'll have direct access to our senior team.", score: { default: 16, best: ['Networker', 'Hands-Off'], worst: [] }, feedback: { good: "Perfect - relationship-focused answer for a relationship-focused person.", bad: "" } },
      { text: "That's great to hear! We pride ourselves on communication. You'll have a dedicated account manager, weekly updates, and monthly strategy reviews.", score: { default: 13, best: ['Micromanager', 'Data-Driven'], worst: ['Hands-Off'] }, feedback: { good: "Clear expectations set.", bad: "That sounds like a lot of meetings for them." } },
      { text: "Our average client stays with us for over 2 years - that tells you more than I could. We focus on results and relationships equally.", score: { default: 14, best: ['Networker', 'Skeptic'], worst: [] }, feedback: { good: "Social proof that resonates.", bad: "" } },
      { text: "We're data-obsessed. Every decision we make is backed by analytics and tied to your bottom line. No fluff, no vanity metrics.", score: { default: 10, best: ['Data-Driven'], worst: ['Networker'] }, feedback: { good: "They appreciate the rigor.", bad: "They came from a referral - they wanted warmth, not metrics." } },
    ]
  },
  {
    category: 'opening',
    question: { text: "Before we start, what's your experience with businesses like mine?", personalities: ['Micromanager', 'Data-Driven'] },
    responses: [
      { text: "We've worked with 12 businesses in your industry over the past 3 years. I can walk you through specific results - one client went from $30K to $95K monthly revenue in 8 months.", score: { default: 16, best: ['Data-Driven', 'Micromanager', 'Skeptic'], worst: [] }, feedback: { good: "Specific, relevant, impressive.", bad: "" } },
      { text: "Every business is unique, so I'd want to learn more about yours before making claims. But we have deep experience in your vertical and understand the challenges.", score: { default: 12, best: ['Visionary', 'Hands-Off'], worst: ['Data-Driven'] }, feedback: { good: "Thoughtful approach.", bad: "They wanted concrete experience, not philosophy." } },
      { text: "Honestly, we're selective about who we work with. If we didn't have relevant experience, we wouldn't be on this call. Let me share some case studies.", score: { default: 14, best: ['Skeptic', 'Data-Driven'], worst: ['Networker'] }, feedback: { good: "Confident and willing to prove it.", bad: "Came across as slightly arrogant." } },
      { text: "Great question. We've found that the principles of good marketing translate across industries. Our process adapts to your specific market dynamics.", score: { default: 9, best: [], worst: ['Micromanager', 'Data-Driven'] }, feedback: { good: "Reasonable answer.", bad: "They specifically asked about THEIR industry experience." } },
    ]
  },
  {
    category: 'opening',
    question: { text: "Walk me through your process. How do you actually work with clients?", personalities: ['Micromanager', 'Data-Driven'] },
    responses: [
      { text: "Phase 1 is discovery - we audit your current marketing and research your market. Phase 2 is strategy - we build a custom plan. Phase 3 is execution and optimization. You get weekly reports throughout.", score: { default: 16, best: ['Micromanager', 'Data-Driven'], worst: ['Impatient'] }, feedback: { good: "Exactly the structured answer they wanted.", bad: "Too much process detail for them." } },
      { text: "We start by listening. What are your goals? What's worked before? What hasn't? Then we build a strategy around your specific situation, not a template.", score: { default: 13, best: ['Visionary', 'Hands-Off'], worst: ['Micromanager'] }, feedback: { good: "They appreciate the custom approach.", bad: "They wanted more concrete process details." } },
      { text: "Simple: we figure out what's working, double down on it, and fix what isn't. Most agencies overcomplicate this. Marketing is about testing, measuring, and optimizing.", score: { default: 11, best: ['Impatient', 'Budget-Hawk'], worst: ['Micromanager'] }, feedback: { good: "The simplicity resonates.", bad: "Way too vague for their taste." } },
      { text: "Here's what makes us different - we don't just run campaigns, we integrate with your sales team to close the loop. We track from first click to closed deal.", score: { default: 14, best: ['Data-Driven', 'Budget-Hawk'], worst: [] }, feedback: { good: "They love the ROI focus.", bad: "" } },
    ]
  },
  
  // ============ PRICING QUESTIONS ============
  {
    category: 'pricing',
    question: { text: "What are we talking about cost-wise? I need to know if this is even in my budget.", personalities: ['Budget-Hawk', 'Impatient'] },
    responses: [
      { text: "Our retainers typically range from $2,500 to $8,000 monthly depending on scope. But let me ask - what would a 3x return on that investment mean for your business?", score: { default: 14, best: ['Data-Driven', 'Budget-Hawk'], worst: ['Impatient'] }, feedback: { good: "Reframed cost as investment.", bad: "They wanted a number, not a question." } },
      { text: "Before I quote a number, I'd need to understand your goals. But I can tell you we're not the cheapest option - we're the results option.", score: { default: 10, best: ['Visionary'], worst: ['Budget-Hawk', 'Impatient'] }, feedback: { good: "Positioned as premium.", bad: "They need to know if it's even feasible." } },
      { text: "Most clients in your industry invest between $3,000-$5,000 monthly with us. That typically generates $15,000-$25,000 in new revenue. Want me to break down the math?", score: { default: 16, best: ['Budget-Hawk', 'Data-Driven'], worst: [] }, feedback: { good: "Specific, relevant, ROI-focused.", bad: "" } },
      { text: "We can work with various budgets. What range were you thinking? I'd rather tailor a proposal to your reality than throw out numbers that don't fit.", score: { default: 12, best: ['Hands-Off', 'Networker'], worst: ['Data-Driven'] }, feedback: { good: "Flexible and accommodating.", bad: "They wanted you to lead with a number." } },
    ]
  },
  {
    category: 'pricing',
    question: { text: "Your rates seem higher than other agencies I've talked to. Why?", personalities: ['Budget-Hawk', 'Skeptic', 'Data-Driven'] },
    responses: [
      { text: "You're right - we're not the cheapest. The difference is we use senior strategists, not junior staff learning on your dime. That expertise costs more but delivers more.", score: { default: 15, best: ['Data-Driven', 'Skeptic'], worst: ['Budget-Hawk'] }, feedback: { good: "Justified the premium clearly.", bad: "They're still focused on the price gap." } },
      { text: "Fair point. Here's my question back: what did those cheaper agencies promise in terms of results? Our clients pay more because they get measurably more.", score: { default: 14, best: ['Skeptic', 'Data-Driven'], worst: ['Impatient'] }, feedback: { good: "Smart reframe around value.", bad: "They wanted a direct answer." } },
      { text: "Because we guarantee our work. If we don't hit agreed-upon targets in 90 days, we work for free until we do. That confidence costs more upfront but protects you.", score: { default: 16, best: ['Budget-Hawk', 'Skeptic'], worst: [] }, feedback: { good: "The guarantee addresses their concern perfectly.", bad: "" } },
      { text: "Honestly, if budget is the primary concern, we might not be the right fit. We compete on results, not price. But I understand that's not right for everyone.", score: { default: 9, best: ['Visionary'], worst: ['Budget-Hawk'] }, feedback: { good: "They respect the positioning.", bad: "You basically just disqualified yourself." } },
    ]
  },
  {
    category: 'pricing',
    question: { text: "Can you work within a tighter budget if we start small?", personalities: ['Budget-Hawk', 'Hands-Off'] },
    responses: [
      { text: "Absolutely. We have a starter package focused on one high-impact channel. It's a great way to prove the ROI before scaling up.", score: { default: 16, best: ['Budget-Hawk', 'Skeptic', 'Hands-Off'], worst: [] }, feedback: { good: "Perfect - flexibility that reduces their risk.", bad: "" } },
      { text: "We can, but I want to be honest - underfunding marketing often leads to underwhelming results. Let me show you the minimum viable investment for real impact.", score: { default: 13, best: ['Data-Driven', 'Skeptic'], worst: ['Budget-Hawk'] }, feedback: { good: "Honest about expectations.", bad: "Felt like you're upselling them." } },
      { text: "I'd rather start smaller and prove ourselves than overcommit you. Let's identify your single biggest opportunity and nail that first.", score: { default: 15, best: ['Hands-Off', 'Networker', 'Skeptic'], worst: [] }, feedback: { good: "They trust your conservative approach.", bad: "" } },
      { text: "Our minimum engagement is $2,000/month. Below that, we can't deliver the results that would make this worthwhile for either of us.", score: { default: 10, best: ['Data-Driven'], worst: ['Budget-Hawk', 'Hands-Off'] }, feedback: { good: "Clear boundaries.", bad: "Felt rigid when they asked for flexibility." } },
    ]
  },
  {
    category: 'pricing',
    question: { text: "That's more than I expected. Can you break down where that money actually goes?", personalities: ['Data-Driven', 'Micromanager'] },
    responses: [
      { text: "Of course. Roughly 40% goes to ad spend, 35% to strategy and management, 15% to creative production, and 10% to reporting and analytics. Every dollar is tracked.", score: { default: 16, best: ['Data-Driven', 'Micromanager'], worst: [] }, feedback: { good: "Exactly the transparency they needed.", bad: "" } },
      { text: "Happy to. But first, let me explain why we allocate it this way - it's optimized for ROI, not just activity. Would that context help?", score: { default: 12, best: ['Visionary'], worst: ['Data-Driven', 'Micromanager'] }, feedback: { good: "Strategic framing.", bad: "They asked for a breakdown, give them one." } },
      { text: "The majority goes directly to campaigns that drive results. Unlike some agencies, we don't pad our fees with unnecessary overhead. You're paying for outcomes.", score: { default: 13, best: ['Budget-Hawk', 'Skeptic'], worst: ['Micromanager'] }, feedback: { good: "Addressed the value question.", bad: "Still didn't give them the specifics they wanted." } },
      { text: "I'll send you a detailed proposal with line-item breakdown. But the headline: you're paying for senior talent and proven systems, not junior staff learning on your account.", score: { default: 14, best: ['Data-Driven', 'Skeptic'], worst: ['Impatient'] }, feedback: { good: "Promised specifics and justified premium.", bad: "They wanted the answer now, not later." } },
    ]
  },
  {
    category: 'pricing',
    question: { text: "What happens if I need to cancel? Am I locked into a long contract?", personalities: ['Skeptic', 'Budget-Hawk'] },
    responses: [
      { text: "We do month-to-month after an initial 90-day commitment. That's enough time to show results, but you're never trapped. If we're not delivering, you should leave.", score: { default: 16, best: ['Skeptic', 'Budget-Hawk'], worst: [] }, feedback: { good: "Fair terms that reduce their risk.", bad: "" } },
      { text: "Most agencies lock you in because they need time to hide poor performance. We do 90 days, then month-to-month. Our retention comes from results, not contracts.", score: { default: 15, best: ['Skeptic', 'Data-Driven'], worst: [] }, feedback: { good: "Bold positioning that builds confidence.", bad: "" } },
      { text: "We require a 6-month commitment to do this right. Marketing takes time, and jumping ship early usually means wasted investment on both sides.", score: { default: 9, best: [], worst: ['Skeptic', 'Budget-Hawk'] }, feedback: { good: "Honest about the time needed.", bad: "That's exactly what they were afraid of." } },
      { text: "Let's discuss terms once we've agreed this is a fit. I'm flexible for the right partnership.", score: { default: 11, best: ['Networker'], worst: ['Skeptic', 'Data-Driven'] }, feedback: { good: "Open to negotiation.", bad: "They wanted a straight answer." } },
    ]
  },
  
  // ============ TRUST QUESTIONS ============
  {
    category: 'trust',
    question: { text: "I've been burned by marketing agencies before. How do I know you're different?", personalities: ['Skeptic', 'Micromanager'] },
    responses: [
      { text: "I hear that constantly, and it's frustrating for good agencies too. Here's what we do differently: monthly reviews, transparent reporting, and a 90-day out clause. If we're not delivering, you're not trapped.", score: { default: 16, best: ['Skeptic', 'Micromanager'], worst: [] }, feedback: { good: "Acknowledged their fear and offered real safeguards.", bad: "" } },
      { text: "I can't speak for other agencies. What I can tell you is our average client stays 2+ years and our referral rate is over 40%. Happy clients don't leave, and they tell their friends.", score: { default: 14, best: ['Networker', 'Data-Driven'], worst: [] }, feedback: { good: "Social proof that matters.", bad: "" } },
      { text: "You don't know until you work with us. But here's my offer: let's do a paid 30-day pilot project. Real work, real results, minimal risk for you.", score: { default: 15, best: ['Skeptic', 'Budget-Hawk'], worst: [] }, feedback: { good: "Put your money where your mouth is.", bad: "" } },
      { text: "Trust is earned. I won't ask you to believe me - I'll ask you to verify me. Talk to three of our clients directly. I'll set up the calls.", score: { default: 14, best: ['Skeptic', 'Data-Driven'], worst: ['Impatient'] }, feedback: { good: "Confidence in your references.", bad: "That's a lot of homework for them." } },
    ]
  },
  {
    category: 'trust',
    question: { text: "Can you show me actual results from businesses similar to mine?", personalities: ['Data-Driven', 'Skeptic'] },
    responses: [
      { text: "Absolutely. I have three case studies from your exact industry. One client grew from $50K to $180K monthly revenue in 8 months. Another reduced their cost-per-lead by 60%. Want the details?", score: { default: 16, best: ['Data-Driven', 'Skeptic'], worst: [] }, feedback: { good: "Concrete proof - exactly what they needed.", bad: "" } },
      { text: "I can, but let me understand your specific goals first. 'Results' means different things to different businesses. What metrics matter most to you?", score: { default: 11, best: ['Visionary'], worst: ['Data-Driven', 'Skeptic'] }, feedback: { good: "Thoughtful approach.", bad: "They asked for proof, not more questions." } },
      { text: "We have case studies, but honestly, your business is unique. What worked for them might not work for you. I'd rather discuss your specific situation.", score: { default: 9, best: [], worst: ['Data-Driven', 'Skeptic'] }, feedback: { good: "Fair point.", bad: "Sounds like you're avoiding the question." } },
      { text: "Yes - and I'll do you one better. I'll introduce you to two current clients in your space who can speak candidly about working with us.", score: { default: 15, best: ['Skeptic', 'Networker'], worst: ['Impatient'] }, feedback: { good: "Living references are even better.", bad: "They wanted quick proof, not homework." } },
    ]
  },
  {
    category: 'trust',
    question: { text: "What happens if this doesn't work? Do I just lose my money?", personalities: ['Budget-Hawk', 'Skeptic'] },
    responses: [
      { text: "If we're not hitting agreed-upon metrics by month three, we either pivot strategy at no extra cost or you walk away. We don't succeed unless you succeed.", score: { default: 16, best: ['Budget-Hawk', 'Skeptic'], worst: [] }, feedback: { good: "Real accountability that addresses their fear.", bad: "" } },
      { text: "Marketing isn't magic - there are variables we can't control. But we guarantee our effort, our transparency, and our commitment to finding what works.", score: { default: 10, best: ['Visionary'], worst: ['Budget-Hawk', 'Skeptic'] }, feedback: { good: "Honest about limitations.", bad: "They wanted protection, not caveats." } },
      { text: "Here's our philosophy: we only take clients we're confident we can help. If we didn't think we could move the needle for you, we wouldn't be having this conversation.", score: { default: 12, best: ['Visionary', 'Networker'], worst: ['Skeptic'] }, feedback: { good: "Confident positioning.", bad: "Still didn't answer what happens if it fails." } },
      { text: "We do performance-based bonuses - part of our fee is tied to hitting your targets. So we literally get paid less if we don't deliver. That's alignment.", score: { default: 15, best: ['Budget-Hawk', 'Data-Driven'], worst: [] }, feedback: { good: "Skin in the game builds trust.", bad: "" } },
    ]
  },
  {
    category: 'trust',
    question: { text: "Why should I trust you with my brand?", personalities: ['Visionary', 'Skeptic'] },
    responses: [
      { text: "Because we treat your brand like it's ours. Every piece of content, every ad, every campaign goes through our brand integrity check. We've been trusted by companies who live and die by reputation.", score: { default: 15, best: ['Visionary', 'Skeptic'], worst: [] }, feedback: { good: "Showed you understand brand importance.", bad: "" } },
      { text: "You probably shouldn't yet. Trust is earned. Let's start with a small project where the stakes are lower, and I'll show you how we operate.", score: { default: 14, best: ['Skeptic', 'Budget-Hawk'], worst: ['Visionary'] }, feedback: { good: "Refreshingly honest approach.", bad: "They wanted confidence in protecting their brand." } },
      { text: "Our clients include companies who trust us with their brand daily. But more importantly - you approve everything before it goes live. You're always in control.", score: { default: 16, best: ['Micromanager', 'Visionary', 'Skeptic'], worst: [] }, feedback: { good: "Social proof plus control - perfect combo.", bad: "" } },
      { text: "I won't pretend brand work is risk-free. But here's our track record: zero brand incidents in 4 years, and a client roster that keeps coming back.", score: { default: 13, best: ['Data-Driven'], worst: ['Visionary'] }, feedback: { good: "Data-backed reassurance.", bad: "They wanted more passion about brand protection." } },
    ]
  },
  
  // ============ TIMING QUESTIONS ============
  {
    category: 'timing',
    question: { text: "This sounds interesting, but I need to think about it. Can you follow up next quarter?", personalities: ['Hands-Off', 'Skeptic'] },
    responses: [
      { text: "Of course - I'll put a reminder in my calendar. But can I ask what's holding you back? Sometimes talking through concerns now saves time later.", score: { default: 14, best: ['Skeptic', 'Hands-Off'], worst: ['Impatient'] }, feedback: { good: "Respectful while uncovering objections.", bad: "They wanted to be left alone." } },
      { text: "Absolutely. Before I go - what would need to be different next quarter for this to make sense? I'd rather address that now if possible.", score: { default: 13, best: ['Skeptic', 'Data-Driven'], worst: ['Hands-Off'] }, feedback: { good: "Trying to identify real objection.", bad: "Too pushy for their style." } },
      { text: "Happy to. One thing to consider: your competitors aren't waiting. Every month without optimized marketing is market share you're leaving on the table.", score: { default: 11, best: ['Impatient', 'Visionary'], worst: ['Hands-Off', 'Skeptic'] }, feedback: { good: "Created urgency.", bad: "Felt like pressure tactics." } },
      { text: "Sure. I'll send you some resources in the meantime - case studies and a mini-audit of your current marketing. No pressure, just useful context for when you're ready.", score: { default: 15, best: ['Hands-Off', 'Skeptic'], worst: [] }, feedback: { good: "Added value without pressure.", bad: "" } },
    ]
  },
  {
    category: 'timing',
    question: { text: "We're in the middle of a busy season. Is now really the right time to start something new?", personalities: ['Micromanager', 'Budget-Hawk'] },
    responses: [
      { text: "Actually, this might be perfect timing. We handle everything - onboarding takes 2 hours of your time, then we run autonomously. You stay focused on the busy season.", score: { default: 16, best: ['Hands-Off', 'Impatient'], worst: [] }, feedback: { good: "Removed their biggest concern.", bad: "" } },
      { text: "Fair point. Here's another angle: starting now means campaigns are optimized before your slow season. You'll capture demand when competitors are scrambling.", score: { default: 14, best: ['Visionary', 'Data-Driven'], worst: ['Hands-Off'] }, feedback: { good: "Reframed timing as an advantage.", bad: "Still feels like more work during busy time." } },
      { text: "I get it - the last thing you need is another thing on your plate. What if we start with just strategy? We build the plan now, execute when you have bandwidth.", score: { default: 15, best: ['Micromanager', 'Skeptic'], worst: [] }, feedback: { good: "Flexible approach that works with their reality.", bad: "" } },
      { text: "There's never a 'perfect' time - businesses are always busy. The question is: will you be less busy in three months? Usually the answer is no.", score: { default: 10, best: ['Impatient'], worst: ['Hands-Off', 'Budget-Hawk'] }, feedback: { good: "True, but...", bad: "Dismissive of their legitimate concern." } },
    ]
  },
  {
    category: 'timing',
    question: { text: "I want to do this, but I have so much on my plate right now. How much of my time will this require?", personalities: ['Hands-Off', 'Impatient'] },
    responses: [
      { text: "Minimal. About 2 hours for initial onboarding, then 30-minute weekly check-ins. We handle everything else. Most clients say we actually save them time.", score: { default: 16, best: ['Hands-Off', 'Impatient'], worst: ['Micromanager'] }, feedback: { good: "Exactly what they needed to hear.", bad: "" } },
      { text: "I won't sugarcoat it - good marketing requires collaboration. But we've built systems to minimize your time while maximizing your input quality. Think 2-3 hours monthly.", score: { default: 13, best: ['Skeptic', 'Data-Driven'], worst: ['Hands-Off'] }, feedback: { good: "Honest about requirements.", bad: "That's still more than they wanted." } },
      { text: "Here's how I'd approach it: you give us one focused hour upfront to download everything about your business. Then we run with it and only interrupt you when it matters.", score: { default: 15, best: ['Hands-Off', 'Impatient'], worst: [] }, feedback: { good: "Minimized ongoing burden.", bad: "" } },
      { text: "The first month is heavier - probably 4-5 hours total for kickoff. After that, it drops to almost nothing unless you want more involvement.", score: { default: 12, best: ['Data-Driven'], worst: ['Impatient', 'Hands-Off'] }, feedback: { good: "Set accurate expectations.", bad: "4-5 hours sounds like a lot to them." } },
    ]
  },
  {
    category: 'timing',
    question: { text: "Let me talk to my partner/team first and get back to you.", personalities: ['all'] },
    responses: [
      { text: "Of course. Would it help if I prepared a one-page summary they can review? I can include the ROI projections and key points from our conversation.", score: { default: 15, best: ['Data-Driven', 'Micromanager'], worst: [] }, feedback: { good: "Helpful without being pushy.", bad: "" } },
      { text: "Makes sense. One question: is there anything specific you think they'll push back on? I'd rather address that now than play phone tag later.", score: { default: 14, best: ['Skeptic', 'Data-Driven'], worst: ['Hands-Off'] }, feedback: { good: "Proactive objection handling.", bad: "Felt like you don't trust them to sell it." } },
      { text: "Absolutely. When do you think you'll have that conversation? I'll follow up then so you're not chasing me down.", score: { default: 13, best: ['Hands-Off', 'Networker'], worst: ['Impatient'] }, feedback: { good: "Professional follow-up setup.", bad: "Could seem like you're tracking them." } },
      { text: "Would it be helpful if I joined a call with your team? I can answer questions directly and save you from being the middleman.", score: { default: 12, best: ['Impatient', 'Micromanager'], worst: ['Hands-Off'] }, feedback: { good: "Direct approach.", bad: "They might want to control the internal conversation." } },
    ]
  },
  {
    category: 'timing',
    question: { text: "Our current marketing is working okay. Why change anything?", personalities: ['Hands-Off', 'Skeptic'] },
    responses: [
      { text: "'Okay' is exactly the problem. 'Okay' means you're leaving money on the table. What if you could do 30% better with the same budget?", score: { default: 14, best: ['Visionary', 'Data-Driven'], worst: ['Hands-Off'] }, feedback: { good: "Challenged the status quo effectively.", bad: "They're comfortable with 'okay.'" } },
      { text: "If it's working, why change? But here's my question: do you know exactly what's driving those results? And could you do more of it?", score: { default: 13, best: ['Data-Driven', 'Skeptic'], worst: ['Hands-Off'] }, feedback: { good: "Made them think.", bad: "Sounds like you're questioning their competence." } },
      { text: "Maybe you shouldn't change anything. Let me do a free audit - if your marketing is already optimized, I'll tell you. No pitch, just data.", score: { default: 16, best: ['Skeptic', 'Hands-Off', 'Data-Driven'], worst: [] }, feedback: { good: "Zero-risk offer that builds trust.", bad: "" } },
      { text: "Working 'okay' is fine if 'okay' is your goal. Most of our clients come to us when they realize they've been settling for less than they could achieve.", score: { default: 10, best: ['Visionary'], worst: ['Hands-Off', 'Skeptic'] }, feedback: { good: "Inspirational framing.", bad: "Came across as condescending." } },
    ]
  },
  
  // ============ COMPETITION QUESTIONS ============
  {
    category: 'competition',
    question: { text: "I'm also talking to two other agencies. What makes you the better choice?", personalities: ['Data-Driven', 'Budget-Hawk'] },
    responses: [
      { text: "Good - you should compare. Three things to evaluate: who will actually work on your account, how they report results, and what clients say about them. Ask those questions and decide.", score: { default: 14, best: ['Data-Driven', 'Skeptic'], worst: ['Impatient'] }, feedback: { good: "Confident enough to invite comparison.", bad: "They wanted you to sell yourself, not give homework." } },
      { text: "I don't know those agencies, so I can't compare directly. What I can tell you: we have a 94% retention rate, senior strategists on every account, and we guarantee transparency on every dollar spent.", score: { default: 15, best: ['Data-Driven', 'Skeptic'], worst: [] }, feedback: { good: "Focused on your strengths without attacking competitors.", bad: "" } },
      { text: "The real difference won't be in the pitch - it'll be in the partnership. We treat clients like partners, not accounts. But that's something you'd have to experience.", score: { default: 12, best: ['Networker', 'Visionary'], worst: ['Data-Driven'] }, feedback: { good: "Relationship-focused answer.", bad: "They wanted concrete differentiation." } },
      { text: "What do they do better than us? Seriously - tell me their pitch, and I'll tell you honestly where we're stronger and where they might be.", score: { default: 14, best: ['Skeptic', 'Data-Driven'], worst: ['Hands-Off'] }, feedback: { good: "Bold and transparent approach.", bad: "Felt like you're grilling them." } },
    ]
  },
  {
    category: 'competition',
    question: { text: "My nephew does social media marketing. Why should I pay you instead?", personalities: ['Budget-Hawk', 'Networker'] },
    responses: [
      { text: "Your nephew might be great! But there's a difference between posting content and building a revenue engine. Does he track attribution, optimize for conversions, and build marketing funnels?", score: { default: 14, best: ['Data-Driven', 'Skeptic'], worst: ['Networker'] }, feedback: { good: "Showed the professional difference without insulting.", bad: "They might feel you dismissed their family." } },
      { text: "Maybe you shouldn't. If he's getting results and you're happy, stick with him. But if you're asking the question, something isn't working. What's missing?", score: { default: 15, best: ['Skeptic', 'Networker'], worst: [] }, feedback: { good: "Uncovered the real concern.", bad: "" } },
      { text: "A few things: accountability, expertise, and bandwidth. Can your nephew dedicate 40 hours a week to your marketing? Does he have case studies proving ROI?", score: { default: 13, best: ['Data-Driven', 'Micromanager'], worst: ['Networker'] }, feedback: { good: "Laid out the professional advantages.", bad: "Might feel like an attack on their nephew." } },
      { text: "Let me ask this: would you hire your nephew to handle your accounting or legal work? Marketing is a profession too, and expertise matters.", score: { default: 11, best: ['Data-Driven'], worst: ['Networker', 'Budget-Hawk'] }, feedback: { good: "Positioned marketing as professional.", bad: "Could come across as arrogant." } },
    ]
  },
  {
    category: 'competition',
    question: { text: "The agency I'm comparing you to has more case studies. Should that worry me?", personalities: ['Data-Driven', 'Skeptic'] },
    responses: [
      { text: "Not necessarily. More case studies might mean they've been around longer, but it doesn't mean they're better. Ask them: what's your retention rate? How many of those clients are still with you?", score: { default: 15, best: ['Skeptic', 'Data-Driven'], worst: [] }, feedback: { good: "Reframed the metric that matters.", bad: "" } },
      { text: "Quality over quantity. We're selective about who we work with and who we feature. Ask both agencies for references you can actually call - that tells you more than polished case studies.", score: { default: 14, best: ['Skeptic', 'Networker'], worst: [] }, feedback: { good: "Elevated the conversation.", bad: "" } },
      { text: "Maybe. But case studies are marketing materials - they show successes, not failures. What's their client retention rate? That's the real story.", score: { default: 14, best: ['Skeptic', 'Data-Driven'], worst: ['Networker'] }, feedback: { good: "Smart reframe.", bad: "Could seem like you're deflecting." } },
      { text: "I'd rather have fewer case studies with deeper results than a library of surface-level wins. Let me show you the detail in ours - you'll see the difference.", score: { default: 13, best: ['Data-Driven', 'Visionary'], worst: ['Skeptic'] }, feedback: { good: "Confident pivot.", bad: "Didn't fully address their concern." } },
    ]
  },
  {
    category: 'competition',
    question: { text: "Another agency offered to guarantee specific results. Why can't you?", personalities: ['Budget-Hawk', 'Skeptic'] },
    responses: [
      { text: "I could, but I'd be lying. Marketing has variables neither of us control - algorithm changes, competitor moves, market shifts. Agencies that guarantee specific numbers are either dishonest or naive.", score: { default: 14, best: ['Skeptic', 'Data-Driven'], worst: ['Budget-Hawk'] }, feedback: { good: "Honest about industry reality.", bad: "They still want some protection." } },
      { text: "What specifically are they guaranteeing? If it's leads or traffic, that's easy to game with low-quality results. If it's revenue, ask them to put it in writing with a refund clause.", score: { default: 16, best: ['Skeptic', 'Data-Driven', 'Budget-Hawk'], worst: [] }, feedback: { good: "Called the bluff and added value.", bad: "" } },
      { text: "We do guarantee something: our process, our transparency, and our effort. If we're not hitting targets by month three, we revise strategy at no cost or let you walk. That's real accountability.", score: { default: 15, best: ['Skeptic', 'Budget-Hawk'], worst: [] }, feedback: { good: "Offered meaningful accountability.", bad: "" } },
      { text: "Because guaranteeing marketing results is like a doctor guaranteeing you'll never get sick. We're professionals who maximize your odds, not fortune tellers.", score: { default: 11, best: ['Visionary'], worst: ['Budget-Hawk'] }, feedback: { good: "Good analogy.", bad: "They wanted something concrete, not philosophy." } },
    ]
  },
  {
    category: 'competition',
    question: { text: "A freelancer quoted me half your price. What am I missing?", personalities: ['Budget-Hawk', 'Data-Driven'] },
    responses: [
      { text: "Probably bandwidth and backup. A freelancer is one person - what happens when they're sick, overwhelmed, or disappear? We're a team with systems, accountability, and continuity.", score: { default: 15, best: ['Skeptic', 'Micromanager'], worst: [] }, feedback: { good: "Highlighted real risks of freelancers.", bad: "" } },
      { text: "Nothing, if the freelancer is great. But ask: what's their track record? Do they have case studies? What happens if the relationship doesn't work? The risk is higher with a solo operator.", score: { default: 14, best: ['Skeptic', 'Data-Driven'], worst: [] }, feedback: { good: "Fair comparison.", bad: "" } },
      { text: "You might not be missing anything - freelancers can be excellent. But you're also getting one person's perspective versus a team's collective expertise. For some businesses, that trade-off is worth the savings.", score: { default: 13, best: ['Hands-Off', 'Budget-Hawk'], worst: ['Skeptic'] }, feedback: { good: "Balanced and honest.", bad: "Maybe too honest - you might lose them." } },
      { text: "Strategy depth. A freelancer can execute tactics, but do they have the strategic foundation to know which tactics matter? That's where the price difference shows up in results.", score: { default: 14, best: ['Data-Driven', 'Visionary'], worst: ['Budget-Hawk'] }, feedback: { good: "Positioned strategy as the difference.", bad: "Might seem like you're dismissing freelancer capabilities." } },
    ]
  },
  
  // ============ CLOSING QUESTIONS ============
  {
    category: 'closing',
    question: { text: "Okay, I'm interested. What are the next steps?", personalities: ['Hands-Off', 'Networker', 'Visionary'] },
    responses: [
      { text: "Great! Step one: I'll send you a proposal with scope and pricing. Step two: we do a 45-minute kickoff call. Step three: we launch within 14 days. You'll see initial data within 30 days.", score: { default: 16, best: ['Impatient', 'Data-Driven', 'Micromanager'], worst: [] }, feedback: { good: "Clear, actionable roadmap.", bad: "" } },
      { text: "Let's start with the paperwork - I'll send over an agreement today. Once signed, we'll schedule your strategy session. We can have campaigns live within two weeks.", score: { default: 15, best: ['Impatient', 'Hands-Off'], worst: [] }, feedback: { good: "Quick path to action.", bad: "" } },
      { text: "Next step is really your call. I can send a formal proposal, or if you're ready, we can kick off onboarding this week. What works for you?", score: { default: 14, best: ['Hands-Off', 'Networker'], worst: ['Micromanager'] }, feedback: { good: "Put them in control.", bad: "They wanted you to lead." } },
      { text: "I'll prepare a custom proposal based on our conversation - should take 24 hours. Then we can walk through it together and make any adjustments. Sound good?", score: { default: 13, best: ['Data-Driven', 'Micromanager'], worst: ['Impatient'] }, feedback: { good: "Professional and thorough.", bad: "Why wait? They're ready now." } },
    ]
  },
  {
    category: 'closing',
    question: { text: "If I sign today, when can we start seeing results?", personalities: ['Impatient', 'Data-Driven'] },
    responses: [
      { text: "Initial metrics within 30 days - traffic, engagement, lead flow. Meaningful ROI typically shows between 60-90 days. Some quick wins might come faster, but sustainable results take time to build.", score: { default: 15, best: ['Data-Driven', 'Skeptic'], worst: ['Impatient'] }, feedback: { good: "Honest timeline with milestones.", bad: "They wanted to hear 'fast.'" } },
      { text: "We can have campaigns live within 14 days. You'll see leading indicators immediately - traffic, clicks, engagement. Actual revenue impact usually shows by month two.", score: { default: 16, best: ['Impatient', 'Data-Driven'], worst: [] }, feedback: { good: "Quick action with realistic expectations.", bad: "" } },
      { text: "Depends on what 'results' means to you. Awareness can be immediate. Leads take 2-4 weeks. Closed deals depend on your sales cycle. What's your priority?", score: { default: 13, best: ['Data-Driven'], worst: ['Impatient'] }, feedback: { good: "Tailored answer.", bad: "They wanted a simple answer." } },
      { text: "Here's my honest take: anyone promising results in 30 days is either lying or gaming vanity metrics. Real, sustainable growth takes 90 days. But you'll see progress along the way.", score: { default: 12, best: ['Skeptic'], worst: ['Impatient'] }, feedback: { good: "Set proper expectations.", bad: "Not what they wanted to hear." } },
    ]
  },
  {
    category: 'closing',
    question: { text: "I need to be honest - I'm still on the fence. What's your best argument for why we should do this?", personalities: ['Skeptic', 'Budget-Hawk'] },
    responses: [
      { text: "Here's the math: if we deliver even half what we typically do for clients like you, that's a 3x return on your investment. The question isn't whether you can afford to try - it's whether you can afford not to.", score: { default: 14, best: ['Data-Driven', 'Budget-Hawk'], worst: ['Skeptic'] }, feedback: { good: "ROI-focused argument.", bad: "They've heard ROI pitches before." } },
      { text: "I won't pressure you - this needs to feel right. But consider: what's the cost of waiting another six months? Your competitors aren't standing still.", score: { default: 12, best: ['Visionary', 'Impatient'], worst: ['Hands-Off'] }, feedback: { good: "Created urgency.", bad: "Felt like pressure." } },
      { text: "My best argument? We're having this conversation because something isn't working. You can try to fix it yourself, or you can hire people who've solved this problem dozens of times.", score: { default: 15, best: ['Skeptic', 'Data-Driven'], worst: [] }, feedback: { good: "Addressed the real reason they're talking.", bad: "" } },
      { text: "Tell me what's keeping you on the fence. Seriously - I'd rather address your real concern than give you a generic pitch.", score: { default: 15, best: ['Skeptic', 'Networker'], worst: ['Impatient'] }, feedback: { good: "Got to the root objection.", bad: "They wanted a closing argument, not more questions." } },
    ]
  },
  {
    category: 'closing',
    question: { text: "What happens after I sign? Walk me through the first 30 days.", personalities: ['Micromanager', 'Data-Driven'] },
    responses: [
      { text: "Week 1: kickoff call and information gathering. Week 2: audit and strategy development. Week 3: campaign setup and creative. Week 4: launch and initial optimization. You'll get a detailed timeline on day one.", score: { default: 16, best: ['Micromanager', 'Data-Driven'], worst: [] }, feedback: { good: "Exactly the detail they wanted.", bad: "" } },
      { text: "First, we schedule a 90-minute strategy session - we learn your business deeply. Then we audit your current marketing, build a custom plan, and launch. You'll have campaigns live by day 21.", score: { default: 15, best: ['Data-Driven', 'Impatient'], worst: [] }, feedback: { good: "Clear process with timeline.", bad: "" } },
      { text: "Short version: we hit the ground running. You'll have a dedicated account manager within 24 hours, a strategy session by day 3, and live campaigns by the end of the month.", score: { default: 14, best: ['Impatient', 'Hands-Off'], worst: ['Micromanager'] }, feedback: { good: "Fast action promised.", bad: "They wanted more detail." } },
      { text: "We follow a proven 30-day onboarding system. I'll email you the full timeline, but the key milestones are: kickoff (day 1), strategy presentation (day 10), campaign launch (day 21), and first report (day 30).", score: { default: 15, best: ['Micromanager', 'Data-Driven'], worst: [] }, feedback: { good: "Structured and comprehensive.", bad: "" } },
    ]
  },
  {
    category: 'closing',
    question: { text: "My gut says yes. But convince me one more time - why your agency?", personalities: ['Visionary', 'Networker'] },
    responses: [
      { text: "Because we genuinely care about your success, not just collecting a retainer. I'm giving you my direct number. If something's not working, you call me. That's how we operate.", score: { default: 16, best: ['Networker', 'Visionary'], worst: ['Data-Driven'] }, feedback: { good: "Personal commitment that lands.", bad: "They wanted data, not feelings." } },
      { text: "Because a year from now, you'll either be glad you started or wish you had. The best time to build a marketing engine was yesterday. The second best time is today.", score: { default: 14, best: ['Visionary'], worst: ['Skeptic', 'Data-Driven'] }, feedback: { good: "Inspirational close.", bad: "They wanted logic, not inspiration." } },
      { text: "Three reasons: we know your industry, our retention rate proves we deliver, and you won't find another team as invested in your success. Trust your gut - it's right.", score: { default: 15, best: ['Networker', 'Visionary'], worst: [] }, feedback: { good: "Validated their instinct with reasons.", bad: "" } },
      { text: "Because we're not the biggest agency or the cheapest. We're the one that will treat your business like it matters - because to us, it does.", score: { default: 14, best: ['Networker', 'Visionary'], worst: ['Data-Driven'] }, feedback: { good: "Emotional appeal that resonates.", bad: "They prefer facts over feelings." } },
    ]
  },
  {
    category: 'closing',
    question: { text: "Last question - what could go wrong here?", personalities: ['Skeptic', 'Data-Driven'] },
    responses: [
      { text: "Honest answer? Two things: your market might shift unexpectedly, or we might need to try multiple approaches before finding what clicks. But we're built for adaptation - we'll course-correct fast.", score: { default: 16, best: ['Skeptic', 'Data-Driven'], worst: [] }, feedback: { good: "Honest about risks with mitigation plan.", bad: "" } },
      { text: "The biggest risk is misaligned expectations. That's why we start with clear goals and regular check-ins. If something's off track, we catch it early.", score: { default: 15, best: ['Skeptic', 'Micromanager'], worst: [] }, feedback: { good: "Addressed the process risk.", bad: "" } },
      { text: "Nothing, if we do our jobs right. But marketing isn't magic - there are variables we can't control. What we guarantee is effort, transparency, and adaptation.", score: { default: 11, best: ['Visionary'], worst: ['Skeptic', 'Data-Driven'] }, feedback: { good: "Confident answer.", bad: "They wanted you to acknowledge real risks." } },
      { text: "Worst case? We try multiple approaches and find that your market is harder to crack than expected. But even then, you'll learn what doesn't work - valuable data for any future efforts.", score: { default: 14, best: ['Skeptic', 'Data-Driven'], worst: ['Impatient'] }, feedback: { good: "Reframed failure as learning.", bad: "Still sounds like failure could happen." } },
    ]
  },
  
  // ============ SCOPE/DETAILS QUESTIONS ============
  {
    category: 'scope',
    question: { text: "What specifically would you do for us in the first month?", personalities: ['Micromanager', 'Data-Driven'] },
    responses: [
      { text: "Month one is foundation: full audit of your current marketing, competitor analysis, target audience deep-dive, and strategy development. By day 30, you'll have a complete roadmap and live campaigns.", score: { default: 16, best: ['Micromanager', 'Data-Driven'], worst: [] }, feedback: { good: "Clear, specific, actionable.", bad: "" } },
      { text: "We start with research and strategy. No random tactics - everything we do needs to connect to your goals. Then we build, launch, and start collecting data. Expect a full report by day 30.", score: { default: 14, best: ['Data-Driven', 'Skeptic'], worst: ['Impatient'] }, feedback: { good: "Strategic approach.", bad: "Felt slow on action." } },
      { text: "Discovery, strategy, and launch. We move fast - you'll see campaigns live by week three. The first month is about establishing baselines so we can prove improvement.", score: { default: 15, best: ['Impatient', 'Data-Driven'], worst: [] }, feedback: { good: "Fast with measurement.", bad: "" } },
      { text: "Every client is different, but typically: deep dive into your business, competitive analysis, strategy development, initial campaign setup, and first optimizations. We can customize to your priorities.", score: { default: 13, best: ['Hands-Off'], worst: ['Micromanager'] }, feedback: { good: "Flexible approach.", bad: "They wanted specifics, not flexibility." } },
    ]
  },
  {
    category: 'scope',
    question: { text: "Will I have a dedicated person or get passed around to different people?", personalities: ['Micromanager', 'Networker'] },
    responses: [
      { text: "One dedicated account manager - same person every call, every email. They'll know your business inside and out. Behind them is a team, but you'll never feel passed around.", score: { default: 16, best: ['Micromanager', 'Networker'], worst: [] }, feedback: { good: "Exactly what they needed to hear.", bad: "" } },
      { text: "You'll have a primary contact who owns your relationship. But you'll also benefit from our specialists when needed - designers, analysts, media buyers. Best of both worlds.", score: { default: 14, best: ['Data-Driven'], worst: ['Networker'] }, feedback: { good: "Showed breadth of support.", bad: "They wanted one person they can trust." } },
      { text: "Dedicated account manager, full stop. I hate the 'passed around' experience too - that's why we structured it this way. Your person is responsible for your results.", score: { default: 15, best: ['Micromanager', 'Networker'], worst: [] }, feedback: { good: "Empathy plus solution.", bad: "" } },
      { text: "You'll primarily work with me directly for the first 90 days. Once we're rolling, I'll introduce you to your long-term account manager - but I'm always a call away.", score: { default: 13, best: ['Networker'], worst: ['Micromanager'] }, feedback: { good: "Personal touch.", bad: "Wait, so you're not their long-term contact?" } },
    ]
  },
  {
    category: 'scope',
    question: { text: "How do you measure success? What metrics will you track?", personalities: ['Data-Driven', 'Skeptic'] },
    responses: [
      { text: "We track the metrics that matter: cost per lead, conversion rates, customer acquisition cost, and ultimately - revenue generated. You'll get a dashboard showing exactly what your investment is producing.", score: { default: 16, best: ['Data-Driven', 'Budget-Hawk'], worst: [] }, feedback: { good: "Business metrics, not vanity metrics.", bad: "" } },
      { text: "That depends on your goals. Some clients prioritize leads, others want brand awareness, others focus on sales. We'll define success metrics together during onboarding.", score: { default: 13, best: ['Visionary', 'Hands-Off'], worst: ['Data-Driven'] }, feedback: { good: "Customized approach.", bad: "They wanted to see your framework." } },
      { text: "Two levels: leading indicators (traffic, engagement, lead volume) and lagging indicators (qualified leads, sales, ROI). We report on both so you can see the full picture.", score: { default: 15, best: ['Data-Driven', 'Skeptic'], worst: [] }, feedback: { good: "Sophisticated measurement framework.", bad: "" } },
      { text: "The only metric that really matters is ROI - are we making you more money than we cost? Everything else is just a diagnostic to help us improve that number.", score: { default: 14, best: ['Budget-Hawk', 'Impatient'], worst: ['Data-Driven'] }, feedback: { good: "Bottom-line focus.", bad: "They wanted to see all the metrics you track." } },
    ]
  },
  
  // ============ INDUSTRY QUESTIONS ============
  {
    category: 'industry',
    question: { text: "Do you really understand my industry? It's different from typical businesses.", personalities: ['Skeptic', 'Micromanager'] },
    responses: [
      { text: "I'll be honest - every industry has nuances. That's why our first step is deep research and interviewing you. We become students of your business before we touch any campaigns.", score: { default: 14, best: ['Skeptic', 'Hands-Off'], worst: ['Impatient'] }, feedback: { good: "Humble and thorough approach.", bad: "They wanted proof of expertise." } },
      { text: "We've worked with 8 businesses in your space over the past two years. I can walk you through what worked, what didn't, and what's unique about marketing in your industry.", score: { default: 16, best: ['Data-Driven', 'Skeptic', 'Micromanager'], worst: [] }, feedback: { good: "Specific experience that builds confidence.", bad: "" } },
      { text: "The fundamentals of marketing apply everywhere - but execution varies dramatically. Let me show you our approach and you can judge if it fits your market.", score: { default: 11, best: ['Visionary'], worst: ['Skeptic', 'Micromanager'] }, feedback: { good: "Confident positioning.", bad: "They wanted industry-specific proof." } },
      { text: "Tell me what makes your industry different. I want to hear your perspective, then I'll share what we've seen work with similar businesses.", score: { default: 13, best: ['Networker', 'Visionary'], worst: ['Data-Driven'] }, feedback: { good: "Collaborative approach.", bad: "They expected you to come prepared." } },
    ]
  },
  {
    category: 'industry',
    question: { text: "My customers are very specific. How would you target them?", personalities: ['Data-Driven', 'Skeptic'] },
    responses: [
      { text: "That's where our research phase is critical. We'll build detailed customer profiles, identify where they spend time online, and create messaging that speaks to their specific pain points.", score: { default: 15, best: ['Data-Driven', 'Micromanager'], worst: [] }, feedback: { good: "Methodical approach.", bad: "" } },
      { text: "Good - specific customers are actually easier to target than broad ones. Tell me more about who they are, and I'll share some initial targeting ideas we'd explore.", score: { default: 14, best: ['Networker', 'Visionary'], worst: ['Data-Driven'] }, feedback: { good: "Engaging and collaborative.", bad: "They wanted to see your expertise, not ask them." } },
      { text: "Three ways: precision targeting on platforms where they exist, content that speaks directly to their needs, and retargeting to stay top of mind. The more specific, the more efficient our spend.", score: { default: 16, best: ['Data-Driven', 'Budget-Hawk'], worst: [] }, feedback: { good: "Concrete strategies.", bad: "" } },
      { text: "We use a combination of data and intuition. Platforms give us targeting tools, but understanding your customer's psychology is what makes campaigns resonate.", score: { default: 12, best: ['Visionary'], worst: ['Data-Driven'] }, feedback: { good: "Balanced approach.", bad: "Too vague for their taste." } },
    ]
  },
  {
    category: 'industry',
    question: { text: "My business is local. Does that change your approach?", personalities: ['Data-Driven', 'Budget-Hawk'] },
    responses: [
      { text: "Completely. Local marketing is its own discipline. We'd focus on local SEO, Google Business optimization, geo-targeted ads, and community-building strategies. Different playbook than national brands.", score: { default: 16, best: ['Data-Driven', 'Micromanager'], worst: [] }, feedback: { good: "Specific local expertise.", bad: "" } },
      { text: "Yes - and it's actually more efficient. Your ad spend goes further when you're not competing nationally. We'd focus on dominating your service area before expanding.", score: { default: 15, best: ['Budget-Hawk', 'Data-Driven'], worst: [] }, feedback: { good: "Efficiency angle they care about.", bad: "" } },
      { text: "Local is our sweet spot. We've helped dozens of local businesses become the obvious choice in their markets. Less competition, more loyalty when you get it right.", score: { default: 14, best: ['Networker', 'Skeptic'], worst: [] }, feedback: { good: "Showed relevant experience.", bad: "" } },
      { text: "Somewhat, but great marketing principles are universal. We just apply them with a local lens - different channels, different messaging, different success metrics.", score: { default: 10, best: [], worst: ['Data-Driven', 'Skeptic'] }, feedback: { good: "Reasonable answer.", bad: "They wanted local-specific expertise." } },
    ]
  },
  {
    category: 'industry',
    question: { text: "Seasonality is huge for us. How would you account for that?", personalities: ['Data-Driven', 'Micromanager'] },
    responses: [
      { text: "Smart marketing rides seasonality, not fights it. We'd front-load acquisition campaigns before your peak season, then shift to retention during slow periods. Planning around your calendar is essential.", score: { default: 16, best: ['Data-Driven', 'Micromanager'], worst: [] }, feedback: { good: "Shows strategic thinking.", bad: "" } },
      { text: "Great question. We'd want to see your historical data, identify the patterns, and build a 12-month plan that maximizes peak seasons while maintaining presence year-round.", score: { default: 15, best: ['Data-Driven', 'Micromanager'], worst: ['Impatient'] }, feedback: { good: "Data-driven approach.", bad: "Feels like a slow process." } },
      { text: "We budget seasonally - heavier investment when buyers are active, lighter during off-seasons. Some clients also use slow periods for brand-building while costs are lower.", score: { default: 14, best: ['Budget-Hawk', 'Data-Driven'], worst: [] }, feedback: { good: "Practical budget approach.", bad: "" } },
      { text: "Seasonality creates opportunities. While competitors go dark in slow seasons, you can build awareness cheaply and be top of mind when demand returns.", score: { default: 13, best: ['Visionary', 'Skeptic'], worst: ['Data-Driven'] }, feedback: { good: "Different perspective.", bad: "They wanted tactical planning." } },
    ]
  },
];

const PitchGame = ({ lead, onComplete, specializations, employees, reputation }) => {
  const [stage, setStage] = useState('prep');
  const [conviction, setConviction] = useState(40); // Start lower, need 90 to close
  const [round, setRound] = useState(1);
  const [feedback, setFeedback] = useState(null);
  const [currentScenario, setCurrentScenario] = useState(null);
  const [usedQuestions, setUsedQuestions] = useState([]);
  
  const tempData = LEAD_TEMPS.find(t => t.temp === lead.temp);
  const isSpecialized = lead.isSpecialized;
  const totalRounds = 5;
  const winThreshold = 90;
  
  // Get a question appropriate for the current round and personality
  const getNextScenario = useCallback(() => {
    // Determine category based on round
    let preferredCategories;
    if (round === 1) preferredCategories = ['opening'];
    else if (round === 5) preferredCategories = ['closing'];
    else preferredCategories = ['pricing', 'trust', 'timing', 'competition', 'scope', 'industry'];
    
    // Get all scenarios in preferred categories that haven't been used
    // Now each scenario has a .question object with .text and .personalities
    const availableScenarios = PITCH_SCENARIOS.filter(s => 
      preferredCategories.includes(s.category) && 
      !usedQuestions.includes(s.question.text) &&
      (s.question.personalities.includes('all') || s.question.personalities.includes(lead.personality))
    );
    
    // Fallback to any unused scenario in preferred categories
    const fallbackScenarios = PITCH_SCENARIOS.filter(s =>
      preferredCategories.includes(s.category) &&
      !usedQuestions.includes(s.question.text)
    );
    
    const scenariosToUse = availableScenarios.length > 0 ? availableScenarios : fallbackScenarios;
    
    let scenario;
    if (scenariosToUse.length === 0) {
      // Ultimate fallback - pick any from preferred categories
      const anyScenario = PITCH_SCENARIOS.filter(s => preferredCategories.includes(s.category));
      scenario = pick(anyScenario);
    } else {
      scenario = pick(scenariosToUse);
    }
    
    // Shuffle responses so order is different each time
    const shuffledResponses = [...scenario.responses].sort(() => Math.random() - 0.5);
    
    setUsedQuestions(prev => [...prev, scenario.question.text]);
    return { ...scenario, responses: shuffledResponses };
  }, [round, usedQuestions, lead.personality]);
  
  // Initialize first scenario when pitch starts
  useEffect(() => {
    if (stage === 'pitch' && !currentScenario) {
      setCurrentScenario(getNextScenario());
    }
  }, [stage, currentScenario, getNextScenario]);
  
  const handleResponse = (response) => {
    // Calculate score based on personality match
    let baseScore = response.score.default;
    let isBadMatch = false;
    
    if (response.score.best.includes(lead.personality)) {
      baseScore = Math.floor(baseScore * 1.4); // 40% bonus for best match
    } else if (response.score.worst.includes(lead.personality)) {
      // CRITICAL FIX: Bad matches make score NEGATIVE
      baseScore = Math.floor(baseScore * -0.5); // Score becomes negative!
      isBadMatch = true;
    }
    
    // Apply modifiers (only to positive scores)
    const tempBonus = tempData.conversionMod;
    const specBonus = isSpecialized ? 1.2 : 0.9;
    const repBonus = 1 + (reputation / 300);
    const empBonus = employees.filter(e => e.skills.includes('pitch')).reduce((s, e) => s + e.productivity * 0.1, 1);
    
    // If base score is negative (bad match), don't apply positive bonuses
    const finalScore = isBadMatch 
      ? baseScore  // Keep it negative
      : Math.floor(baseScore * tempBonus * specBonus * repBonus * empBonus);
    
    // Conviction CAN DECREASE now
    const newConviction = Math.max(0, Math.min(100, conviction + finalScore));
    
    // Determine feedback
    const isGoodResponse = finalScore >= 12;
    const isPerfectResponse = response.score.best.includes(lead.personality);
    const isWorstResponse = response.score.worst.includes(lead.personality);
    
    setConviction(newConviction);
    setFeedback({
      score: finalScore,
      isGood: isGoodResponse,
      isPerfect: isPerfectResponse,
      isWorst: isWorstResponse,
      decreased: finalScore < 0,
      message: isWorstResponse 
        ? `❌ ${response.feedback.bad} (${finalScore} conviction!)` 
        : (isGoodResponse ? response.feedback.good : response.feedback.bad || "Decent response, but could be better."),
    });
    
    setTimeout(() => {
      setFeedback(null);
      if (round < totalRounds) {
        setRound(r => r + 1);
        setCurrentScenario(getNextScenario());
      } else {
        setStage('result');
      }
    }, 2000);
  };
  
  if (stage === 'prep') {
    return (
      <div style={modal.container}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
          <span style={{ fontSize: '40px' }}>{lead.industryData.icon}</span>
          <div>
            <h2 style={{ ...modal.title, marginBottom: '2px', textAlign: 'left' }}>{lead.businessName}</h2>
            <p style={{ fontSize: '12px', color: '#64748b', margin: 0 }}>{lead.name} • {lead.personalityData.icon} {lead.personality}</p>
          </div>
        </div>
        
        <div style={{ background: '#f8fafc', borderRadius: '10px', padding: '12px', marginBottom: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div>
              <div style={{ fontSize: '20px', fontWeight: '800', color: '#22c55e' }}>${lead.budget}/mo</div>
              <TempBadge temp={lead.temp} />
            </div>
            <div style={{ textAlign: 'right' }}>
              {isSpecialized && <div style={{ fontSize: '11px', color: '#8b5cf6', fontWeight: '600' }}>✨ Your Specialty</div>}
              <div style={{ fontSize: '10px', color: '#64748b' }}>Needs: {lead.needs.join(', ')}</div>
            </div>
          </div>
        </div>
        
        <div style={{ background: '#fef3c7', borderRadius: '8px', padding: '10px', marginBottom: '12px', fontSize: '11px' }}>
          <strong>💡 Intel on {lead.personality}s:</strong> {lead.personalityData.tip}
        </div>
        
        <div style={{ background: '#f0f9ff', borderRadius: '8px', padding: '10px', marginBottom: '8px', fontSize: '11px' }}>
          <strong>🎯 Goal:</strong> Reach {winThreshold}% conviction in {totalRounds} questions. Match your responses to their personality!
        </div>
        
        <div style={{ background: '#fef2f2', borderRadius: '8px', padding: '10px', marginBottom: '12px', fontSize: '11px', color: '#b91c1c' }}>
          <strong>⚠️ Warning:</strong> Wrong answers will REDUCE conviction! Choose carefully.
        </div>
        
        <button onClick={() => setStage('pitch')} style={modal.primaryBtn}>
          Start Pitch →
        </button>
      </div>
    );
  }
  
  if (stage === 'result') {
    const won = conviction >= winThreshold;
    const wasPerfect = won && conviction >= 100;
    return (
      <div style={modal.container}>
        <div style={{ fontSize: '56px', marginBottom: '8px', animation: 'popIn 0.4s ease' }}>
          {wasPerfect ? '🏆' : won ? '🎉' : '😔'}
        </div>
        <h2 style={{ ...modal.title, color: won ? '#22c55e' : '#ef4444' }}>
          {wasPerfect ? 'PERFECT PITCH!' : won ? 'DEAL CLOSED!' : 'Not Convinced'}
        </h2>
        <p style={modal.subtitle}>
          {won 
            ? `${lead.firstName} is in! You closed a $${lead.budget}/mo contract!` 
            : `${lead.firstName} ended at ${conviction}% - needed ${winThreshold}%. The lead goes cold.`}
        </p>
        {wasPerfect && (
          <div style={{ background: '#fef3c7', borderRadius: '10px', padding: '12px', marginBottom: '12px', textAlign: 'center' }}>
            <div style={{ fontSize: '14px', fontWeight: '700', color: '#92400e' }}>🏆 Achievement: Perfect Pitch!</div>
          </div>
        )}
        {won && (
          <div style={{ background: '#f0fdf4', borderRadius: '10px', padding: '12px', marginBottom: '12px' }}>
            <div style={{ fontSize: '22px', fontWeight: '800', color: '#22c55e' }}>+${lead.budget}/mo recurring</div>
            {isSpecialized && <div style={{ fontSize: '11px', color: '#8b5cf6' }}>✨ Specialty bonus was active!</div>}
          </div>
        )}
        {!won && (
          <div style={{ background: '#fef2f2', borderRadius: '10px', padding: '12px', marginBottom: '12px', fontSize: '12px', color: '#b91c1c' }}>
            <strong>Tip:</strong> {lead.personality}s respond best to {
              lead.personality === 'Data-Driven' ? 'specific numbers, case studies, and ROI metrics' :
              lead.personality === 'Budget-Hawk' ? 'value propositions, ROI math, and flexible options' :
              lead.personality === 'Skeptic' ? 'proof, guarantees, transparency, and honest assessments' :
              lead.personality === 'Impatient' ? 'quick answers, clear timelines, and urgency' :
              lead.personality === 'Micromanager' ? 'detailed plans, dedicated contacts, and specific processes' :
              lead.personality === 'Hands-Off' ? 'low time requirements, done-for-you services, and simplicity' :
              lead.personality === 'Visionary' ? 'big picture thinking, growth potential, and inspiration' :
              'relationship building, personal connections, and trust'
            }.
          </div>
        )}
        <button onClick={() => onComplete(won, wasPerfect)} style={modal.primaryBtn}>Continue</button>
      </div>
    );
  }
  
  // Main pitch interface
  return (
    <div style={{ ...modal.container, maxWidth: '440px' }}>
      {/* Progress header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
        <span style={{ fontSize: '11px', color: '#64748b', fontWeight: '600' }}>Question {round} of {totalRounds}</span>
        <span style={{ 
          fontSize: '13px', fontWeight: '700', 
          color: conviction >= winThreshold ? '#22c55e' : conviction >= 70 ? '#f59e0b' : conviction >= 50 ? '#3b82f6' : '#ef4444' 
        }}>
          {conviction}% convinced
        </span>
      </div>
      
      {/* Progress bar with threshold marker */}
      <div style={{ position: 'relative', marginBottom: '12px' }}>
        <ProgressBar 
          value={conviction} 
          max={100} 
          color={conviction >= winThreshold ? '#22c55e' : conviction >= 70 ? '#f59e0b' : '#3b82f6'} 
          h={10} 
        />
        {/* Threshold marker */}
        <div style={{
          position: 'absolute',
          left: `${winThreshold}%`,
          top: '-2px',
          bottom: '-2px',
          width: '3px',
          background: '#22c55e',
          borderRadius: '2px',
        }} />
        <div style={{
          position: 'absolute',
          left: `${winThreshold}%`,
          top: '-16px',
          transform: 'translateX(-50%)',
          fontSize: '9px',
          color: '#22c55e',
          fontWeight: '700',
        }}>
          {winThreshold}%
        </div>
      </div>
      
      {/* Client info bar */}
      <div style={{ 
        display: 'flex', alignItems: 'center', gap: '8px', 
        padding: '8px 10px', background: '#f8fafc', borderRadius: '8px', marginBottom: '10px',
        fontSize: '11px'
      }}>
        <span style={{ fontSize: '18px' }}>{lead.industryData.icon}</span>
        <span style={{ fontWeight: '600' }}>{lead.firstName}</span>
        <span style={{ color: '#64748b' }}>•</span>
        <span style={{ color: '#64748b' }}>{lead.personalityData.icon} {lead.personality}</span>
        <span style={{ marginLeft: 'auto', color: '#22c55e', fontWeight: '600' }}>${lead.budget}/mo</span>
      </div>
      
      {/* Question - now uses currentScenario.question.text */}
      {currentScenario && (
        <div style={{ 
          background: 'linear-gradient(135deg, #f8fafc, #f1f5f9)', 
          borderRadius: '12px', 
          padding: '14px', 
          marginBottom: '12px',
          borderLeft: '4px solid #3b82f6'
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
            <span style={{ fontSize: '24px' }}>{lead.personalityData.icon}</span>
            <div>
              <div style={{ fontWeight: '700', fontSize: '12px', color: '#1e293b', marginBottom: '4px' }}>{lead.firstName} asks:</div>
              <p style={{ fontSize: '13px', color: '#334155', margin: 0, lineHeight: '1.5', fontStyle: 'italic' }}>
                "{currentScenario.question.text}"
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Feedback or Response options */}
      {feedback ? (
        <div style={{
          padding: '14px', borderRadius: '12px',
          background: feedback.isGood ? '#f0fdf4' : '#fef2f2',
          border: `2px solid ${feedback.isGood ? '#86efac' : '#fca5a5'}`,
          animation: 'popIn 0.3s ease',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
            <span style={{ fontSize: '24px' }}>
              {feedback.isPerfect ? '🎯' : feedback.isGood ? '✅' : feedback.isWorst ? '💥' : '😐'}
            </span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: '700', fontSize: '13px', color: feedback.isGood ? '#166534' : '#b91c1c' }}>
                {feedback.isPerfect ? 'Perfect Response!' : feedback.isGood ? 'Good Response' : feedback.isWorst ? 'Bad Match!' : 'Weak Response'}
              </div>
            </div>
            <span style={{ 
              fontWeight: '800', fontSize: '18px', 
              color: feedback.isGood ? '#22c55e' : '#ef4444' 
            }}>
              +{feedback.score}%
            </span>
          </div>
          <p style={{ fontSize: '11px', color: '#64748b', margin: 0, fontStyle: 'italic' }}>
            {feedback.message}
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {currentScenario?.responses.map((response, idx) => (
            <button
              key={idx}
              onClick={() => handleResponse(response)}
              style={{
                padding: '12px 14px',
                background: 'white',
                border: '2px solid #e2e8f0',
                borderRadius: '10px',
                textAlign: 'left',
                fontSize: '12px',
                lineHeight: '1.4',
                color: '#334155',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
              onMouseEnter={(e) => {
                e.target.style.borderColor = '#3b82f6';
                e.target.style.background = '#f8fafc';
              }}
              onMouseLeave={(e) => {
                e.target.style.borderColor = '#e2e8f0';
                e.target.style.background = 'white';
              }}
            >
              "{response.text}"
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ============ RANDOM EVENTS ============

const RANDOM_EVENTS = [
  // NEGATIVE EVENTS
  { type: 'negative', id: 'client_lawsuit', name: '⚖️ Client Dispute', desc: 'A former client is threatening legal action over "poor results"', effect: { cash: -2000, reputation: -8 }, chance: 0.03 },
  { type: 'negative', id: 'employee_poached', name: '👋 Employee Poached', desc: 'A rival agency offered your best employee double the salary', effect: { loseEmployee: true }, chance: 0.04 },
  { type: 'negative', id: 'bad_review', name: '⭐ Viral Bad Review', desc: 'An angry client left a scathing review that\'s getting attention', effect: { reputation: -12 }, chance: 0.05 },
  { type: 'negative', id: 'software_down', name: '💻 Tools Outage', desc: 'Your marketing software is down. Campaigns paused for a week.', effect: { clientSatisfaction: -10 }, chance: 0.04 },
  { type: 'negative', id: 'google_update', name: '🔄 Algorithm Update', desc: 'Google changed their algorithm. Your SEO rankings tanked.', effect: { clientSatisfaction: -15, reputation: -5 }, chance: 0.03 },
  { type: 'negative', id: 'key_client_bankruptcy', name: '💀 Client Went Bankrupt', desc: 'One of your clients just shut down. Outstanding invoices unpaid.', effect: { loseRandomClient: true, cash: -1500 }, chance: 0.03 },
  { type: 'negative', id: 'tax_audit', name: '📋 Tax Audit', desc: 'The IRS wants to review your books. Legal fees required.', effect: { cash: -3000 }, chance: 0.02 },
  { type: 'negative', id: 'office_issue', name: '🏢 Office Emergency', desc: 'Burst pipe flooded your office. Equipment damaged.', effect: { cash: -1500 }, chance: 0.03 },
  { type: 'negative', id: 'lead_data_breach', name: '🔓 Data Breach', desc: 'Your lead database was compromised. Trust is shaken.', effect: { reputation: -15, loseLeads: 3 }, chance: 0.02 },
  { type: 'negative', id: 'competitor_smear', name: '🗣️ Competitor Smear Campaign', desc: 'A rival is spreading rumors about your agency.', effect: { reputation: -10 }, chance: 0.04 },
  
  // POSITIVE EVENTS
  { type: 'positive', id: 'referral_boom', name: '🎉 Referral Boom', desc: 'A happy client referred three businesses to you!', effect: { hotLeads: 3 }, chance: 0.04 },
  { type: 'positive', id: 'viral_case_study', name: '📈 Viral Case Study', desc: 'Your case study went viral on LinkedIn!', effect: { reputation: 15, warmLeads: 2 }, chance: 0.03 },
  { type: 'positive', id: 'award_nomination', name: '🏆 Award Nomination', desc: 'Your agency was nominated for a local business award!', effect: { reputation: 10 }, chance: 0.04 },
  { type: 'positive', id: 'media_feature', name: '📰 Media Feature', desc: 'A local news outlet wants to feature your success story.', effect: { reputation: 12, warmLeads: 1 }, chance: 0.03 },
  { type: 'positive', id: 'client_expansion', name: '💰 Client Expansion', desc: 'Your biggest client wants to double their budget!', effect: { expandTopClient: true }, chance: 0.04 },
  { type: 'positive', id: 'windfall', name: '💵 Unexpected Payment', desc: 'An old client finally paid their overdue invoice!', effect: { cash: 2500 }, chance: 0.03 },
  { type: 'positive', id: 'tool_discount', name: '🎁 Software Deal', desc: 'Your marketing tools provider offered a loyalty discount.', effect: { reduceOverhead: 200 }, chance: 0.04 },
  { type: 'positive', id: 'speaking_gig', name: '🎤 Speaking Opportunity', desc: 'You\'ve been invited to speak at a marketing conference.', effect: { reputation: 8, cash: 1000 }, chance: 0.03 },
];

// Overhead costs that scale with business size
const OVERHEAD_COSTS = {
  base: 500, // Basic costs (software, phone, etc.)
  perEmployee: 300, // Per employee overhead
  perClient: 100, // Per client management overhead
  office: { // Office tiers
    home: { name: 'Home Office', cost: 0, maxEmployees: 2, prestigeBonus: 0 },
    small: { name: 'Small Office', cost: 800, maxEmployees: 5, prestigeBonus: 5 },
    professional: { name: 'Professional Suite', cost: 2000, maxEmployees: 10, prestigeBonus: 15 },
    agency: { name: 'Agency HQ', cost: 4000, maxEmployees: 20, prestigeBonus: 25 },
  }
};

// ============ MAIN GAME ============

const MarketingMillionaire = () => {
  // Game phases
  const [phase, setPhase] = useState('title'); // title, setup, playing, gameover
  const [specializations, setSpecializations] = useState([]);
  const [gameOverReason, setGameOverReason] = useState(null);
  const [showTutorial, setShowTutorial] = useState(false);
  const [hasSaveGame, setHasSaveGame] = useState(false);
  const [showSaveNotice, setShowSaveNotice] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showLoadModal, setShowLoadModal] = useState(false);
  const [cloudCode, setCloudCode] = useState('');
  const [showCloudImport, setShowCloudImport] = useState(false);
  
  // Check for saved game on mount
  useEffect(() => {
    // Check all 3 slots for any save
    let hasAnySave = false;
    for (let i = 1; i <= 3; i++) {
      if (localStorage.getItem(`marketingMillionaireSave_slot${i}`)) {
        hasAnySave = true;
        break;
      }
    }
    setHasSaveGame(hasAnySave);
  }, []);
  
  // Core resources
  const [cash, setCash] = useState(5000); // Start with more but have more costs
  const [reputation, setReputation] = useState(20);
  const [month, setMonth] = useState(1);
  const [actions, setActions] = useState(5);
  
  // Stats tracking for achievements
  const [stats, setStats] = useState({
    totalLeadsGenerated: 0,
    totalClientsWon: 0,
    totalHires: 0,
    totalPitches: 0,
    perfectPitches: 0,
    longestClientRetention: 0,
    maxClients: 0,
    maxEmployees: 0,
    peakCash: 5000,
  });
  
  // Achievements
  const [unlockedAchievements, setUnlockedAchievements] = useState([]);
  const [achievementPopup, setAchievementPopup] = useState(null);
  
  // Mini-game states
  const [networkingGame, setNetworkingGame] = useState(null);
  const [adsGame, setAdsGame] = useState(null);
  
  // Employee management states
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeModal, setEmployeeModal] = useState(null); // 'train' | 'bonus' | null
  
  // Client management states
  const [pendingDemands, setPendingDemands] = useState([]); // Active client demands
  const [pendingCampaignEvent, setPendingCampaignEvent] = useState(null); // Current campaign decision
  
  // New mechanics
  const [debt, setDebt] = useState(0); // Loan amount
  const [debtInterestRate] = useState(0.08); // 8% monthly interest
  const [office, setOffice] = useState('home');
  const [accountsReceivable, setAccountsReceivable] = useState([]); // Pending payments
  const [energy, setEnergy] = useState(100); // Personal burnout meter
  const [consecutiveLosses, setConsecutiveLosses] = useState(0); // Track bad months
  const [eventLog, setEventLog] = useState([]);
  const [pendingEvent, setPendingEvent] = useState(null);
  const [overheadReduction, setOverheadReduction] = useState(0); // From events
  
  // Market state
  const [marketCondition, setMarketCondition] = useState(MARKET_CONDITIONS[3]); // Start stable
  const [marketTurnsLeft, setMarketTurnsLeft] = useState(3);
  
  // Entities
  const [leads, setLeads] = useState([]);
  const [clients, setClients] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [rivals, setRivals] = useState(() => RIVALS.map(r => ({ ...r, cash: 3000, clients: 2, marketShare: 0.2, revenue: 800 })));
  
  // Leaderboard history - tracks month-by-month standings
  const [leaderboardHistory, setLeaderboardHistory] = useState([]);
  
  // Active campaigns
  const [activeCampaigns, setActiveCampaigns] = useState([]);
  
  // UI state
  const [tab, setTab] = useState('market');
  const [screen, setScreen] = useState('main');
  const [selectedLead, setSelectedLead] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [notification, setNotification] = useState(null);
  const [floaters, setFloaters] = useState([]);
  const [lastMilestoneIndex, setLastMilestoneIndex] = useState(0);
  const [hiringCandidates, setHiringCandidates] = useState(null); // For hiring modal
  
  // Computed
  const monthlyRevenue = clients.reduce((s, c) => s + c.budget, 0);
  const salaries = employees.reduce((s, e) => s + e.salary, 0);
  const officeData = OVERHEAD_COSTS.office[office];
  const overhead = OVERHEAD_COSTS.base + 
    (employees.length * OVERHEAD_COSTS.perEmployee) + 
    (clients.length * OVERHEAD_COSTS.perClient) + 
    officeData.cost - overheadReduction;
  const debtPayment = Math.floor(debt * debtInterestRate);
  const monthlyCosts = salaries + overhead + debtPayment;
  const netCashFlow = monthlyRevenue - monthlyCosts;
  const milestone = getMilestone(cash);
  const runway = netCashFlow < 0 ? Math.floor(cash / Math.abs(netCashFlow)) : 999;
  
  // Employee bonus calculations
  const bonusActions = employees.reduce((sum, e) => sum + (e.effectiveBonusActions || 0), 0);
  const maxActions = 5 + bonusActions;
  const totalAutoClose = employees.reduce((sum, e) => sum + (e.effectiveAutoClose || 0), 0);
  const totalClientBonus = employees.reduce((sum, e) => sum + (e.effectiveClientBonus || 0), 0);
  const totalCampaignBonus = employees.reduce((sum, e) => sum + (e.effectiveCampaignBonus || 0), 0);
  const totalLeadBonus = employees.reduce((sum, e) => sum + (e.effectiveLeadBonus || 0), 0);
  
  // Achievement checking
  useEffect(() => {
    if (phase !== 'playing') return;
    const currentStats = {
      ...stats,
      maxClients: Math.max(stats.maxClients, clients.length),
      maxEmployees: Math.max(stats.maxEmployees, employees.length),
      peakCash: Math.max(stats.peakCash, cash),
      longestClientRetention: Math.max(stats.longestClientRetention, ...clients.map(c => c.monthsActive || 0), 0),
    };
    if (JSON.stringify(currentStats) !== JSON.stringify(stats)) {
      setStats(currentStats);
    }
    // Check for new achievements
    ACHIEVEMENTS.forEach(ach => {
      if (!unlockedAchievements.includes(ach.id) && ach.check(currentStats)) {
        setUnlockedAchievements(prev => [...prev, ach.id]);
        setAchievementPopup(ach);
        setTimeout(() => setAchievementPopup(null), 4000);
      }
    });
  }, [cash, clients, employees, stats, phase, unlockedAchievements]);
  
  // Check for game over conditions
  useEffect(() => {
    if (phase !== 'playing') return;
    
    // Bankruptcy - negative cash for too long or deeply negative
    if (cash < -5000) {
      setGameOverReason({ 
        type: 'bankruptcy', 
        title: '💀 BANKRUPT', 
        desc: 'You\'ve accumulated too much debt and creditors have forced you to close.',
        tip: 'Keep an emergency fund and don\'t over-expand before you have steady revenue.'
      });
      setPhase('gameover');
      return;
    }
    
    // Reputation destroyed
    if (reputation <= 0) {
      setGameOverReason({ 
        type: 'reputation', 
        title: '📉 REPUTATION DESTROYED', 
        desc: 'Word has spread that your agency can\'t deliver. No one will work with you.',
        tip: 'Keep clients happy with successful campaigns and avoid taking on more than you can handle.'
      });
      setPhase('gameover');
      return;
    }
    
    // Burnout
    if (energy <= 0) {
      setGameOverReason({ 
        type: 'burnout', 
        title: '😵 BURNOUT', 
        desc: 'You pushed yourself too hard. Your health forced you to shut down the agency.',
        tip: 'Hire employees to reduce workload and take rest actions when energy is low.'
      });
      setPhase('gameover');
      return;
    }
    
    // Too many consecutive losses
    if (consecutiveLosses >= 6) {
      setGameOverReason({ 
        type: 'spiral', 
        title: '📉 DEATH SPIRAL', 
        desc: '6 months of losses. Investors and creditors have lost confidence.',
        tip: 'If you\'re bleeding money, cut costs or pivot strategy before it\'s too late.'
      });
      setPhase('gameover');
      return;
    }
  }, [cash, reputation, energy, consecutiveLosses, phase]);
  
  // Milestone check
  useEffect(() => {
    const { index } = getMilestone(cash);
    if (index > lastMilestoneIndex) {
      notify(`🎉 Milestone: ${MILESTONES[index].name}!`, 'success');
      setLastMilestoneIndex(index);
    }
  }, [cash, lastMilestoneIndex]);
  
  const notify = useCallback((msg, type = 'info') => {
    setNotification({ msg, type });
    setTimeout(() => setNotification(null), 3000);
  }, []);
  
  // Generate save data object
  const generateSaveData = useCallback(() => {
    return {
      version: '3.0',
      savedAt: new Date().toISOString(),
      // Core state
      phase,
      specializations,
      cash,
      reputation,
      month,
      actions,
      stats,
      unlockedAchievements,
      // Mechanics
      debt,
      office,
      energy,
      consecutiveLosses,
      eventLog,
      overheadReduction,
      // Market
      marketCondition,
      marketTurnsLeft,
      // Entities
      leads,
      clients,
      employees,
      rivals,
      leaderboardHistory,
      pendingDemands,
      // UI
      tab,
    };
  }, [phase, specializations, cash, reputation, month, actions, stats, unlockedAchievements, 
      debt, office, energy, consecutiveLosses, eventLog, overheadReduction,
      marketCondition, marketTurnsLeft, leads, clients, employees, rivals, 
      leaderboardHistory, pendingDemands, tab]);

  // Restore game state from data object
  const restoreFromSaveData = useCallback((data) => {
    setPhase(data.phase || 'playing');
    setSpecializations(data.specializations || []);
    setCash(data.cash || 5000);
    setReputation(data.reputation || 20);
    setMonth(data.month || 1);
    setActions(data.actions || 5);
    setStats(data.stats || {});
    setUnlockedAchievements(data.unlockedAchievements || []);
    setDebt(data.debt || 0);
    setOffice(data.office || 'home');
    setEnergy(data.energy || 100);
    setConsecutiveLosses(data.consecutiveLosses || 0);
    setEventLog(data.eventLog || []);
    setOverheadReduction(data.overheadReduction || 0);
    setMarketCondition(data.marketCondition || MARKET_CONDITIONS[3]);
    setMarketTurnsLeft(data.marketTurnsLeft || 3);
    setLeads(data.leads || []);
    setClients(data.clients || []);
    setEmployees(data.employees || []);
    setRivals(data.rivals || RIVALS.map(r => ({ ...r, cash: 3000, clients: 2, marketShare: 0.2, revenue: 800 })));
    setLeaderboardHistory(data.leaderboardHistory || []);
    setPendingDemands(data.pendingDemands || []);
    setTab(data.tab || 'market');
  }, []);

  // Get all save slots info
  const getSaveSlots = useCallback(() => {
    const slots = [];
    for (let i = 1; i <= 3; i++) {
      const saved = localStorage.getItem(`marketingMillionaireSave_slot${i}`);
      if (saved) {
        try {
          const data = JSON.parse(saved);
          slots.push({
            slot: i,
            exists: true,
            month: data.month,
            cash: data.cash,
            savedAt: data.savedAt,
            specializations: data.specializations,
          });
        } catch {
          slots.push({ slot: i, exists: false });
        }
      } else {
        slots.push({ slot: i, exists: false });
      }
    }
    return slots;
  }, []);

  // Save game to specific slot
  const saveGameToSlot = useCallback((slotNumber) => {
    const saveData = generateSaveData();
    try {
      localStorage.setItem(`marketingMillionaireSave_slot${slotNumber}`, JSON.stringify(saveData));
      setHasSaveGame(true);
      setShowSaveNotice(true);
      setTimeout(() => setShowSaveNotice(false), 2000);
      notify(`Saved to Slot ${slotNumber}!`, 'success');
      return true;
    } catch (e) {
      notify('Failed to save game', 'error');
      return false;
    }
  }, [generateSaveData, notify]);

  // Quick save (uses slot 1 / auto-save slot)
  const saveGame = useCallback(() => {
    saveGameToSlot(1);
  }, [saveGameToSlot]);

  // Load game from specific slot
  const loadGameFromSlot = useCallback((slotNumber) => {
    try {
      const saved = localStorage.getItem(`marketingMillionaireSave_slot${slotNumber}`);
      if (!saved) {
        notify('No saved game in this slot', 'error');
        return false;
      }
      
      const data = JSON.parse(saved);
      restoreFromSaveData(data);
      notify(`Loaded Slot ${slotNumber}!`, 'success');
      return true;
    } catch (e) {
      notify('Failed to load save', 'error');
      return false;
    }
  }, [restoreFromSaveData, notify]);

  // Quick load (uses slot 1)
  const loadGame = useCallback(() => {
    return loadGameFromSlot(1);
  }, [loadGameFromSlot]);

  // Delete specific save slot
  const deleteSaveSlot = useCallback((slotNumber) => {
    localStorage.removeItem(`marketingMillionaireSave_slot${slotNumber}`);
    // Check if any saves remain
    const remaining = getSaveSlots().some(s => s.exists);
    setHasSaveGame(remaining);
    notify(`Slot ${slotNumber} deleted`, 'info');
  }, [getSaveSlots, notify]);

  // Delete all saves
  const deleteSave = useCallback(() => {
    for (let i = 1; i <= 3; i++) {
      localStorage.removeItem(`marketingMillionaireSave_slot${i}`);
    }
    setHasSaveGame(false);
    notify('All saves deleted', 'info');
  }, [notify]);

  // Export save as shareable code
  const exportSaveCode = useCallback(() => {
    const saveData = generateSaveData();
    try {
      // Compress and encode
      const jsonStr = JSON.stringify(saveData);
      const encoded = btoa(unescape(encodeURIComponent(jsonStr)));
      return encoded;
    } catch (e) {
      notify('Failed to export save', 'error');
      return null;
    }
  }, [generateSaveData, notify]);

  // Import save from code
  const importSaveCode = useCallback((code) => {
    try {
      // Decode and decompress
      const jsonStr = decodeURIComponent(escape(atob(code.trim())));
      const data = JSON.parse(jsonStr);
      
      if (!data.version || !data.month) {
        throw new Error('Invalid save data');
      }
      
      restoreFromSaveData(data);
      // Also save to slot 1
      localStorage.setItem('marketingMillionaireSave_slot1', JSON.stringify(data));
      setHasSaveGame(true);
      notify('Save imported successfully!', 'success');
      return true;
    } catch (e) {
      notify('Invalid save code', 'error');
      return false;
    }
  }, [restoreFromSaveData, notify]);

  // Auto-save every month end
  useEffect(() => {
    if (phase === 'playing' && month > 1) {
      saveGame();
    }
  }, [month, phase]); // Intentionally not including saveGame to avoid loops

  const addFloater = useCallback((value, color, e) => {
    const rect = e?.target?.getBoundingClientRect();
    const x = rect ? rect.left + rect.width / 2 : window.innerWidth / 2;
    const y = rect ? rect.top : 100;
    const id = Date.now() + Math.random();
    setFloaters(prev => [...prev, { id, value, color, x, y }]);
    setTimeout(() => setFloaters(prev => prev.filter(f => f.id !== id)), 1200);
  }, []);
  
  // Initialize game after specialization pick
  const startGame = (specs) => {
    setSpecializations(specs);
    // Generate initial leads based on specializations
    const initialLeads = [];
    for (let i = 0; i < 4; i++) {
      initialLeads.push(genLead(reputation, specs, marketCondition));
    }
    setLeads(initialLeads);
    setPhase('playing');
  };
  
  // ============ ACTIONS ============
  
  const prospect = (source, e) => {
    const costs = { cold: 0, networking: 100, ads: 300 };
    const actionCosts = { cold: 1, networking: 2, ads: 2 };
    
    if (actions < actionCosts[source]) return notify(`Need ${actionCosts[source]} actions!`, 'error');
    if (cash < costs[source]) return notify(`Need $${costs[source]}!`, 'error');
    
    setActions(a => a - actionCosts[source]);
    setCash(c => c - costs[source]);
    
    // Launch mini-games for networking and ads
    if (source === 'networking') {
      setNetworkingGame({ active: true });
      return;
    }
    
    if (source === 'ads') {
      setAdsGame({ active: true });
      return;
    }
    
    // Cold calling - direct leads with lead bonus
    const count = 1 + (Math.random() < 0.3 ? 1 : 0) + totalLeadBonus;
    for (let i = 0; i < count; i++) {
      const lead = genLead(reputation, specializations, marketCondition);
      lead.temp = 'cool';
      lead.source = source;
      setLeads(prev => [...prev, lead]);
    }
    
    setStats(s => ({ ...s, totalLeadsGenerated: s.totalLeadsGenerated + count }));
    notify(`+${count} new leads!`, 'success');
    if (e) addFloater(`+${count} leads`, '#3b82f6', e);
  };
  
  // Networking game completion handler
  const completeNetworking = (leadCount, quality) => {
    setNetworkingGame(null);
    for (let i = 0; i < leadCount; i++) {
      const lead = genLead(reputation, specializations, marketCondition);
      lead.temp = quality;
      lead.source = 'networking';
      setLeads(prev => [...prev, lead]);
    }
    if (leadCount > 0) {
      setStats(s => ({ ...s, totalLeadsGenerated: s.totalLeadsGenerated + leadCount }));
      notify(`Networking success! +${leadCount} ${quality} leads`, 'success');
    } else {
      notify('Networking event was a bust...', 'error');
    }
  };
  
  // Ads game completion handler
  const completeAds = (leadCount, quality, cashLost) => {
    setAdsGame(null);
    setCash(c => c - cashLost);
    for (let i = 0; i < leadCount; i++) {
      const lead = genLead(reputation, specializations, marketCondition);
      lead.temp = quality;
      lead.source = 'ads';
      setLeads(prev => [...prev, lead]);
    }
    if (leadCount > 0) {
      setStats(s => ({ ...s, totalLeadsGenerated: s.totalLeadsGenerated + leadCount }));
      notify(`Campaign complete! +${leadCount} leads${cashLost > 0 ? `, -$${cashLost} wasted` : ''}`, leadCount > 2 ? 'success' : 'warning');
    } else {
      notify(`Campaign failed! $${cashLost} wasted`, 'error');
    }
  };
  
  const nurtureLead = (lead, e) => {
    if (actions < 1) return notify('Need 1 action!', 'error');
    setActions(a => a - 1);
    
    const temps = ['cold', 'cool', 'warm', 'hot'];
    const currentIdx = temps.indexOf(lead.temp);
    const empBonus = employees.filter(e => e.skills.includes('nurture')).length > 0;
    const newIdx = Math.min(temps.length - 1, currentIdx + (empBonus ? 2 : 1));
    
    setLeads(prev => prev.map(l => 
      l.id === lead.id ? { ...l, temp: temps[newIdx], tempAge: 0 } : l
    ));
    
    notify(`${lead.firstName} warmed up to ${temps[newIdx]}!`, 'success');
  };
  
  const startPitch = (lead) => {
    if (actions < 1) return notify('Need 1 action!', 'error');
    setActions(a => a - 1);
    setSelectedLead(lead);
    setScreen('pitch');
  };
  
  const completePitch = (won, wasPerfect = false) => {
    setStats(s => ({ 
      ...s, 
      totalPitches: s.totalPitches + 1,
      totalClientsWon: won ? s.totalClientsWon + 1 : s.totalClientsWon,
      perfectPitches: wasPerfect ? s.perfectPitches + 1 : s.perfectPitches,
    }));
    
    if (won) {
      const newClient = {
        ...selectedLead,
        satisfaction: 100,
        monthsRetained: 0,
        monthsActive: 0,
        campaign: null,
        performance: 1.0,
        demands: selectedLead.personalityData.patience,
      };
      setClients(prev => [...prev, newClient]);
      setCash(c => c + newClient.budget); // Signing bonus
      setReputation(r => Math.min(100, r + 2));
    } else {
      // Failed pitch - lead goes cold
      setLeads(prev => prev.map(l => 
        l.id === selectedLead.id ? { ...l, temp: 'cold', tempAge: 0 } : l
      ));
    }
    setLeads(prev => prev.filter(l => l.id !== selectedLead.id));
    setSelectedLead(null);
    setScreen('main');
  };
  
  const planCampaign = (client) => {
    if (client.campaign) return notify('Already has active campaign!', 'error');
    setSelectedClient(client);
    setScreen('campaign');
  };
  
  const launchCampaign = (plan) => {
    setClients(prev => prev.map(c => 
      c.id === selectedClient.id ? {
        ...c,
        campaign: {
          ...plan.campaign,
          budget: plan.budget,
          approach: plan.approach,
          monthsLeft: plan.campaign.duration,
          projectedResult: plan.projected,
          risk: plan.risk,
        }
      } : c
    ));
    setSelectedClient(null);
    setScreen('main');
    notify(`Campaign launched for ${selectedClient.firstName}!`, 'success');
  };
  
  const hireEmployee = () => {
    // Check office capacity first
    if (employees.length >= officeData.maxEmployees) {
      return notify(`Office at capacity! Upgrade to hire more.`, 'error');
    }
    
    // Generate 3 unique candidates
    const candidates = Array.from({ length: 3 }, genEmployee);
    setHiringCandidates(candidates);
  };
  
  const confirmHire = (emp) => {
    if (cash < emp.cost) {
      notify('Not enough cash to hire!', 'error');
      return;
    }
    setCash(c => c - emp.cost);
    setEmployees(prev => [...prev, emp]);
    setStats(s => ({ ...s, totalHires: s.totalHires + 1 }));
    setHiringCandidates(null);
    notify(`Hired ${emp.name}!`, 'success');
  };
  
  const fireEmployee = (emp) => {
    const severance = emp.salary * 2;
    setCash(c => c - severance);
    setEmployees(prev => prev.filter(e => e.id !== emp.id));
    notify(`Fired ${emp.name}. Paid $${severance} severance.`, 'info');
  };
  
  // Train an employee
  const trainEmployee = (emp, training) => {
    if (cash < training.cost) return notify(`Need $${training.cost} for training!`, 'error');
    
    setCash(c => c - training.cost);
    setEmployees(prev => prev.map(e => {
      if (e.id !== emp.id) return e;
      const newTrainingLevel = Math.min(100, e.trainingLevel + training.effectivenessBoost);
      const effectivenessMult = 1 + (newTrainingLevel / 100); // Up to 2x at 100%
      return {
        ...e,
        trainingLevel: newTrainingLevel,
        morale: Math.min(100, e.morale + training.moraleBoost),
        // Recalculate effective bonuses with training
        effectiveBonusActions: Math.floor(e.bonusActions * e.productivity * effectivenessMult),
        effectiveAutoClose: Math.floor(e.autoClose * e.productivity * effectivenessMult),
        effectiveClientBonus: Math.floor(e.clientBonus * e.productivity * effectivenessMult),
        effectiveCampaignBonus: Math.floor(e.campaignBonus * e.productivity * effectivenessMult),
        effectiveLeadBonus: Math.floor(e.leadBonus * e.productivity * effectivenessMult),
      };
    }));
    setEmployeeModal(null);
    setSelectedEmployee(null);
    notify(`${emp.name} completed ${training.name}! +${training.effectivenessBoost}% effectiveness`, 'success');
  };
  
  // Give bonus to employee
  const giveBonus = (emp, bonus) => {
    if (cash < bonus.cost) return notify(`Need $${bonus.cost} for bonus!`, 'error');
    
    setCash(c => c - bonus.cost);
    setEmployees(prev => prev.map(e => {
      if (e.id !== emp.id) return e;
      return {
        ...e,
        morale: Math.min(100, e.morale + bonus.moraleBoost),
        loyaltyMonths: e.loyaltyMonths + (bonus.loyaltyMonths || 0),
      };
    }));
    setEmployeeModal(null);
    setSelectedEmployee(null);
    notify(`Gave ${emp.name} a ${bonus.name}! +${bonus.moraleBoost} morale`, 'success');
  };
  
  // Handle client demand
  const handleClientDemand = (demand, accept) => {
    const client = clients.find(c => c.id === demand.clientId);
    if (!client) {
      setPendingDemands(prev => prev.filter(d => d.id !== demand.id));
      return;
    }
    
    if (accept) {
      // Check if we can afford it
      if (demand.actionCost && actions < demand.actionCost) {
        return notify(`Need ${demand.actionCost} actions!`, 'error');
      }
      if (demand.cashCost && cash < demand.cashCost) {
        return notify(`Need $${demand.cashCost}!`, 'error');
      }
      
      // Pay costs
      if (demand.actionCost) setActions(a => a - demand.actionCost);
      if (demand.cashCost) setCash(c => c - demand.cashCost);
      
      // Handle risky demands
      if (demand.successChance) {
        const success = Math.random() < demand.successChance;
        if (success) {
          setClients(prev => prev.map(c => 
            c.id === client.id ? { ...c, satisfaction: Math.min(100, c.satisfaction + demand.satisfactionGain) } : c
          ));
          notify(`${client.firstName}'s ${demand.name} was successful! +${demand.satisfactionGain} satisfaction`, 'success');
        } else {
          const penalty = demand.failEffect || -15;
          setClients(prev => prev.map(c => 
            c.id === client.id ? { ...c, satisfaction: Math.max(0, c.satisfaction + penalty) } : c
          ));
          notify(`${client.firstName}'s ${demand.name} failed! ${penalty} satisfaction`, 'error');
        }
      } else {
        // Guaranteed success
        setClients(prev => prev.map(c => 
          c.id === client.id ? { 
            ...c, 
            satisfaction: Math.min(100, c.satisfaction + demand.satisfactionGain),
            budget: demand.budgetCut ? Math.floor(c.budget * (1 - demand.budgetCut)) : c.budget,
          } : c
        ));
        if (demand.budgetCut) {
          notify(`Accepted ${client.firstName}'s discount request. Budget reduced 15%.`, 'info');
        } else {
          notify(`Handled ${client.firstName}'s ${demand.name}. +${demand.satisfactionGain} satisfaction`, 'success');
        }
      }
    } else {
      // Ignore the demand
      setClients(prev => prev.map(c => 
        c.id === client.id ? { ...c, satisfaction: Math.max(0, c.satisfaction + demand.ignoreEffect) } : c
      ));
      notify(`Ignored ${client.firstName}'s ${demand.name}. ${demand.ignoreEffect} satisfaction!`, 'warning');
    }
    
    setPendingDemands(prev => prev.filter(d => d.id !== demand.id));
  };
  
  // Handle campaign event decision
  const handleCampaignEvent = (option) => {
    const event = pendingCampaignEvent;
    if (!event) return;
    
    // Check costs
    if (option.actionCost && actions < option.actionCost) {
      return notify(`Need ${option.actionCost} actions!`, 'error');
    }
    if (option.cost && cash < option.cost) {
      return notify(`Need $${option.cost}!`, 'error');
    }
    
    // Pay costs
    if (option.actionCost) setActions(a => a - option.actionCost);
    if (option.cost) setCash(c => c - option.cost);
    
    // Determine success
    const success = Math.random() < option.successChance;
    const effect = success ? option.successEffect : option.failEffect;
    
    // Apply to client satisfaction
    setClients(prev => prev.map(c => 
      c.id === event.clientId ? { 
        ...c, 
        satisfaction: Math.max(0, Math.min(100, c.satisfaction + effect)) 
      } : c
    ));
    
    const client = clients.find(c => c.id === event.clientId);
    if (success && effect > 0) {
      notify(`${event.name} handled well for ${client?.firstName}! +${effect} satisfaction`, 'success');
    } else if (!success && effect < 0) {
      notify(`${event.name} backfired for ${client?.firstName}! ${effect} satisfaction`, 'error');
    } else {
      notify(`${event.name} resolved for ${client?.firstName}.`, 'info');
    }
    
    setPendingCampaignEvent(null);
  };
  
  const takeLoan = (amount) => {
    if (debt > cash * 2) return notify('Credit limit reached!', 'error');
    setDebt(d => d + amount);
    setCash(c => c + amount);
    notify(`Took $${amount} loan at 8% monthly interest.`, 'info');
  };
  
  const payDebt = (amount) => {
    const payment = Math.min(amount, debt, cash);
    if (payment <= 0) return;
    setDebt(d => d - payment);
    setCash(c => c - payment);
    notify(`Paid off $${payment} of debt.`, 'success');
  };
  
  const rest = () => {
    if (actions < 2) return notify('Need 2 actions to rest!', 'error');
    setActions(a => a - 2);
    setEnergy(e => Math.min(100, e + 25));
    notify('Took time off. Energy restored!', 'success');
  };
  
  const upgradeOffice = (newOffice) => {
    const costs = { small: 5000, professional: 15000, agency: 40000 };
    const cost = costs[newOffice];
    if (!cost) return;
    if (cash < cost) return notify(`Need $${cost} to upgrade!`, 'error');
    
    setCash(c => c - cost);
    setOffice(newOffice);
    setReputation(r => Math.min(100, r + OVERHEAD_COSTS.office[newOffice].prestigeBonus));
    notify(`Upgraded to ${OVERHEAD_COSTS.office[newOffice].name}!`, 'success');
  };
  
  const applyEvent = () => {
    if (!pendingEvent) return;
    
    const effect = pendingEvent.effect;
    
    if (effect.cash) setCash(c => c + effect.cash);
    if (effect.reputation) setReputation(r => Math.max(0, Math.min(100, r + effect.reputation)));
    if (effect.clientSatisfaction) {
      setClients(prev => prev.map(c => ({
        ...c,
        satisfaction: Math.max(0, Math.min(100, c.satisfaction + effect.clientSatisfaction))
      })));
    }
    if (effect.loseEmployee && employees.length > 0) {
      const best = employees.reduce((a, b) => a.productivity > b.productivity ? a : b);
      setEmployees(prev => prev.filter(e => e.id !== best.id));
      notify(`${best.name} was poached by a competitor!`, 'error');
    }
    if (effect.loseRandomClient && clients.length > 0) {
      const victim = pick(clients);
      setClients(prev => prev.filter(c => c.id !== victim.id));
    }
    if (effect.loseLeads) {
      setLeads(prev => prev.slice(effect.loseLeads));
    }
    if (effect.hotLeads) {
      for (let i = 0; i < effect.hotLeads; i++) {
        const lead = genLead(reputation, specializations, marketCondition);
        lead.temp = 'hot';
        lead.source = 'referral';
        setLeads(prev => [...prev, lead]);
      }
    }
    if (effect.warmLeads) {
      for (let i = 0; i < effect.warmLeads; i++) {
        const lead = genLead(reputation, specializations, marketCondition);
        lead.temp = 'warm';
        lead.source = 'referral';
        setLeads(prev => [...prev, lead]);
      }
    }
    if (effect.expandTopClient && clients.length > 0) {
      const top = clients.reduce((a, b) => a.budget > b.budget ? a : b);
      setClients(prev => prev.map(c => 
        c.id === top.id ? { ...c, budget: Math.floor(c.budget * 1.5) } : c
      ));
      notify(`${top.firstName} increased their budget by 50%!`, 'success');
    }
    if (effect.reduceOverhead) {
      setOverheadReduction(prev => prev + effect.reduceOverhead);
    }
    
    setEventLog(prev => [...prev, { ...pendingEvent, month }]);
    setPendingEvent(null);
  };
  
  const endMonth = (e) => {
    // 0. Check for random event
    const eventRoll = Math.random();
    let cumulativeChance = 0;
    for (const event of RANDOM_EVENTS) {
      cumulativeChance += event.chance;
      if (eventRoll < cumulativeChance) {
        setPendingEvent(event);
        break;
      }
    }
    
    // 1. Collect revenue (some clients pay late)
    const revenue = monthlyRevenue;
    
    // 2. Pay all costs
    const totalCosts = monthlyCosts;
    const net = revenue - totalCosts;
    
    // Track consecutive losses
    if (net < 0) {
      setConsecutiveLosses(prev => prev + 1);
    } else {
      setConsecutiveLosses(0);
    }
    
    setCash(c => c + net);
    if (net !== 0) addFloater(net >= 0 ? `+$${net}` : `-$${Math.abs(net)}`, net >= 0 ? '#22c55e' : '#ef4444', e);
    
    // 3. Pay down debt automatically (minimum payment)
    if (debt > 0) {
      const minPayment = Math.min(debt, Math.floor(debt * 0.1) + 100);
      setDebt(d => Math.max(0, d - minPayment));
      setCash(c => c - minPayment);
    }
    
    // 4. Energy drain based on workload
    const workload = clients.length * 5 + (employees.length === 0 ? 15 : 0) + (leads.length > 10 ? 5 : 0);
    const restFromEmployees = employees.length * 3;
    const energyChange = -workload + restFromEmployees + 10; // Base recovery of 10
    setEnergy(prev => Math.max(0, Math.min(100, prev + energyChange)));
    
    // 5. Process campaigns
    const processedClients = clients.map(client => {
      if (!client.campaign) {
        // No campaign = satisfaction drops
        return { ...client, satisfaction: Math.max(0, client.satisfaction - 15), demands: client.demands - 0.5 };
      }
      
      client.campaign.monthsLeft--;
      
      if (client.campaign.monthsLeft <= 0) {
        // Campaign finished - calculate results
        const roll = randFloat(client.campaign.minResult, client.campaign.maxResult);
        const success = roll >= 0.8;
        const newPerformance = client.performance * roll;
        const satChange = success ? 15 : -20;
        
        notify(success ? 
          `✅ ${client.firstName}'s campaign succeeded!` : 
          `❌ ${client.firstName}'s campaign underperformed`, 
          success ? 'success' : 'error'
        );
        
        if (!success) {
          setReputation(r => Math.max(0, r - 3));
        }
        
        return {
          ...client,
          campaign: null,
          performance: newPerformance,
          satisfaction: Math.max(0, Math.min(100, client.satisfaction + satChange)),
          demands: client.demands - 0.3,
        };
      }
      
      return { ...client, satisfaction: Math.min(100, client.satisfaction + 5) };
    });
    
    // 6. Check for client churn
    const retained = [];
    const churned = [];
    processedClients.forEach(client => {
      client.monthsRetained++;
      if (client.satisfaction < 20 || client.demands <= 0) {
        churned.push(client);
        setReputation(r => Math.max(0, r - 5));
      } else if (client.satisfaction < 40 && Math.random() < 0.3) {
        // Unhappy clients might leave randomly
        churned.push(client);
        setReputation(r => Math.max(0, r - 3));
      } else {
        retained.push(client);
      }
    });
    
    if (churned.length > 0) {
      notify(`😢 Lost ${churned.length} client${churned.length > 1 ? 's' : ''}! (${churned.map(c => c.firstName).join(', ')})`, 'error');
    }
    setClients(retained);
    
    // 7. Decay lead temperature
    setLeads(prev => prev.map(lead => {
      const newAge = lead.tempAge + 1;
      const temps = ['hot', 'warm', 'cool', 'cold'];
      const currentIdx = temps.indexOf(lead.temp);
      const decayRate = marketCondition.leadDecay || 1;
      const shouldDecay = newAge >= (2 / decayRate);
      
      if (shouldDecay && currentIdx < temps.length - 1) {
        return { ...lead, temp: temps[currentIdx + 1], tempAge: 0 };
      }
      return { ...lead, tempAge: newAge };
    }).filter(lead => lead.temp !== 'cold' || lead.tempAge < 2)); // Remove old cold leads
    
    // 8. Market condition changes
    if (marketTurnsLeft <= 1) {
      const newCondition = pick(MARKET_CONDITIONS);
      setMarketCondition(newCondition);
      setMarketTurnsLeft(randInt(2, 4));
      notify(`📊 Market shift: ${newCondition.name}`, 'info');
    } else {
      setMarketTurnsLeft(t => t - 1);
    }
    
    // 9. Rival actions - more competitive!
    let updatedRivals = rivals.map(rival => {
      // Different growth strategies
      let clientGrowth = 0;
      let cashGrowth = 0;
      const marketMult = marketCondition.rivalAggression || 1;
      
      if (rival.strategy === 'aggressive') {
        // Aggressive: Fast cash burn but high growth
        clientGrowth = Math.random() < 0.25 * marketMult ? 1 : 0;
        cashGrowth = rival.clients * 350 - 500; // Higher overhead
        
        // Try to steal unhappy clients
        if (retained.length > 0 && Math.random() < 0.15 * marketMult) {
          const targetClient = retained.find(c => c.satisfaction < 60);
          if (targetClient) {
            setClients(prev => prev.filter(c => c.id !== targetClient.id));
            notify(`🦈 ${rival.name} stole ${targetClient.firstName}!`, 'error');
            setReputation(r => Math.max(0, r - 3));
            clientGrowth += 1;
          }
        }
      } else if (rival.strategy === 'volume') {
        // Volume: Steady growth, thin margins
        clientGrowth = Math.random() < 0.2 * marketMult ? 1 : (Math.random() < 0.1 ? -1 : 0);
        cashGrowth = rival.clients * 250; // Lower revenue per client
      } else if (rival.strategy === 'premium') {
        // Premium: Slow growth, high margins
        clientGrowth = Math.random() < 0.1 * marketMult ? 1 : 0;
        cashGrowth = rival.clients * 600; // Higher revenue per client
        
        // Premium agencies can also lose clients to you if your reputation is high
        if (reputation > 60 && Math.random() < 0.1) {
          clientGrowth -= 1;
        }
      }
      
      // Random events affect rivals too
      if (Math.random() < 0.05) {
        // Bad month for rival
        clientGrowth -= 1;
        cashGrowth -= 1000;
      }
      
      const newClients = Math.max(0, rival.clients + clientGrowth);
      const newRevenue = newClients * (rival.strategy === 'premium' ? 600 : rival.strategy === 'aggressive' ? 350 : 250);
      
      return {
        ...rival,
        cash: Math.max(0, rival.cash + cashGrowth),
        clients: newClients,
        revenue: newRevenue,
      };
    });
    
    setRivals(updatedRivals);
    
    // 9.5 Track leaderboard history
    const allAgencies = [
      { id: 'player', name: 'Your Agency', icon: '🏠', cash: cash + net, clients: retained.length, isPlayer: true },
      ...updatedRivals
    ].sort((a, b) => b.cash - a.cash);
    
    const playerRank = allAgencies.findIndex(a => a.isPlayer) + 1;
    const leader = allAgencies[0];
    
    setLeaderboardHistory(prev => {
      const newHistory = [...prev, {
        month: month,
        playerRank,
        playerCash: cash + net,
        playerClients: retained.length,
        leader: { icon: leader.icon, cash: leader.cash, name: leader.name },
        standings: allAgencies.map(a => ({ icon: a.icon, cash: a.cash, clients: a.clients })),
      }];
      
      // Notify on rank changes
      if (prev.length > 0) {
        const lastRank = prev[prev.length - 1].playerRank;
        if (playerRank < lastRank) {
          notify(`📈 You moved up to #${playerRank} in the rankings!`, 'success');
        } else if (playerRank > lastRank) {
          notify(`📉 You dropped to #${playerRank} in the rankings.`, 'warning');
        }
        if (playerRank === 1 && lastRank !== 1) {
          notify(`🏆 YOU'RE NOW #1! Leading the market!`, 'success');
        }
      }
      
      return newHistory;
    });
    
    // 10. Passive lead generation from reputation
    if (reputation > 30 && Math.random() < reputation / 150) {
      const newLead = genLead(reputation, specializations, marketCondition);
      newLead.temp = 'warm';
      newLead.source = 'referral';
      setLeads(prev => [...prev, newLead]);
      notify('📬 Referral lead arrived!', 'success');
    }
    
    // 11. Employee stuff - morale decay, training effects, quitting
    setEmployees(prev => prev.map(emp => {
      // Morale decays monthly, affected by company health
      let moraleChange = -8; // Base decay
      if (net > 0) moraleChange += 5; // Profitable month
      if (net < -1000) moraleChange -= 5; // Bad month
      if (emp.trainingLevel > 0) moraleChange += 2; // Trained employees happier
      
      return {
        ...emp,
        monthsEmployed: emp.monthsEmployed + 1,
        morale: Math.max(0, Math.min(100, emp.morale + moraleChange)),
        loyaltyMonths: Math.max(0, emp.loyaltyMonths - 1), // Decrement loyalty timer
      };
    }).filter(emp => {
      // Employees with loyalty months won't quit
      if (emp.loyaltyMonths > 0) return true;
      
      if (emp.morale < 30 && Math.random() < emp.personalityData.turnoverRisk * 2) {
        notify(`👋 ${emp.name} quit due to low morale (${emp.morale}%)!`, 'error');
        return false;
      }
      if (emp.morale < 15) {
        notify(`👋 ${emp.name} quit! Morale was critical.`, 'error');
        return false;
      }
      return true;
    }));
    
    // 12. Generate client demands for next month
    const newDemands = [];
    retained.forEach(client => {
      // Update monthsActive for achievements
      client.monthsActive = (client.monthsActive || 0) + 1;
      
      // Check for demands based on personality
      CLIENT_DEMANDS.forEach(demandType => {
        if (demandType.personalities.includes(client.personality) && Math.random() < demandType.chance) {
          newDemands.push({
            ...demandType,
            id: `demand_${Date.now()}_${Math.random()}`,
            clientId: client.id,
            clientName: client.firstName,
            clientBusiness: client.businessName,
          });
        }
      });
    });
    if (newDemands.length > 0) {
      setPendingDemands(prev => [...prev, ...newDemands.slice(0, 3)]); // Max 3 new demands
    }
    
    // 13. Generate campaign events for active campaigns
    retained.forEach(client => {
      if (client.campaign && client.campaign.monthsLeft > 0 && Math.random() < 0.35) {
        const event = pick(CAMPAIGN_EVENTS);
        if (!pendingCampaignEvent) { // Only one at a time
          setPendingCampaignEvent({
            ...event,
            clientId: client.id,
            clientName: client.firstName,
          });
        }
      }
    });
    
    // 14. Reputation decay/growth based on performance
    if (retained.length > 0) {
      const avgSatisfaction = retained.reduce((s, c) => s + c.satisfaction, 0) / retained.length;
      if (avgSatisfaction > 70) {
        setReputation(r => Math.min(100, r + 1));
      } else if (avgSatisfaction < 40) {
        setReputation(r => Math.max(0, r - 2));
      }
    }
    
    setMonth(m => m + 1);
    setActions(maxActions);
    
    // Add energy recovery action hint
    if (energy < 30) {
      notify('⚠️ Energy critical! Consider resting or hiring help.', 'error');
    }
  };
  
  // ============ RENDER ============
  
  const floaterElements = floaters.map(f => <FloatingNumber key={f.id} {...f} />);
  
  // Title screen
  if (phase === 'title') {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1e3a5f 0%, #0f172a 50%, #1e293b 100%)',
        fontFamily: '"DM Sans", system-ui, sans-serif',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <style>{animationCSS}</style>
        
        {/* Animated background elements */}
        <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
          {/* Floating icons */}
          {['💰', '📈', '🎯', '💼', '📊', '🚀', '💎', '⭐'].map((icon, i) => (
            <div key={i} style={{
              position: 'absolute',
              fontSize: `${20 + Math.random() * 20}px`,
              opacity: 0.15,
              left: `${10 + (i * 12)}%`,
              top: `${10 + ((i * 17) % 80)}%`,
              animation: `float ${3 + i * 0.5}s ease-in-out infinite`,
              animationDelay: `${i * 0.3}s`,
            }}>
              {icon}
            </div>
          ))}
          
          {/* Gradient orbs */}
          <div style={{
            position: 'absolute',
            width: '300px',
            height: '300px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(59, 130, 246, 0.3) 0%, transparent 70%)',
            top: '-100px',
            right: '-100px',
            animation: 'pulse 4s ease-in-out infinite',
          }} />
          <div style={{
            position: 'absolute',
            width: '250px',
            height: '250px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(34, 197, 94, 0.2) 0%, transparent 70%)',
            bottom: '-50px',
            left: '-50px',
            animation: 'pulse 5s ease-in-out infinite',
            animationDelay: '1s',
          }} />
        </div>
        
        {/* Main content */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          zIndex: 1,
          animation: 'fadeInUp 0.8s ease-out',
        }}>
          {/* Logo/Icon */}
          <div style={{
            fontSize: '80px',
            marginBottom: '16px',
            animation: 'float 3s ease-in-out infinite',
            filter: 'drop-shadow(0 10px 30px rgba(34, 197, 94, 0.4))',
          }}>
            💵
          </div>
          
          {/* Title */}
          <h1 style={{
            fontSize: '36px',
            fontWeight: '800',
            color: 'white',
            textAlign: 'center',
            margin: '0 0 8px 0',
            textShadow: '0 4px 20px rgba(0,0,0,0.3)',
            letterSpacing: '-1px',
          }}>
            Marketing
          </h1>
          <h1 style={{
            fontSize: '42px',
            fontWeight: '800',
            background: 'linear-gradient(135deg, #22c55e, #3b82f6, #8b5cf6)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            textAlign: 'center',
            margin: '0 0 16px 0',
            letterSpacing: '-1px',
          }}>
            Millionaire
          </h1>
          
          {/* Tagline */}
          <p style={{
            color: '#94a3b8',
            fontSize: '16px',
            textAlign: 'center',
            margin: '0 0 40px 0',
            maxWidth: '280px',
            lineHeight: 1.5,
            animation: 'fadeInUp 0.8s ease-out 0.2s both',
          }}>
            Build your agency empire from startup to <span style={{ color: '#22c55e', fontWeight: '700' }}>$1,000,000</span>
          </p>
          
          {/* Feature pills */}
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px',
            justifyContent: 'center',
            marginBottom: '40px',
            animation: 'fadeInUp 0.8s ease-out 0.4s both',
          }}>
            {[
              { icon: '🎯', text: 'Close Deals' },
              { icon: '👥', text: 'Build Teams' },
              { icon: '⚔️', text: 'Beat Rivals' },
              { icon: '🏆', text: 'Win Big' },
            ].map((pill, i) => (
              <div key={i} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                background: 'rgba(255,255,255,0.1)',
                backdropFilter: 'blur(10px)',
                padding: '8px 14px',
                borderRadius: '20px',
                border: '1px solid rgba(255,255,255,0.1)',
                color: 'white',
                fontSize: '12px',
                fontWeight: '600',
              }}>
                <span>{pill.icon}</span>
                <span>{pill.text}</span>
              </div>
            ))}
          </div>
          
          {/* Continue Game button (if save exists) */}
          {hasSaveGame && (
            <button
              onClick={() => setShowLoadModal(true)}
              style={{
                padding: '18px 60px',
                fontSize: '18px',
                fontWeight: '800',
                color: 'white',
                background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                border: 'none',
                borderRadius: '16px',
                cursor: 'pointer',
                boxShadow: '0 8px 30px rgba(59, 130, 246, 0.4), inset 0 1px 0 rgba(255,255,255,0.2)',
                animation: 'glow 2s ease-in-out infinite, fadeInUp 0.8s ease-out 0.5s both',
                textTransform: 'uppercase',
                letterSpacing: '2px',
                marginBottom: '12px',
              }}
            >
              ▶ Continue
            </button>
          )}
          
          {/* Import Cloud Save button */}
          <button
            onClick={() => setShowCloudImport(true)}
            style={{
              marginBottom: hasSaveGame ? '12px' : '0',
              padding: '10px 24px',
              fontSize: '12px',
              fontWeight: '600',
              color: '#94a3b8',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '10px',
              cursor: 'pointer',
              animation: 'fadeInUp 0.8s ease-out 0.55s both',
            }}
          >
            ☁️ Import Save Code
          </button>
          
          {/* Start button */}
          <button
            onClick={() => {
              if (hasSaveGame) {
                if (window.confirm('Start a new game? Your saves will remain until you overwrite them.')) {
                  setPhase('setup');
                }
              } else {
                setPhase('setup');
              }
            }}
            style={{
              padding: hasSaveGame ? '14px 40px' : '18px 60px',
              fontSize: hasSaveGame ? '14px' : '18px',
              fontWeight: '800',
              color: hasSaveGame ? '#94a3b8' : 'white',
              background: hasSaveGame ? 'rgba(255,255,255,0.1)' : 'linear-gradient(135deg, #22c55e, #16a34a)',
              border: hasSaveGame ? '1px solid rgba(255,255,255,0.2)' : 'none',
              borderRadius: '16px',
              cursor: 'pointer',
              boxShadow: hasSaveGame ? 'none' : '0 8px 30px rgba(34, 197, 94, 0.4), inset 0 1px 0 rgba(255,255,255,0.2)',
              animation: hasSaveGame ? 'fadeInUp 0.8s ease-out 0.6s both' : 'glow 2s ease-in-out infinite, fadeInUp 0.8s ease-out 0.6s both',
              textTransform: 'uppercase',
              letterSpacing: '2px',
            }}
          >
            {hasSaveGame ? 'New Game' : 'Start Game'}
          </button>
          
          {/* How to Play button */}
          <button
            onClick={() => setShowTutorial(true)}
            style={{
              marginTop: '16px',
              padding: '12px 30px',
              fontSize: '14px',
              fontWeight: '600',
              color: '#94a3b8',
              background: 'rgba(255,255,255,0.1)',
              border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: '12px',
              cursor: 'pointer',
              animation: 'fadeInUp 0.8s ease-out 0.7s both',
            }}
          >
            📖 How to Play
          </button>
          
          {/* Vendero Apps Branding */}
          <div style={{
            marginTop: '30px',
            textAlign: 'center',
            animation: 'fadeInUp 0.8s ease-out 0.8s both',
          }}>
            {/* Logo and Company Name */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '16px',
            }}>
              {/* Vendero Apps Logo */}
              <img 
                src="/vendero-logo.png" 
                alt="Vendero Apps"
                style={{
                  width: '70px',
                  height: '70px',
                  borderRadius: '12px',
                  objectFit: 'cover',
                  boxShadow: '0 4px 15px rgba(212, 175, 55, 0.3)',
                }}
                onError={(e) => {
                  // Fallback to styled VA if image fails to load
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              {/* Fallback VA Logo (hidden by default) */}
              <div style={{
                width: '50px',
                height: '50px',
                background: 'linear-gradient(135deg, #d4af37, #b8860b)',
                borderRadius: '10px',
                display: 'none',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 15px rgba(212, 175, 55, 0.3)',
              }}>
                <span style={{
                  fontSize: '24px',
                  fontWeight: '800',
                  color: '#1a1a1a',
                  fontFamily: 'Georgia, serif',
                  letterSpacing: '-2px',
                }}>VA</span>
              </div>
              <div style={{
                color: '#d4af37',
                fontSize: '12px',
                fontWeight: '600',
                letterSpacing: '3px',
                textTransform: 'uppercase',
              }}>
                Vendero Apps
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', marginBottom: '12px', color: '#64748b', fontSize: '11px' }}>
              <span>v3.0</span>
              <span>•</span>
              <span>📱 Mobile Optimized</span>
            </div>
            
            {/* Support Links */}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <a 
                href="#" 
                target="_blank" 
                rel="noopener noreferrer"
                style={{
                  color: '#f59e0b',
                  textDecoration: 'none',
                  padding: '6px 12px',
                  background: 'rgba(245, 158, 11, 0.1)',
                  borderRadius: '6px',
                  fontSize: '11px',
                  fontWeight: '600',
                }}
              >
                ⭐ Rate Game
              </a>
              <a 
                href="#" 
                target="_blank" 
                rel="noopener noreferrer"
                style={{
                  color: '#ec4899',
                  textDecoration: 'none',
                  padding: '6px 12px',
                  background: 'rgba(236, 72, 153, 0.1)',
                  borderRadius: '6px',
                  fontSize: '11px',
                  fontWeight: '600',
                }}
              >
                ☕ Support Dev
              </a>
            </div>
          </div>
        </div>
        
        {/* Tutorial Modal */}
        {showTutorial && (
          <div style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
            padding: '20px',
            animation: 'fadeIn 0.3s ease',
          }}>
            <div style={{
              background: 'white',
              borderRadius: '20px',
              padding: '24px',
              maxWidth: '360px',
              maxHeight: '80vh',
              overflowY: 'auto',
              animation: 'popIn 0.3s ease',
            }}>
              <h2 style={{ fontSize: '20px', fontWeight: '800', color: '#1e293b', marginBottom: '16px', textAlign: 'center' }}>
                📖 How to Play
              </h2>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {/* Step 1 */}
                <div style={{ display: 'flex', gap: '12px' }}>
                  <div style={{ width: '36px', height: '36px', background: '#dbeafe', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <span style={{ fontSize: '18px' }}>1️⃣</span>
                  </div>
                  <div>
                    <div style={{ fontWeight: '700', fontSize: '13px', color: '#1e293b', marginBottom: '2px' }}>Generate Leads</div>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>Cold call, network, or run ads to find potential clients.</div>
                  </div>
                </div>
                
                {/* Step 2 */}
                <div style={{ display: 'flex', gap: '12px' }}>
                  <div style={{ width: '36px', height: '36px', background: '#dcfce7', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <span style={{ fontSize: '18px' }}>2️⃣</span>
                  </div>
                  <div>
                    <div style={{ fontWeight: '700', fontSize: '13px', color: '#1e293b', marginBottom: '2px' }}>Pitch & Close</div>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>Warm up leads, then pitch them. Match your approach to their personality!</div>
                  </div>
                </div>
                
                {/* Step 3 */}
                <div style={{ display: 'flex', gap: '12px' }}>
                  <div style={{ width: '36px', height: '36px', background: '#fef3c7', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <span style={{ fontSize: '18px' }}>3️⃣</span>
                  </div>
                  <div>
                    <div style={{ fontWeight: '700', fontSize: '13px', color: '#1e293b', marginBottom: '2px' }}>Manage Clients</div>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>Run campaigns, handle their demands, and keep them satisfied or they'll leave!</div>
                  </div>
                </div>
                
                {/* Step 4 */}
                <div style={{ display: 'flex', gap: '12px' }}>
                  <div style={{ width: '36px', height: '36px', background: '#e0e7ff', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <span style={{ fontSize: '18px' }}>4️⃣</span>
                  </div>
                  <div>
                    <div style={{ fontWeight: '700', fontSize: '13px', color: '#1e293b', marginBottom: '2px' }}>Build Your Team</div>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>Hire employees with special bonuses. Train them and give bonuses to prevent quitting!</div>
                  </div>
                </div>
                
                {/* Step 5 */}
                <div style={{ display: 'flex', gap: '12px' }}>
                  <div style={{ width: '36px', height: '36px', background: '#fce7f3', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <span style={{ fontSize: '18px' }}>5️⃣</span>
                  </div>
                  <div>
                    <div style={{ fontWeight: '700', fontSize: '13px', color: '#1e293b', marginBottom: '2px' }}>Beat Rivals & Grow</div>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>Compete against 3 rival agencies. Watch the leaderboard and reach $1M to win!</div>
                  </div>
                </div>
              </div>
              
              {/* Tips */}
              <div style={{ marginTop: '16px', background: '#f0f9ff', borderRadius: '10px', padding: '12px' }}>
                <div style={{ fontWeight: '700', fontSize: '11px', color: '#0369a1', marginBottom: '6px' }}>💡 PRO TIPS</div>
                <ul style={{ fontSize: '10px', color: '#64748b', margin: 0, paddingLeft: '16px' }}>
                  <li>Keep client satisfaction above 30% or they'll leave</li>
                  <li>Employee morale matters - give bonuses!</li>
                  <li>Match pitch style to client personality</li>
                  <li>Watch your energy - rest when low</li>
                </ul>
              </div>
              
              <button
                onClick={() => setShowTutorial(false)}
                style={{
                  width: '100%',
                  marginTop: '16px',
                  padding: '12px',
                  fontSize: '14px',
                  fontWeight: '700',
                  color: 'white',
                  background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer',
                }}
              >
                Got It!
              </button>
            </div>
          </div>
        )}
        
        {/* Load Game Modal */}
        {showLoadModal && (
          <div style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
            padding: '20px',
            animation: 'fadeIn 0.3s ease',
          }}>
            <div style={{
              background: 'white',
              borderRadius: '20px',
              padding: '24px',
              maxWidth: '360px',
              width: '100%',
              animation: 'popIn 0.3s ease',
            }}>
              <h2 style={{ fontSize: '20px', fontWeight: '800', color: '#1e293b', marginBottom: '16px', textAlign: 'center' }}>
                💾 Load Game
              </h2>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '16px' }}>
                {getSaveSlots().map(slot => (
                  <div key={slot.slot} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px',
                    background: slot.exists ? '#f0f9ff' : '#f8fafc',
                    border: `2px solid ${slot.exists ? '#3b82f6' : '#e2e8f0'}`,
                    borderRadius: '12px',
                    opacity: slot.exists ? 1 : 0.5,
                  }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      background: slot.exists ? '#dbeafe' : '#e2e8f0',
                      borderRadius: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '18px',
                      fontWeight: '700',
                      color: slot.exists ? '#3b82f6' : '#94a3b8',
                    }}>
                      {slot.slot}
                    </div>
                    <div style={{ flex: 1 }}>
                      {slot.exists ? (
                        <>
                          <div style={{ fontWeight: '700', fontSize: '13px', color: '#1e293b' }}>
                            Month {slot.month} • ${slot.cash?.toLocaleString()}
                          </div>
                          <div style={{ fontSize: '10px', color: '#64748b' }}>
                            {new Date(slot.savedAt).toLocaleDateString()}
                          </div>
                        </>
                      ) : (
                        <div style={{ fontSize: '13px', color: '#94a3b8' }}>Empty Slot</div>
                      )}
                    </div>
                    {slot.exists && (
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button
                          onClick={() => {
                            loadGameFromSlot(slot.slot);
                            setShowLoadModal(false);
                          }}
                          style={{
                            padding: '8px 12px',
                            background: '#3b82f6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '11px',
                            fontWeight: '600',
                            cursor: 'pointer',
                          }}
                        >
                          Load
                        </button>
                        <button
                          onClick={() => {
                            if (window.confirm('Delete this save?')) {
                              deleteSaveSlot(slot.slot);
                            }
                          }}
                          style={{
                            padding: '8px',
                            background: '#fee2e2',
                            color: '#ef4444',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '11px',
                            cursor: 'pointer',
                          }}
                        >
                          🗑
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              
              <button
                onClick={() => setShowLoadModal(false)}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: '#f1f5f9',
                  border: 'none',
                  borderRadius: '10px',
                  color: '#64748b',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
        
        {/* Cloud Import Modal */}
        {showCloudImport && (
          <div style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
            padding: '20px',
            animation: 'fadeIn 0.3s ease',
          }}>
            <div style={{
              background: 'white',
              borderRadius: '20px',
              padding: '24px',
              maxWidth: '360px',
              width: '100%',
              animation: 'popIn 0.3s ease',
            }}>
              <h2 style={{ fontSize: '20px', fontWeight: '800', color: '#1e293b', marginBottom: '8px', textAlign: 'center' }}>
                ☁️ Import Save
              </h2>
              <p style={{ fontSize: '12px', color: '#64748b', textAlign: 'center', marginBottom: '16px' }}>
                Paste your save code to restore progress from another device
              </p>
              
              <textarea
                value={cloudCode}
                onChange={(e) => setCloudCode(e.target.value)}
                placeholder="Paste your save code here..."
                style={{
                  width: '100%',
                  height: '100px',
                  padding: '12px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '10px',
                  fontSize: '12px',
                  fontFamily: 'monospace',
                  resize: 'none',
                  marginBottom: '12px',
                }}
              />
              
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={() => {
                    setShowCloudImport(false);
                    setCloudCode('');
                  }}
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: '#f1f5f9',
                    border: 'none',
                    borderRadius: '10px',
                    color: '#64748b',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (importSaveCode(cloudCode)) {
                      setShowCloudImport(false);
                      setCloudCode('');
                    }
                  }}
                  disabled={!cloudCode.trim()}
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: cloudCode.trim() ? '#3b82f6' : '#e2e8f0',
                    border: 'none',
                    borderRadius: '10px',
                    color: cloudCode.trim() ? 'white' : '#94a3b8',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: cloudCode.trim() ? 'pointer' : 'not-allowed',
                  }}
                >
                  Import
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
  
  // Setup phase
  if (phase === 'setup') {
    return (
      <div style={styles.overlay}>
        <style>{animationCSS}</style>
        <SpecializationPicker onSelect={startGame} />
      </div>
    );
  }
  
  // Game Over screen
  if (phase === 'gameover' && gameOverReason) {
    return (
      <div style={styles.overlay}>
        <style>{animationCSS}</style>
        <div style={{ ...modal.container, background: '#fef2f2', border: '3px solid #ef4444' }}>
          <div style={{ fontSize: '64px', marginBottom: '12px' }}>💀</div>
          <h1 style={{ fontSize: '24px', fontWeight: '800', color: '#b91c1c', marginBottom: '8px' }}>{gameOverReason.title}</h1>
          <p style={{ color: '#7f1d1d', marginBottom: '16px', fontSize: '14px' }}>{gameOverReason.desc}</p>
          
          <div style={{ background: 'white', borderRadius: '10px', padding: '12px', marginBottom: '16px', textAlign: 'left' }}>
            <div style={{ fontWeight: '700', fontSize: '12px', color: '#64748b', marginBottom: '6px' }}>📊 FINAL STATS</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '12px' }}>
              <div>Survived: <strong>{month} months</strong></div>
              <div>Peak Cash: <strong>${Math.max(cash, 5000).toLocaleString()}</strong></div>
              <div>Clients Served: <strong>{clients.length}</strong></div>
              <div>Final Rep: <strong>{reputation}</strong></div>
            </div>
          </div>
          
          <div style={{ background: '#fef3c7', borderRadius: '8px', padding: '10px', marginBottom: '16px', fontSize: '12px', textAlign: 'left' }}>
            <strong>💡 Tip:</strong> {gameOverReason.tip}
          </div>
          
          <button onClick={() => window.location.reload()} style={modal.primaryBtn}>
            Try Again
          </button>
          
          {/* Vendero Apps Branding */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            marginTop: '16px',
            opacity: 0.5,
          }}>
            <div style={{
              width: '18px',
              height: '18px',
              background: 'linear-gradient(135deg, #d4af37, #b8860b)',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <span style={{ fontSize: '9px', fontWeight: '800', color: '#1a1a1a', fontFamily: 'Georgia, serif' }}>VA</span>
            </div>
            <span style={{ fontSize: '9px', color: '#64748b', letterSpacing: '1px' }}>VENDERO APPS</span>
          </div>
        </div>
      </div>
    );
  }
  
  // Event Modal
  if (pendingEvent) {
    return (
      <div style={styles.overlay}>
        <style>{animationCSS}</style>
        <div style={{ 
          ...modal.container, 
          background: pendingEvent.type === 'negative' ? '#fef2f2' : '#f0fdf4',
          border: `3px solid ${pendingEvent.type === 'negative' ? '#fca5a5' : '#86efac'}`
        }}>
          <div style={{ fontSize: '48px', marginBottom: '8px', animation: 'popIn 0.4s ease' }}>
            {pendingEvent.type === 'negative' ? '⚠️' : '🎉'}
          </div>
          <h2 style={{ 
            ...modal.title, 
            color: pendingEvent.type === 'negative' ? '#b91c1c' : '#166534' 
          }}>
            {pendingEvent.name}
          </h2>
          <p style={{ 
            color: pendingEvent.type === 'negative' ? '#7f1d1d' : '#14532d',
            marginBottom: '16px',
            fontSize: '13px'
          }}>
            {pendingEvent.desc}
          </p>
          
          <div style={{ background: 'white', borderRadius: '10px', padding: '12px', marginBottom: '16px' }}>
            <div style={{ fontWeight: '700', fontSize: '11px', color: '#64748b', marginBottom: '6px' }}>EFFECTS</div>
            <div style={{ fontSize: '12px' }}>
              {pendingEvent.effect.cash && (
                <div style={{ color: pendingEvent.effect.cash > 0 ? '#22c55e' : '#ef4444' }}>
                  💰 {pendingEvent.effect.cash > 0 ? '+' : ''}{pendingEvent.effect.cash.toLocaleString()} cash
                </div>
              )}
              {pendingEvent.effect.reputation && (
                <div style={{ color: pendingEvent.effect.reputation > 0 ? '#22c55e' : '#ef4444' }}>
                  ⭐ {pendingEvent.effect.reputation > 0 ? '+' : ''}{pendingEvent.effect.reputation} reputation
                </div>
              )}
              {pendingEvent.effect.clientSatisfaction && (
                <div style={{ color: '#f59e0b' }}>
                  😊 {pendingEvent.effect.clientSatisfaction > 0 ? '+' : ''}{pendingEvent.effect.clientSatisfaction}% client satisfaction
                </div>
              )}
              {pendingEvent.effect.loseEmployee && <div style={{ color: '#ef4444' }}>👋 Lose your best employee</div>}
              {pendingEvent.effect.loseRandomClient && <div style={{ color: '#ef4444' }}>💔 Lose a client</div>}
              {pendingEvent.effect.loseLeads && <div style={{ color: '#ef4444' }}>📉 Lose {pendingEvent.effect.loseLeads} leads</div>}
              {pendingEvent.effect.hotLeads && <div style={{ color: '#22c55e' }}>🔥 +{pendingEvent.effect.hotLeads} hot leads</div>}
              {pendingEvent.effect.warmLeads && <div style={{ color: '#22c55e' }}>☀️ +{pendingEvent.effect.warmLeads} warm leads</div>}
              {pendingEvent.effect.expandTopClient && <div style={{ color: '#22c55e' }}>📈 Top client budget +50%</div>}
              {pendingEvent.effect.reduceOverhead && <div style={{ color: '#22c55e' }}>💸 -${pendingEvent.effect.reduceOverhead}/mo overhead</div>}
            </div>
          </div>
          
          <button onClick={applyEvent} style={modal.primaryBtn}>
            Continue
          </button>
        </div>
      </div>
    );
  }
  
  // Client Demand Modal
  if (pendingDemands.length > 0) {
    const demand = pendingDemands[0];
    const client = clients.find(c => c.id === demand.clientId);
    return (
      <div style={styles.overlay}>
        <style>{animationCSS}</style>
        <div style={modal.container}>
          <div style={{ fontSize: '40px', marginBottom: '8px', textAlign: 'center' }}>{demand.icon}</div>
          <h2 style={modal.title}>Client Request</h2>
          <p style={modal.subtitle}>{demand.clientName} from {demand.clientBusiness}</p>
          
          <div style={{ background: '#fef3c7', borderRadius: '10px', padding: '14px', marginBottom: '14px', textAlign: 'center' }}>
            <div style={{ fontSize: '16px', fontWeight: '700', color: '#92400e', marginBottom: '4px' }}>{demand.name}</div>
            <div style={{ fontSize: '12px', color: '#78350f' }}>
              {demand.actionCost && <span>Costs {demand.actionCost}⚡ </span>}
              {demand.cashCost && <span>Costs ${demand.cashCost} </span>}
              {demand.budgetCut && <span>Reduces their budget 15% </span>}
            </div>
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
            <div style={{ background: '#f0fdf4', borderRadius: '8px', padding: '10px', textAlign: 'center' }}>
              <div style={{ fontSize: '10px', color: '#166534' }}>If you help:</div>
              <div style={{ fontWeight: '700', color: '#22c55e' }}>+{demand.satisfactionGain} satisfaction</div>
              {demand.successChance && <div style={{ fontSize: '10px', color: '#64748b' }}>{Math.round(demand.successChance * 100)}% success rate</div>}
            </div>
            <div style={{ background: '#fef2f2', borderRadius: '8px', padding: '10px', textAlign: 'center' }}>
              <div style={{ fontSize: '10px', color: '#b91c1c' }}>If you ignore:</div>
              <div style={{ fontWeight: '700', color: '#ef4444' }}>{demand.ignoreEffect} satisfaction</div>
            </div>
          </div>
          
          {client && client.satisfaction < 50 && (
            <div style={{ background: '#fef2f2', borderRadius: '8px', padding: '8px', marginBottom: '12px', fontSize: '11px', color: '#b91c1c', textAlign: 'center' }}>
              ⚠️ {client.firstName}'s satisfaction is only {client.satisfaction}%! Be careful.
            </div>
          )}
          
          <div style={{ display: 'flex', gap: '10px' }}>
            <button 
              onClick={() => handleClientDemand(demand, true)}
              disabled={(demand.actionCost && actions < demand.actionCost) || (demand.cashCost && cash < demand.cashCost)}
              style={{ ...modal.primaryBtn, flex: 1 }}
            >
              ✓ Handle It
            </button>
            <button 
              onClick={() => handleClientDemand(demand, false)}
              style={{ ...modal.secondaryBtn, flex: 1, background: '#fef2f2', color: '#b91c1c' }}
            >
              ✗ Ignore
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  // Campaign Event Modal
  if (pendingCampaignEvent) {
    const event = pendingCampaignEvent;
    const client = clients.find(c => c.id === event.clientId);
    return (
      <div style={styles.overlay}>
        <style>{animationCSS}</style>
        <div style={{ ...modal.container, maxWidth: '440px' }}>
          <div style={{ fontSize: '40px', marginBottom: '8px', textAlign: 'center' }}>{event.icon}</div>
          <h2 style={modal.title}>{event.name}</h2>
          <p style={modal.subtitle}>{event.clientName}'s campaign needs attention!</p>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '14px' }}>
            {event.options.map((opt, i) => (
              <button
                key={i}
                onClick={() => handleCampaignEvent(opt)}
                disabled={(opt.actionCost && actions < opt.actionCost) || (opt.cost && cash < opt.cost)}
                style={{
                  padding: '12px',
                  background: 'white',
                  border: '2px solid #e2e8f0',
                  borderRadius: '10px',
                  textAlign: 'left',
                  cursor: 'pointer',
                }}
              >
                <div style={{ fontWeight: '700', fontSize: '13px', marginBottom: '4px' }}>{opt.text}</div>
                <div style={{ display: 'flex', gap: '12px', fontSize: '11px', color: '#64748b' }}>
                  <span style={{ color: '#22c55e' }}>Success: +{opt.successEffect}</span>
                  <span style={{ color: '#ef4444' }}>Fail: {opt.failEffect}</span>
                  <span>({Math.round(opt.successChance * 100)}% odds)</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }
  
  // Employee Training/Bonus Modal
  if (selectedEmployee && employeeModal) {
    const emp = selectedEmployee;
    return (
      <div style={styles.overlay}>
        <style>{animationCSS}</style>
        <div style={modal.container}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
            <span style={{ fontSize: '36px' }}>{emp.icon}</span>
            <div>
              <h2 style={{ ...modal.title, textAlign: 'left', marginBottom: '2px' }}>{emp.name}</h2>
              <p style={{ fontSize: '12px', color: '#64748b', margin: 0 }}>{emp.role} • Morale: {emp.morale}%</p>
            </div>
          </div>
          
          {employeeModal === 'train' && (
            <>
              <div style={{ background: '#f0f9ff', borderRadius: '8px', padding: '10px', marginBottom: '12px', fontSize: '12px' }}>
                <strong>Current Training Level:</strong> {emp.trainingLevel}% (effectiveness: {100 + emp.trainingLevel}%)
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '14px' }}>
                {TRAINING_TYPES.map(training => (
                  <button
                    key={training.id}
                    onClick={() => trainEmployee(emp, training)}
                    disabled={cash < training.cost || emp.trainingLevel >= 100}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      padding: '12px', background: 'white', border: '2px solid #e2e8f0',
                      borderRadius: '10px', textAlign: 'left', cursor: 'pointer',
                    }}
                  >
                    <span style={{ fontSize: '24px' }}>{training.icon}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '700', fontSize: '13px' }}>{training.name}</div>
                      <div style={{ fontSize: '11px', color: '#64748b' }}>{training.desc}</div>
                    </div>
                    <div style={{ fontWeight: '700', color: cash >= training.cost ? '#22c55e' : '#ef4444' }}>
                      ${training.cost}
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}
          
          {employeeModal === 'bonus' && (
            <>
              <div style={{ background: emp.morale < 50 ? '#fef2f2' : '#f0fdf4', borderRadius: '8px', padding: '10px', marginBottom: '12px', fontSize: '12px' }}>
                <strong>Morale:</strong> {emp.morale}% 
                {emp.morale < 30 && <span style={{ color: '#ef4444' }}> ⚠️ Quitting risk!</span>}
                {emp.loyaltyMonths > 0 && <span style={{ color: '#22c55e' }}> 🔒 {emp.loyaltyMonths}mo loyalty</span>}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '14px' }}>
                {BONUS_TYPES.map(bonus => (
                  <button
                    key={bonus.id}
                    onClick={() => giveBonus(emp, bonus)}
                    disabled={cash < bonus.cost}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      padding: '12px', background: 'white', border: '2px solid #e2e8f0',
                      borderRadius: '10px', textAlign: 'left', cursor: 'pointer',
                    }}
                  >
                    <span style={{ fontSize: '24px' }}>{bonus.icon}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '700', fontSize: '13px' }}>{bonus.name}</div>
                      <div style={{ fontSize: '11px', color: '#64748b' }}>{bonus.desc}</div>
                    </div>
                    <div style={{ fontWeight: '700', color: cash >= bonus.cost ? '#22c55e' : '#ef4444' }}>
                      ${bonus.cost}
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}
          
          <button 
            onClick={() => { setSelectedEmployee(null); setEmployeeModal(null); }}
            style={modal.secondaryBtn}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }
  
  // Hiring Modal - Choose from 3 candidates
  if (hiringCandidates) {
    return (
      <div style={styles.overlay}>
        <style>{animationCSS}</style>
        <div style={{ ...modal.container, maxWidth: '500px' }}>
          <h2 style={modal.title}>👥 Hire a Team Member</h2>
          <p style={modal.subtitle}>Choose from 3 candidates. Each brings unique bonuses to your agency.</p>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px' }}>
            {hiringCandidates.map((emp, idx) => (
              <div 
                key={emp.id}
                style={{
                  background: 'white',
                  border: '2px solid #e2e8f0',
                  borderRadius: '12px',
                  padding: '14px',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#3b82f6';
                  e.currentTarget.style.background = '#f8fafc';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e2e8f0';
                  e.currentTarget.style.background = 'white';
                }}
                onClick={() => confirmHire(emp)}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
                  <span style={{ fontSize: '28px' }}>{emp.icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '700', fontSize: '14px', color: '#1e293b' }}>{emp.name}</div>
                    <div style={{ fontSize: '12px', color: '#64748b' }}>
                      {emp.role} • {emp.personalityData.icon} {emp.personality}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontWeight: '800', fontSize: '16px', color: cash >= emp.cost ? '#22c55e' : '#ef4444' }}>
                      ${emp.cost.toLocaleString()}
                    </div>
                    <div style={{ fontSize: '10px', color: '#64748b' }}>
                      ${emp.salary}/mo salary
                    </div>
                  </div>
                </div>
                
                {/* Attribute Bonuses */}
                <div style={{ display: 'flex', gap: '6px', marginBottom: '8px', flexWrap: 'wrap' }}>
                  {emp.effectiveBonusActions > 0 && (
                    <div style={{ background: '#dcfce7', border: '1px solid #86efac', borderRadius: '6px', padding: '4px 8px', fontSize: '11px', color: '#166534', fontWeight: '600' }}>
                      ⚡ +{emp.effectiveBonusActions} actions/mo
                    </div>
                  )}
                  {emp.effectiveAutoClose > 0 && (
                    <div style={{ background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: '6px', padding: '4px 8px', fontSize: '11px', color: '#92400e', fontWeight: '600' }}>
                      🎯 {emp.effectiveAutoClose}% auto-close
                    </div>
                  )}
                  {emp.effectiveClientBonus > 0 && (
                    <div style={{ background: '#fce7f3', border: '1px solid #f9a8d4', borderRadius: '6px', padding: '4px 8px', fontSize: '11px', color: '#9d174d', fontWeight: '600' }}>
                      ❤️ +{emp.effectiveClientBonus}% satisfaction
                    </div>
                  )}
                  {emp.effectiveCampaignBonus > 0 && (
                    <div style={{ background: '#e0e7ff', border: '1px solid #a5b4fc', borderRadius: '6px', padding: '4px 8px', fontSize: '11px', color: '#3730a3', fontWeight: '600' }}>
                      📈 +{emp.effectiveCampaignBonus}% campaigns
                    </div>
                  )}
                  {emp.effectiveLeadBonus > 0 && (
                    <div style={{ background: '#dbeafe', border: '1px solid #93c5fd', borderRadius: '6px', padding: '4px 8px', fontSize: '11px', color: '#1e40af', fontWeight: '600' }}>
                      📣 +{emp.effectiveLeadBonus} leads/action
                    </div>
                  )}
                </div>
                
                {/* Personality info */}
                <div style={{ display: 'flex', gap: '12px', fontSize: '11px' }}>
                  <div style={{ 
                    color: emp.productivity >= 1.2 ? '#22c55e' : emp.productivity >= 1.0 ? '#64748b' : '#f59e0b' 
                  }}>
                    ⚡ {Math.round(emp.productivity * 100)}% effectiveness
                  </div>
                  <div style={{ color: '#64748b' }}>
                    📉 {Math.round(emp.personalityData.turnoverRisk * 100)}% turnover risk
                  </div>
                </div>
                
                <div style={{ marginTop: '8px', fontSize: '10px', color: '#64748b', fontStyle: 'italic' }}>
                  {emp.desc}
                </div>
              </div>
            ))}
          </div>
          
          <button 
            onClick={() => setHiringCandidates(null)} 
            style={{ ...modal.secondaryBtn, width: '100%' }}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }
  
  // Pitch screen
  if (screen === 'pitch' && selectedLead) {
    return (
      <div style={styles.overlay}>
        <style>{animationCSS}</style>
        <PitchGame
          lead={selectedLead}
          onComplete={completePitch}
          specializations={specializations}
          employees={employees}
          reputation={reputation}
        />
      </div>
    );
  }
  
  // Campaign planner
  if (screen === 'campaign' && selectedClient) {
    return (
      <div style={styles.overlay}>
        <style>{animationCSS}</style>
        <CampaignPlanner
          client={selectedClient}
          onSubmit={launchCampaign}
          onCancel={() => { setSelectedClient(null); setScreen('main'); }}
          specializations={specializations}
          employees={employees}
        />
      </div>
    );
  }
  
  // Win check
  if (cash >= 1000000) {
    return (
      <div style={styles.overlay}>
        <style>{animationCSS}</style>
        <div style={{ ...modal.container, background: 'linear-gradient(135deg, #fef3c7, #fde68a)' }}>
          <div style={{ fontSize: '64px', animation: 'bounce 0.6s ease infinite' }}>👑</div>
          <h1 style={{ fontSize: '26px', fontWeight: '800', color: '#1e293b' }}>MILLIONAIRE!</h1>
          <p style={{ color: '#64748b' }}>Built a ${(cash/1000000).toFixed(2)}M agency in {month} months</p>
          <p style={{ color: '#64748b', fontSize: '13px' }}>Specialties: {specializations.map(s => SPECIALIZATIONS.find(x => x.id === s)?.name).join(', ')}</p>
        </div>
      </div>
    );
  }
  
  // Game over check
  if (cash < 0 && clients.length === 0) {
    return (
      <div style={styles.overlay}>
        <style>{animationCSS}</style>
        <div style={modal.container}>
          <div style={{ fontSize: '56px' }}>💀</div>
          <h2 style={modal.title}>Bankrupt!</h2>
          <p style={modal.subtitle}>Your agency couldn't survive. Try again with different strategy.</p>
          <button onClick={() => window.location.reload()} style={modal.primaryBtn}>Try Again</button>
        </div>
      </div>
    );
  }
  
  // Networking mini-game
  if (networkingGame) {
    return (
      <div style={styles.overlay}>
        <style>{animationCSS}</style>
        <NetworkingGame 
          onComplete={completeNetworking} 
          employees={employees}
        />
      </div>
    );
  }
  
  // Ads mini-game
  if (adsGame) {
    return (
      <div style={styles.overlay}>
        <style>{animationCSS}</style>
        <AdsPuzzleGame 
          onComplete={completeAds}
          employees={employees}
        />
      </div>
    );
  }
  
  const TABS = ['market', 'leads', 'clients', 'team', 'finance', 'rivals', 'goals'];
  
  return (
    <div style={styles.container}>
      <style>{animationCSS}</style>
      {floaterElements}
      
      {/* Achievement Popup */}
      {achievementPopup && (
        <div style={{
          position: 'fixed', top: '50px', left: '50%', transform: 'translateX(-50%)',
          background: 'linear-gradient(135deg, #fef3c7, #fde68a)', borderRadius: '12px',
          padding: '16px 24px', boxShadow: '0 8px 32px rgba(0,0,0,0.2)', zIndex: 2000,
          animation: 'popIn 0.4s ease', textAlign: 'center',
        }}>
          <div style={{ fontSize: '32px', marginBottom: '4px' }}>{achievementPopup.icon}</div>
          <div style={{ fontWeight: '800', fontSize: '14px', color: '#92400e' }}>Achievement Unlocked!</div>
          <div style={{ fontWeight: '600', fontSize: '13px', color: '#78350f' }}>{achievementPopup.name}</div>
        </div>
      )}
      
      {notification && (
        <div style={{
          ...styles.notif,
          background: notification.type === 'error' ? '#fef2f2' : notification.type === 'success' ? '#f0fdf4' : '#f0f9ff',
          borderColor: notification.type === 'error' ? '#fca5a5' : notification.type === 'success' ? '#86efac' : '#93c5fd',
        }}>
          {notification.msg}
        </div>
      )}
      
      {/* Header */}
      <div style={styles.header}>
        <div>
          <div style={styles.cashDisplay}>${cash.toLocaleString()}</div>
          <div style={styles.subtext}>
            {netCashFlow >= 0 ? '+' : ''}{netCashFlow.toLocaleString()}/mo
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={styles.monthDisplay}>Month {month}</div>
          <div style={styles.subtext}>⚡ {actions}/{maxActions} actions</div>
        </div>
      </div>
      
      {/* Milestone Progress */}
      <div style={styles.milestoneBar}>
        <div style={styles.milestoneInfo}>
          <span style={{ fontSize: '18px' }}>{milestone.current.icon}</span>
          <span style={{ fontWeight: '700', color: milestone.current.color, fontSize: '12px' }}>{milestone.current.name}</span>
          {milestone.next && (
            <span style={{ color: '#94a3b8', fontSize: '11px' }}>→ {milestone.next.icon} ${(milestone.next.threshold/1000).toFixed(0)}K</span>
          )}
        </div>
        <ProgressBar 
          value={milestone.next ? cash - milestone.current.threshold : 100} 
          max={milestone.next ? milestone.next.threshold - milestone.current.threshold : 100} 
          color={milestone.current.color} 
          h={6} 
          glow 
        />
      </div>
      
      {/* Market Condition Banner */}
      <div style={{ ...styles.marketBanner, background: `${marketCondition.id === 'recession' ? '#fef2f2' : marketCondition.id === 'boom' ? '#f0fdf4' : '#f8fafc'}` }}>
        <span style={{ fontSize: '18px' }}>{marketCondition.icon}</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: '700', fontSize: '12px' }}>{marketCondition.name}</div>
          <div style={{ fontSize: '10px', color: '#64748b' }}>{marketCondition.desc}</div>
        </div>
        <div style={{ fontSize: '10px', color: '#64748b' }}>{marketTurnsLeft}mo left</div>
      </div>
      
      {/* Quick Stats */}
      <div style={styles.quickStats}>
        <div style={styles.quickStat}>
          <span style={{ fontSize: '14px' }}>📋</span>
          <span style={{ fontWeight: '700' }}>{leads.length}</span>
          <span style={{ fontSize: '10px', color: '#64748b' }}>Leads</span>
        </div>
        <div style={styles.quickStat}>
          <span style={{ fontSize: '14px' }}>👥</span>
          <span style={{ fontWeight: '700' }}>{clients.length}</span>
          <span style={{ fontSize: '10px', color: '#64748b' }}>Clients</span>
        </div>
        <div style={styles.quickStat}>
          <span style={{ fontSize: '14px' }}>⭐</span>
          <span style={{ fontWeight: '700', color: reputation < 20 ? '#ef4444' : reputation < 40 ? '#f59e0b' : '#22c55e' }}>{reputation}</span>
          <span style={{ fontSize: '10px', color: '#64748b' }}>Rep</span>
        </div>
        <div style={styles.quickStat}>
          <span style={{ fontSize: '14px' }}>⚡</span>
          <span style={{ fontWeight: '700', color: energy < 30 ? '#ef4444' : energy < 60 ? '#f59e0b' : '#22c55e' }}>{energy}%</span>
          <span style={{ fontSize: '10px', color: '#64748b' }}>Energy</span>
        </div>
      </div>
      
      {/* Warning Banners */}
      {cash < 0 && (
        <div style={{ background: '#fef2f2', border: '2px solid #fca5a5', borderRadius: '10px', padding: '10px', marginBottom: '8px', fontSize: '12px', color: '#b91c1c' }}>
          ⚠️ <strong>NEGATIVE CASH!</strong> You're ${ Math.abs(cash).toLocaleString()} in the red. Take a loan or cut costs!
        </div>
      )}
      {runway < 3 && runway > 0 && netCashFlow < 0 && (
        <div style={{ background: '#fef3c7', border: '2px solid #fcd34d', borderRadius: '10px', padding: '10px', marginBottom: '8px', fontSize: '12px', color: '#92400e' }}>
          ⚠️ <strong>LOW RUNWAY!</strong> At current burn rate, you have ~{runway} months before bankruptcy.
        </div>
      )}
      {energy < 30 && (
        <div style={{ background: '#fef3c7', border: '2px solid #fcd34d', borderRadius: '10px', padding: '10px', marginBottom: '8px', fontSize: '12px', color: '#92400e' }}>
          😓 <strong>BURNOUT WARNING!</strong> Your energy is critical. Rest or hire help!
        </div>
      )}
      {consecutiveLosses >= 3 && (
        <div style={{ background: '#fef2f2', border: '2px solid #fca5a5', borderRadius: '10px', padding: '10px', marginBottom: '8px', fontSize: '12px', color: '#b91c1c' }}>
          📉 <strong>{consecutiveLosses} MONTHS OF LOSSES!</strong> Turn things around or face closure at 6 months.
        </div>
      )}
      
      {/* Tabs */}
      <div style={styles.tabs}>
        {TABS.map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{ ...styles.tab, ...(tab === t ? styles.tabActive : {}) }}
          >
            {t === 'market' && '📊'}
            {t === 'leads' && '📋'}
            {t === 'clients' && '👥'}
            {t === 'team' && '🏢'}
            {t === 'finance' && '💰'}
            {t === 'rivals' && '⚔️'}
            {t === 'goals' && '🏆'}
            <span style={{ marginLeft: '4px' }}>{t.charAt(0).toUpperCase() + t.slice(1)}</span>
          </button>
        ))}
      </div>
      
      {/* Tab Content */}
      <div style={styles.tabContent}>
        {tab === 'market' && (
          <>
            <h3 style={styles.sectionTitle}>📢 Find Leads</h3>
            <div style={styles.actionGrid}>
              <button onClick={(e) => prospect('cold', e)} disabled={actions < 1} style={styles.actionBtn}>
                <span>📞</span>
                <span>Cold Call</span>
                <span style={styles.actionMeta}>1⚡ • Free</span>
              </button>
              <button onClick={(e) => prospect('networking', e)} disabled={actions < 2 || cash < 100} style={styles.actionBtn}>
                <span>🤝</span>
                <span>Network</span>
                <span style={styles.actionMeta}>2⚡ • $100</span>
              </button>
              <button onClick={(e) => prospect('ads', e)} disabled={actions < 2 || cash < 300} style={styles.actionBtn}>
                <span>📺</span>
                <span>Run Ads</span>
                <span style={styles.actionMeta}>2⚡ • $300</span>
              </button>
            </div>
            
            <h3 style={{ ...styles.sectionTitle, marginTop: '16px' }}>🧘 Self Care</h3>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
              <button onClick={rest} disabled={actions < 2} style={{ ...styles.restBtn, flex: 1 }}>
                <span>😴 Rest & Recover</span>
                <span style={{ fontSize: '10px', opacity: 0.8 }}>2⚡ • +25 Energy</span>
              </button>
            </div>
            
            <h3 style={{ ...styles.sectionTitle, marginTop: '12px' }}>🎯 Your Specializations</h3>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
              {specializations.map(s => {
                const spec = SPECIALIZATIONS.find(x => x.id === s);
                return (
                  <div key={s} style={styles.specBadge}>
                    <span>{spec?.icon}</span>
                    <span style={{ fontSize: '11px', fontWeight: '600' }}>{spec?.name}</span>
                </div>
                );
              })}
            </div>
            
            {/* Save and End Month buttons */}
            <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
              <button 
                onClick={() => setShowSaveModal(true)} 
                style={{
                  padding: '14px 16px',
                  background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                  border: 'none',
                  borderRadius: '12px',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  position: 'relative',
                }}
              >
                💾 Save
                {showSaveNotice && (
                  <span style={{
                    position: 'absolute',
                    top: '-8px',
                    right: '-8px',
                    background: '#22c55e',
                    color: 'white',
                    fontSize: '10px',
                    padding: '2px 6px',
                    borderRadius: '8px',
                    animation: 'popIn 0.3s ease',
                  }}>✓</span>
                )}
              </button>
              <button onClick={endMonth} style={{ ...styles.endMonthBtn, flex: 1, marginTop: 0 }}>
                📅 End Month → {netCashFlow >= 0 ? `+$${netCashFlow.toLocaleString()}` : `-$${Math.abs(netCashFlow).toLocaleString()}`}
              </button>
            </div>
            
            {/* Save Modal */}
            {showSaveModal && (
              <div style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,0.7)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 100,
                padding: '20px',
              }}>
                <div style={{
                  background: 'white',
                  borderRadius: '16px',
                  padding: '20px',
                  maxWidth: '340px',
                  width: '100%',
                }}>
                  <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b', marginBottom: '16px', textAlign: 'center' }}>
                    💾 Save Game
                  </h3>
                  
                  {/* Save Slots */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
                    {getSaveSlots().map(slot => (
                      <button
                        key={slot.slot}
                        onClick={() => {
                          if (!slot.exists || window.confirm('Overwrite this save?')) {
                            saveGameToSlot(slot.slot);
                            setShowSaveModal(false);
                          }
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '12px',
                          background: '#f8fafc',
                          border: '2px solid #e2e8f0',
                          borderRadius: '10px',
                          cursor: 'pointer',
                          textAlign: 'left',
                        }}
                      >
                        <div style={{
                          width: '36px',
                          height: '36px',
                          background: '#dbeafe',
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: '700',
                          color: '#3b82f6',
                        }}>
                          {slot.slot}
                        </div>
                        <div style={{ flex: 1 }}>
                          {slot.exists ? (
                            <>
                              <div style={{ fontWeight: '600', fontSize: '12px', color: '#1e293b' }}>
                                Month {slot.month} • ${slot.cash?.toLocaleString()}
                              </div>
                              <div style={{ fontSize: '10px', color: '#64748b' }}>
                                {new Date(slot.savedAt).toLocaleDateString()} {new Date(slot.savedAt).toLocaleTimeString()}
                              </div>
                            </>
                          ) : (
                            <div style={{ fontSize: '12px', color: '#94a3b8' }}>Empty Slot</div>
                          )}
                        </div>
                        <span style={{ color: '#64748b' }}>→</span>
                      </button>
                    ))}
                  </div>
                  
                  {/* Export Code Section */}
                  <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '12px', marginBottom: '12px' }}>
                    <div style={{ fontSize: '11px', fontWeight: '600', color: '#64748b', marginBottom: '8px' }}>
                      ☁️ CLOUD SAVE (share across devices)
                    </div>
                    <button
                      onClick={() => {
                        const code = exportSaveCode();
                        if (code) {
                          navigator.clipboard.writeText(code).then(() => {
                            notify('Save code copied to clipboard!', 'success');
                          }).catch(() => {
                            // Fallback for older browsers
                            prompt('Copy this save code:', code);
                          });
                        }
                      }}
                      style={{
                        width: '100%',
                        padding: '10px',
                        background: '#f0f9ff',
                        border: '2px solid #bae6fd',
                        borderRadius: '8px',
                        color: '#0369a1',
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: 'pointer',
                      }}
                    >
                      📋 Copy Save Code
                    </button>
                    <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '6px', textAlign: 'center' }}>
                      Use this code to continue on another device
                    </div>
                  </div>
                  
                  <button
                    onClick={() => setShowSaveModal(false)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: '#f1f5f9',
                      border: 'none',
                      borderRadius: '10px',
                      color: '#64748b',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </>
        )}
        
        {tab === 'leads' && (
          <>
            <h3 style={styles.sectionTitle}>📋 Lead Pipeline ({leads.length})</h3>
            {leads.length === 0 ? (
              <div style={styles.empty}>
                <span style={{ fontSize: '36px' }}>🔍</span>
                <p>No leads! Go to Market tab to find prospects.</p>
              </div>
            ) : (
              <div style={styles.leadList}>
                {leads.map(lead => (
                  <div key={lead.id} style={styles.leadCard}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '24px' }}>{lead.industryData.icon}</span>
                        <div>
                          <div style={{ fontWeight: '700', fontSize: '12px' }}>{lead.businessName}</div>
                          <div style={{ fontSize: '10px', color: '#64748b' }}>{lead.name} • {lead.personalityData.icon} {lead.personality}</div>
                        </div>
                      </div>
                      <TempBadge temp={lead.temp} />
                    </div>
                    
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <span style={{ fontWeight: '700', color: '#22c55e' }}>${lead.budget}/mo</span>
                        {lead.isSpecialized && <span style={{ marginLeft: '6px', fontSize: '10px', color: '#8b5cf6' }}>✨ Specialty</span>}
                      </div>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button 
                          onClick={() => nurtureLead(lead)} 
                          disabled={actions < 1 || lead.temp === 'hot'}
                          style={styles.smallBtn}
                        >
                          ☀️ Warm Up
                        </button>
                        <button 
                          onClick={() => startPitch(lead)} 
                          disabled={actions < 1}
                          style={{ ...styles.smallBtn, background: '#3b82f6', color: 'white' }}
                        >
                          🎯 Pitch
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
        
        {tab === 'clients' && (
          <>
            <h3 style={styles.sectionTitle}>👥 Active Clients ({clients.length})</h3>
            
            {/* Pending demands banner */}
            {pendingDemands.length > 0 && (
              <div style={{ 
                background: '#fef3c7', 
                border: '2px solid #fcd34d', 
                borderRadius: '10px', 
                padding: '10px', 
                marginBottom: '10px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{ fontSize: '20px' }}>📩</span>
                <div>
                  <div style={{ fontWeight: '700', fontSize: '12px', color: '#92400e' }}>
                    {pendingDemands.length} Client Request{pendingDemands.length > 1 ? 's' : ''}!
                  </div>
                  <div style={{ fontSize: '10px', color: '#78350f' }}>
                    Handle or ignore - consequences either way
                  </div>
                </div>
              </div>
            )}
            
            {clients.length === 0 ? (
              <div style={styles.empty}>
                <span style={{ fontSize: '36px' }}>🤝</span>
                <p>No clients yet! Pitch leads to sign them.</p>
              </div>
            ) : (
              <div style={styles.leadList}>
                {clients.map(client => {
                  const clientDemands = pendingDemands.filter(d => d.clientId === client.id);
                  const isAtRisk = client.satisfaction < 40;
                  const isCritical = client.satisfaction < 25;
                  
                  return (
                    <div key={client.id} style={{
                      ...styles.clientCard,
                      borderColor: isCritical ? '#ef4444' : isAtRisk ? '#fcd34d' : '#e2e8f0',
                      borderWidth: isCritical ? '2px' : '1px',
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '24px' }}>{client.industryData.icon}</span>
                          <div>
                            <div style={{ fontWeight: '700', fontSize: '12px' }}>
                              {client.businessName}
                              {clientDemands.length > 0 && (
                                <span style={{ marginLeft: '6px', background: '#fef3c7', color: '#92400e', padding: '2px 6px', borderRadius: '4px', fontSize: '9px' }}>
                                  {clientDemands.length} request{clientDemands.length > 1 ? 's' : ''}
                                </span>
                              )}
                            </div>
                            <div style={{ fontSize: '10px', color: '#64748b' }}>
                              {client.firstName} • {client.personalityData.icon} {client.personality} • {client.monthsRetained}mo
                            </div>
                          </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontWeight: '700', color: '#22c55e' }}>${client.budget}/mo</div>
                        </div>
                      </div>
                      
                      {/* Warning banner */}
                      {isCritical && (
                        <div style={{ background: '#fef2f2', borderRadius: '6px', padding: '6px', marginBottom: '8px', fontSize: '10px', color: '#b91c1c', textAlign: 'center' }}>
                          ⚠️ CRITICAL: May leave soon! Run a campaign & handle requests!
                        </div>
                      )}
                      {isAtRisk && !isCritical && (
                        <div style={{ background: '#fef3c7', borderRadius: '6px', padding: '6px', marginBottom: '8px', fontSize: '10px', color: '#92400e', textAlign: 'center' }}>
                          ⚠️ At risk of churning. Improve satisfaction!
                        </div>
                      )}
                      
                      <div style={{ marginBottom: '8px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', marginBottom: '2px' }}>
                          <span>Satisfaction {client.satisfaction < 30 && '⚠️'}</span>
                          <span style={{ color: client.satisfaction > 60 ? '#22c55e' : client.satisfaction > 30 ? '#f59e0b' : '#ef4444' }}>
                            {client.satisfaction}%
                          </span>
                        </div>
                        <ProgressBar 
                          value={client.satisfaction} 
                          max={100} 
                          color={client.satisfaction > 60 ? '#22c55e' : client.satisfaction > 30 ? '#f59e0b' : '#ef4444'} 
                          h={5} 
                        />
                      </div>
                      
                      {client.campaign ? (
                        <div style={{ background: '#f0fdf4', borderRadius: '6px', padding: '8px', fontSize: '11px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span>{client.campaign.icon} {client.campaign.name}</span>
                            <span style={{ color: '#64748b' }}>{client.campaign.monthsLeft}mo left</span>
                          </div>
                        </div>
                      ) : (
                        <button onClick={() => planCampaign(client)} style={{
                          ...styles.campaignBtn,
                          background: isCritical ? '#fef2f2' : '#fef3c7',
                          borderColor: isCritical ? '#fca5a5' : '#fcd34d',
                          animation: !client.campaign && isCritical ? 'pulse 1s infinite' : 'none',
                        }}>
                          📋 Plan Campaign {isCritical ? '(URGENT!)' : '(Needed!)'}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            
            <div style={{ marginTop: '12px', background: '#fef2f2', borderRadius: '8px', padding: '10px', fontSize: '11px', color: '#b91c1c' }}>
              <strong>⚠️ Client Churn:</strong> Below 30% satisfaction = high risk of leaving. Below 20% = will definitely leave!
            </div>
          </>
        )}
        
        {tab === 'team' && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <h3 style={styles.sectionTitle}>🏢 Your Team ({employees.length}/{officeData.maxEmployees})</h3>
              <button onClick={hireEmployee} disabled={cash < 2500 || employees.length >= officeData.maxEmployees} style={styles.hireBtn}>
                + Hire
              </button>
            </div>
            
            {employees.length === 0 ? (
              <div style={styles.empty}>
                <span style={{ fontSize: '36px' }}>🪑</span>
                <p>No employees yet! Hire to scale your agency and reduce burnout.</p>
              </div>
            ) : (
              <div style={styles.leadList}>
                {employees.map(emp => (
                  <div key={emp.id} style={{
                    ...styles.empCard,
                    borderColor: emp.morale < 30 ? '#fca5a5' : emp.morale < 50 ? '#fcd34d' : '#e2e8f0',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ fontSize: '28px' }}>{emp.icon}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '700', fontSize: '12px' }}>
                          {emp.name}
                          {emp.trainingLevel > 0 && <span style={{ marginLeft: '6px', fontSize: '10px', color: '#8b5cf6' }}>🎓{emp.trainingLevel}%</span>}
                        </div>
                        <div style={{ fontSize: '10px', color: '#64748b' }}>{emp.role} • {emp.personalityData.icon} {emp.personality}</div>
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '4px' }}>
                          {emp.effectiveBonusActions > 0 && <span style={styles.empBadge}>+{emp.effectiveBonusActions} actions</span>}
                          {emp.effectiveAutoClose > 0 && <span style={styles.empBadge}>{emp.effectiveAutoClose}% auto-close</span>}
                          {emp.effectiveClientBonus > 0 && <span style={styles.empBadge}>+{emp.effectiveClientBonus}% satisfaction</span>}
                          {emp.effectiveCampaignBonus > 0 && <span style={styles.empBadge}>+{emp.effectiveCampaignBonus}% campaigns</span>}
                          {emp.effectiveLeadBonus > 0 && <span style={styles.empBadge}>+{emp.effectiveLeadBonus} leads</span>}
                          {emp.loyaltyMonths > 0 && <span style={{ ...styles.empBadge, background: '#dcfce7', color: '#166534' }}>🔒{emp.loyaltyMonths}mo</span>}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '11px', color: '#ef4444' }}>-${emp.salary}/mo</div>
                      </div>
                    </div>
                    
                    {/* Morale bar */}
                    <div style={{ marginTop: '6px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', marginBottom: '2px' }}>
                        <span>Morale {emp.morale < 30 && '⚠️'}</span>
                        <span style={{ color: emp.morale < 30 ? '#ef4444' : emp.morale < 50 ? '#f59e0b' : '#22c55e' }}>{emp.morale}%</span>
                      </div>
                      <ProgressBar value={emp.morale} max={100} color={emp.morale > 50 ? '#22c55e' : emp.morale > 30 ? '#f59e0b' : '#ef4444'} h={4} />
                    </div>
                    
                    {/* Action buttons */}
                    <div style={{ display: 'flex', gap: '6px', marginTop: '8px' }}>
                      <button 
                        onClick={() => { setSelectedEmployee(emp); setEmployeeModal('train'); }}
                        style={{ flex: 1, padding: '6px', background: '#e0e7ff', border: '1px solid #a5b4fc', borderRadius: '6px', fontSize: '10px', fontWeight: '600', color: '#3730a3' }}
                      >
                        📚 Train
                      </button>
                      <button 
                        onClick={() => { setSelectedEmployee(emp); setEmployeeModal('bonus'); }}
                        style={{ flex: 1, padding: '6px', background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: '6px', fontSize: '10px', fontWeight: '600', color: '#92400e' }}
                      >
                        💰 Bonus
                      </button>
                      <button 
                        onClick={() => fireEmployee(emp)} 
                        style={{ padding: '6px 10px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '6px', fontSize: '10px', fontWeight: '600', color: '#b91c1c' }}
                      >
                        Fire
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            <div style={{ marginTop: '16px', background: '#f0f9ff', borderRadius: '10px', padding: '12px' }}>
              <h4 style={{ fontSize: '11px', fontWeight: '700', color: '#0369a1', marginBottom: '6px' }}>💡 EMPLOYEE MANAGEMENT:</h4>
              <ul style={{ fontSize: '11px', color: '#64748b', margin: 0, paddingLeft: '16px' }}>
                <li><strong>Train:</strong> Increase effectiveness (+bonuses)</li>
                <li><strong>Bonus:</strong> Boost morale, prevent quitting</li>
                <li><strong>Morale:</strong> Decays monthly - keep above 30%!</li>
                <li>Low morale = employees may quit</li>
              </ul>
            </div>
          </>
        )}
        
        {tab === 'finance' && (
          <>
            <h3 style={styles.sectionTitle}>💰 Monthly P&L</h3>
            <div style={styles.plCard}>
              <div style={styles.plRow}>
                <span>Client Revenue</span>
                <span style={{ color: '#22c55e', fontWeight: '700' }}>+${monthlyRevenue.toLocaleString()}</span>
              </div>
              <div style={{ ...styles.plRow, color: '#64748b', fontSize: '11px' }}>
                <span>└ Salaries ({employees.length} employees)</span>
                <span style={{ color: '#ef4444' }}>-${salaries.toLocaleString()}</span>
              </div>
              <div style={{ ...styles.plRow, color: '#64748b', fontSize: '11px' }}>
                <span>└ Office ({officeData.name})</span>
                <span style={{ color: '#ef4444' }}>-${officeData.cost.toLocaleString()}</span>
              </div>
              <div style={{ ...styles.plRow, color: '#64748b', fontSize: '11px' }}>
                <span>└ Overhead & Tools</span>
                <span style={{ color: '#ef4444' }}>-${(overhead - officeData.cost).toLocaleString()}</span>
              </div>
              {debt > 0 && (
                <div style={{ ...styles.plRow, color: '#64748b', fontSize: '11px' }}>
                  <span>└ Debt Interest (8%)</span>
                  <span style={{ color: '#ef4444' }}>-${debtPayment.toLocaleString()}</span>
                </div>
              )}
              <div style={{ ...styles.plRow, borderTop: '2px solid #e2e8f0', paddingTop: '8px', marginTop: '8px' }}>
                <span style={{ fontWeight: '700' }}>Net Cash Flow</span>
                <span style={{ fontWeight: '700', color: netCashFlow >= 0 ? '#22c55e' : '#ef4444' }}>
                  {netCashFlow >= 0 ? '+' : ''}{netCashFlow.toLocaleString()}/mo
                </span>
              </div>
              {netCashFlow < 0 && (
                <div style={{ fontSize: '10px', color: '#f59e0b', marginTop: '4px' }}>
                  ⚠️ Runway: ~{runway} months at current burn rate
                </div>
              )}
            </div>
            
            {/* Debt Section */}
            <h3 style={{ ...styles.sectionTitle, marginTop: '16px' }}>🏦 Business Loan</h3>
            <div style={styles.plCard}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '12px' }}>Current Debt:</span>
                <span style={{ fontSize: '14px', fontWeight: '700', color: debt > 0 ? '#ef4444' : '#22c55e' }}>
                  ${debt.toLocaleString()}
                </span>
              </div>
              {debt > 0 && (
                <div style={{ fontSize: '10px', color: '#64748b', marginBottom: '8px' }}>
                  Monthly interest: ${debtPayment.toLocaleString()} (8%)
                </div>
              )}
              <div style={{ display: 'flex', gap: '6px' }}>
                <button onClick={() => takeLoan(5000)} disabled={debt > cash * 2} style={styles.loanBtn}>
                  Borrow $5K
                </button>
                <button onClick={() => takeLoan(10000)} disabled={debt > cash * 2} style={styles.loanBtn}>
                  Borrow $10K
                </button>
                {debt > 0 && (
                  <button onClick={() => payDebt(Math.min(debt, cash))} disabled={cash <= 0} style={{ ...styles.loanBtn, background: '#dcfce7', color: '#166534' }}>
                    Pay Off
                  </button>
                )}
              </div>
            </div>
            
            {/* Office Upgrade */}
            <h3 style={{ ...styles.sectionTitle, marginTop: '16px' }}>🏢 Office</h3>
            <div style={styles.plCard}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: '700' }}>{officeData.name}</div>
                  <div style={{ fontSize: '10px', color: '#64748b' }}>
                    Capacity: {employees.length}/{officeData.maxEmployees} employees
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '11px', color: '#ef4444' }}>${officeData.cost}/mo</div>
                  {officeData.prestigeBonus > 0 && (
                    <div style={{ fontSize: '10px', color: '#8b5cf6' }}>+{officeData.prestigeBonus} rep</div>
                  )}
                </div>
              </div>
              
              {office !== 'agency' && (
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {office === 'home' && (
                    <button onClick={() => upgradeOffice('small')} disabled={cash < 5000} style={styles.upgradeBtn}>
                      🏠 Small Office ($5K)
                    </button>
                  )}
                  {(office === 'home' || office === 'small') && (
                    <button onClick={() => upgradeOffice('professional')} disabled={cash < 15000} style={styles.upgradeBtn}>
                      🏢 Professional ($15K)
                    </button>
                  )}
                  {office !== 'agency' && (
                    <button onClick={() => upgradeOffice('agency')} disabled={cash < 40000} style={styles.upgradeBtn}>
                      🏛️ Agency HQ ($40K)
                    </button>
                  )}
                </div>
              )}
            </div>
            
            {/* Recent Events */}
            {eventLog.length > 0 && (
              <>
                <h3 style={{ ...styles.sectionTitle, marginTop: '16px' }}>📜 Recent Events</h3>
                <div style={{ maxHeight: '120px', overflowY: 'auto' }}>
                  {eventLog.slice(-5).reverse().map((event, i) => (
                    <div key={i} style={{ 
                      fontSize: '11px', 
                      padding: '6px', 
                      background: event.type === 'negative' ? '#fef2f2' : '#f0fdf4',
                      borderRadius: '6px',
                      marginBottom: '4px'
                    }}>
                      <span style={{ fontWeight: '600' }}>Month {event.month}:</span> {event.name}
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
        
        {tab === 'rivals' && (
          <>
            <h3 style={styles.sectionTitle}>⚔️ Competitive Landscape</h3>
            
            {/* Current Standings Table */}
            <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '12px', marginBottom: '14px' }}>
              <div style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', marginBottom: '8px' }}>CURRENT STANDINGS - MONTH {month}</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                <thead>
                  <tr style={{ background: '#e2e8f0' }}>
                    <th style={{ padding: '8px 6px', textAlign: 'left', borderRadius: '6px 0 0 6px' }}>Rank</th>
                    <th style={{ padding: '8px 6px', textAlign: 'left' }}>Agency</th>
                    <th style={{ padding: '8px 6px', textAlign: 'right' }}>Cash</th>
                    <th style={{ padding: '8px 6px', textAlign: 'right' }}>Clients</th>
                    <th style={{ padding: '8px 6px', textAlign: 'right', borderRadius: '0 6px 6px 0' }}>Rev/mo</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { id: 'player', name: 'Your Agency', icon: '🏠', cash, clients: clients.length, revenue: monthlyRevenue, isPlayer: true },
                    ...rivals
                  ].sort((a, b) => b.cash - a.cash).map((agency, idx) => (
                    <tr key={agency.id} style={{ 
                      background: agency.isPlayer ? '#dbeafe' : idx % 2 === 0 ? 'white' : '#f8fafc',
                      fontWeight: agency.isPlayer ? '700' : '400',
                    }}>
                      <td style={{ padding: '8px 6px' }}>
                        {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `#${idx + 1}`}
                      </td>
                      <td style={{ padding: '8px 6px' }}>
                        <span style={{ marginRight: '4px' }}>{agency.icon}</span>
                        {agency.isPlayer ? 'You' : agency.name}
                      </td>
                      <td style={{ padding: '8px 6px', textAlign: 'right', color: '#22c55e' }}>${agency.cash.toLocaleString()}</td>
                      <td style={{ padding: '8px 6px', textAlign: 'right' }}>{agency.clients}</td>
                      <td style={{ padding: '8px 6px', textAlign: 'right', color: '#64748b' }}>${agency.revenue?.toLocaleString() || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Rival Profiles */}
            <div style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', marginBottom: '8px' }}>RIVAL PROFILES</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '14px' }}>
              {rivals.map(rival => {
                const playerRank = [{ cash }, ...rivals].sort((a, b) => b.cash - a.cash).findIndex(a => a.cash === cash) + 1;
                const rivalRank = [{ cash }, ...rivals].sort((a, b) => b.cash - a.cash).findIndex(a => a.id === rival.id) + 1;
                const isAhead = rival.cash > cash;
                const diff = rival.cash - cash;
                
                return (
                  <div key={rival.id} style={{
                    background: 'white',
                    border: `2px solid ${isAhead ? '#fca5a5' : '#86efac'}`,
                    borderRadius: '10px',
                    padding: '10px',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '24px' }}>{rival.icon}</span>
                        <div>
                          <div style={{ fontWeight: '700', fontSize: '12px' }}>{rival.name}</div>
                          <div style={{ fontSize: '10px', color: '#64748b' }}>{rival.desc}</div>
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '10px', color: '#64748b' }}>Rank #{rivalRank}</div>
                        <div style={{ fontWeight: '700', color: isAhead ? '#ef4444' : '#22c55e', fontSize: '11px' }}>
                          {isAhead ? `$${diff.toLocaleString()} ahead` : `$${Math.abs(diff).toLocaleString()} behind`}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '12px', fontSize: '10px', color: '#64748b' }}>
                      <span>💰 ${rival.cash.toLocaleString()}</span>
                      <span>👥 {rival.clients} clients</span>
                      <span>📈 ${rival.revenue?.toLocaleString() || 0}/mo</span>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Month-by-Month History */}
            {leaderboardHistory.length > 0 && (
              <>
                <div style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', marginBottom: '8px' }}>MONTHLY HISTORY</div>
                <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '10px', maxHeight: '200px', overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
                    <thead>
                      <tr style={{ background: '#e2e8f0' }}>
                        <th style={{ padding: '6px', textAlign: 'left', position: 'sticky', top: 0, background: '#e2e8f0' }}>Month</th>
                        <th style={{ padding: '6px', textAlign: 'center', position: 'sticky', top: 0, background: '#e2e8f0' }}>Your Rank</th>
                        <th style={{ padding: '6px', textAlign: 'right', position: 'sticky', top: 0, background: '#e2e8f0' }}>Your Cash</th>
                        <th style={{ padding: '6px', textAlign: 'right', position: 'sticky', top: 0, background: '#e2e8f0' }}>Leader</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...leaderboardHistory].reverse().map((entry, idx) => (
                        <tr key={entry.month} style={{ background: idx % 2 === 0 ? 'white' : '#f8fafc' }}>
                          <td style={{ padding: '6px' }}>Month {entry.month}</td>
                          <td style={{ padding: '6px', textAlign: 'center' }}>
                            <span style={{
                              background: entry.playerRank === 1 ? '#fef3c7' : entry.playerRank <= 2 ? '#dcfce7' : '#fee2e2',
                              padding: '2px 8px',
                              borderRadius: '4px',
                              fontWeight: '600',
                            }}>
                              {entry.playerRank === 1 ? '🥇' : entry.playerRank === 2 ? '🥈' : entry.playerRank === 3 ? '🥉' : `#${entry.playerRank}`}
                            </span>
                          </td>
                          <td style={{ padding: '6px', textAlign: 'right', color: '#22c55e' }}>${entry.playerCash.toLocaleString()}</td>
                          <td style={{ padding: '6px', textAlign: 'right', fontSize: '9px' }}>
                            {entry.leader.icon} ${entry.leader.cash.toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
            
            {leaderboardHistory.length === 0 && (
              <div style={{ background: '#fef3c7', borderRadius: '10px', padding: '12px', fontSize: '11px', color: '#92400e', textAlign: 'center' }}>
                📊 Complete your first month to see competitive history!
              </div>
            )}
            
            {/* Competition Tips */}
            <div style={{ marginTop: '14px', background: '#f0f9ff', borderRadius: '10px', padding: '12px' }}>
              <h4 style={{ fontSize: '11px', fontWeight: '700', color: '#0369a1', marginBottom: '6px' }}>💡 COMPETITIVE TIPS</h4>
              <ul style={{ fontSize: '10px', color: '#64748b', margin: 0, paddingLeft: '16px' }}>
                <li><strong>🦈 Apex:</strong> May steal your unhappy clients (&lt;60% satisfaction)</li>
                <li><strong>💎 Prestige:</strong> Targets big accounts, less aggressive</li>
                <li><strong>🤖 Scale:</strong> Grows steadily through volume</li>
                <li>Keep clients satisfied to prevent poaching!</li>
              </ul>
            </div>
          </>
        )}
        
        {tab === 'goals' && (
          <>
            <h3 style={styles.sectionTitle}>🏆 Achievements ({unlockedAchievements.length}/{ACHIEVEMENTS.length})</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
              {ACHIEVEMENTS.map(ach => {
                const unlocked = unlockedAchievements.includes(ach.id);
                return (
                  <div key={ach.id} style={{
                    background: unlocked ? '#f0fdf4' : '#f8fafc',
                    border: `2px solid ${unlocked ? '#86efac' : '#e2e8f0'}`,
                    borderRadius: '10px',
                    padding: '10px',
                    opacity: unlocked ? 1 : 0.6,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <span style={{ fontSize: '20px' }}>{unlocked ? ach.icon : '🔒'}</span>
                      <div style={{ fontWeight: '700', fontSize: '11px', color: unlocked ? '#166534' : '#64748b' }}>{ach.name}</div>
                    </div>
                    <div style={{ fontSize: '10px', color: '#64748b' }}>{ach.desc}</div>
                  </div>
                );
              })}
            </div>
            
            <div style={{ marginTop: '16px', background: '#f0f9ff', borderRadius: '10px', padding: '12px' }}>
              <h4 style={{ fontSize: '11px', fontWeight: '700', color: '#0369a1', marginBottom: '6px' }}>📊 Your Stats</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '6px', fontSize: '11px', color: '#64748b' }}>
                <div>Leads Generated: <strong>{stats.totalLeadsGenerated}</strong></div>
                <div>Clients Won: <strong>{stats.totalClientsWon}</strong></div>
                <div>Employees Hired: <strong>{stats.totalHires}</strong></div>
                <div>Pitches Made: <strong>{stats.totalPitches}</strong></div>
                <div>Perfect Pitches: <strong>{stats.perfectPitches}</strong></div>
                <div>Peak Cash: <strong>${stats.peakCash.toLocaleString()}</strong></div>
                <div>Best Retention: <strong>{stats.longestClientRetention} mo</strong></div>
                <div>Max Clients: <strong>{stats.maxClients}</strong></div>
              </div>
            </div>
          </>
        )}
      </div>
      
      {/* Vendero Apps Footer */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        padding: '12px',
        marginTop: '8px',
        opacity: 0.6,
      }}>
        <div style={{
          width: '20px',
          height: '20px',
          background: 'linear-gradient(135deg, #d4af37, #b8860b)',
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <span style={{
            fontSize: '10px',
            fontWeight: '800',
            color: '#1a1a1a',
            fontFamily: 'Georgia, serif',
            letterSpacing: '-1px',
          }}>VA</span>
        </div>
        <span style={{
          fontSize: '10px',
          color: '#64748b',
          fontWeight: '500',
          letterSpacing: '1px',
        }}>VENDERO APPS</span>
      </div>
    </div>
  );
};

// ============ STYLES ============

const animationCSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
  * { box-sizing: border-box; }
  html { 
    height: 100%; 
    overflow: auto !important;
  }
  body { 
    height: auto !important;
    min-height: 100%;
    overflow-y: scroll !important; 
    overflow-x: hidden !important;
    -webkit-overflow-scrolling: touch !important;
    position: relative !important;
    margin: 0;
    padding: 0;
  }
  #root { 
    min-height: 100%; 
    height: auto !important;
    overflow: visible !important;
  }
  button { transition: all 0.15s ease; cursor: pointer; font-family: inherit; }
  button:hover:not(:disabled) { transform: scale(1.02); filter: brightness(1.05); }
  button:active:not(:disabled) { transform: scale(0.98); }
  button:disabled { opacity: 0.5; cursor: not-allowed; }
  @keyframes floatUp { 0% { opacity: 1; transform: translateY(0); } 100% { opacity: 0; transform: translateY(-50px); } }
  @keyframes popIn { 0% { transform: scale(0.8); opacity: 0; } 100% { transform: scale(1); opacity: 1; } }
  @keyframes fadeIn { 0% { opacity: 0; } 100% { opacity: 1; } }
  @keyframes fadeInUp { 0% { opacity: 0; transform: translateY(30px); } 100% { opacity: 1; transform: translateY(0); } }
  @keyframes bounce { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
  @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.7; } }
  @keyframes slideIn { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
  @keyframes float { 0%, 100% { transform: translateY(0px) rotate(0deg); } 50% { transform: translateY(-10px) rotate(2deg); } }
  @keyframes glow { 0%, 100% { box-shadow: 0 0 20px rgba(59, 130, 246, 0.5); } 50% { box-shadow: 0 0 40px rgba(59, 130, 246, 0.8); } }
  @keyframes typewriter { from { width: 0; } to { width: 100%; } }
  @keyframes blink { 50% { opacity: 0; } }
  @keyframes coinSpin { 0% { transform: rotateY(0deg); } 100% { transform: rotateY(360deg); } }
  @keyframes scaleIn { 0% { transform: scale(0); opacity: 0; } 50% { transform: scale(1.1); } 100% { transform: scale(1); opacity: 1; } }
  @keyframes slideRight { 0% { transform: translateX(-100%); opacity: 0; } 100% { transform: translateX(0); opacity: 1; } }
  input[type="range"] { -webkit-appearance: none; height: 6px; background: #e2e8f0; border-radius: 3px; }
  input[type="range"]::-webkit-slider-thumb { -webkit-appearance: none; width: 18px; height: 18px; background: #3b82f6; border-radius: 50%; cursor: pointer; }
`;

const styles = {
  container: { 
    minHeight: '100%',
    background: 'linear-gradient(180deg,#f8fafc,#e2e8f0)', 
    fontFamily: '"DM Sans",system-ui,sans-serif', 
    padding: '12px', 
    paddingBottom: '80px',
    maxWidth: '480px', 
    margin: '0 auto',
  },
  overlay: { minHeight: '100vh', background: 'linear-gradient(180deg,#f8fafc,#e2e8f0)', fontFamily: '"DM Sans",system-ui,sans-serif', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' },
  notif: { position: 'fixed', top: '12px', left: '50%', transform: 'translateX(-50%)', padding: '10px 18px', borderRadius: '12px', border: '2px solid', fontWeight: '600', fontSize: '13px', zIndex: 1000, animation: 'slideIn .3s ease' },
  
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' },
  cashDisplay: { fontSize: '26px', fontWeight: '800', color: '#22c55e' },
  monthDisplay: { fontSize: '14px', fontWeight: '700', color: '#1e293b' },
  subtext: { fontSize: '11px', color: '#64748b' },
  
  milestoneBar: { background: 'white', borderRadius: '10px', padding: '10px 12px', marginBottom: '8px', boxShadow: '0 2px 6px rgba(0,0,0,.04)' },
  milestoneInfo: { display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' },
  
  marketBanner: { display: 'flex', alignItems: 'center', gap: '10px', borderRadius: '10px', padding: '10px 12px', marginBottom: '8px', border: '1px solid #e2e8f0' },
  
  quickStats: { display: 'flex', justifyContent: 'space-between', background: 'white', borderRadius: '10px', padding: '10px', marginBottom: '10px', boxShadow: '0 2px 6px rgba(0,0,0,.04)' },
  quickStat: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' },
  
  tabs: { display: 'flex', gap: '5px', marginBottom: '10px', overflowX: 'auto', WebkitOverflowScrolling: 'touch', paddingBottom: '4px' },
  tab: { display: 'flex', alignItems: 'center', padding: '8px 12px', background: 'white', border: '2px solid #e2e8f0', borderRadius: '10px', fontSize: '12px', fontWeight: '600', color: '#64748b', whiteSpace: 'nowrap', flexShrink: 0 },
  tabActive: { background: '#3b82f6', borderColor: '#3b82f6', color: 'white' },
  
  tabContent: { background: 'white', borderRadius: '14px', padding: '14px', marginBottom: '20px' },
  sectionTitle: { fontSize: '12px', fontWeight: '700', color: '#64748b', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '.5px', margin: 0 },
  
  actionGrid: { display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '8px', marginBottom: '12px' },
  actionBtn: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', padding: '14px 8px', background: 'linear-gradient(135deg,#3b82f6,#2563eb)', border: 'none', borderRadius: '12px', color: 'white', fontSize: '12px', fontWeight: '600' },
  actionMeta: { fontSize: '9px', opacity: 0.8 },
  
  specBadge: { display: 'flex', alignItems: 'center', gap: '4px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '8px', padding: '6px 10px' },
  
  endMonthBtn: { width: '100%', padding: '14px', background: 'linear-gradient(135deg,#1e293b,#334155)', border: 'none', borderRadius: '12px', color: 'white', fontSize: '14px', fontWeight: '700', marginTop: '16px' },
  
  empty: { textAlign: 'center', padding: '24px', color: '#64748b' },
  
  leadList: { display: 'flex', flexDirection: 'column', gap: '10px' },
  leadCard: { background: '#f8fafc', borderRadius: '10px', padding: '12px', border: '1px solid #e2e8f0' },
  clientCard: { background: '#f8fafc', borderRadius: '10px', padding: '12px', border: '1px solid #e2e8f0' },
  empCard: { background: '#f8fafc', borderRadius: '10px', padding: '12px', border: '1px solid #e2e8f0' },
  
  smallBtn: { padding: '6px 10px', background: '#e2e8f0', border: 'none', borderRadius: '6px', fontSize: '10px', fontWeight: '600', color: '#475569' },
  hireBtn: { padding: '6px 12px', background: '#22c55e', border: 'none', borderRadius: '8px', color: 'white', fontSize: '11px', fontWeight: '700' },
  campaignBtn: { width: '100%', padding: '8px', background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: '6px', fontSize: '11px', fontWeight: '600', color: '#92400e' },
  
  plCard: { background: '#f8fafc', borderRadius: '10px', padding: '12px' },
  plRow: { display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '4px' },
  
  loanBtn: { flex: 1, padding: '8px', background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: '8px', fontSize: '10px', fontWeight: '600', color: '#92400e' },
  upgradeBtn: { padding: '8px 12px', background: '#e0e7ff', border: '1px solid #a5b4fc', borderRadius: '8px', fontSize: '10px', fontWeight: '600', color: '#3730a3' },
  restBtn: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', padding: '12px', background: 'linear-gradient(135deg, #a855f7, #7c3aed)', border: 'none', borderRadius: '12px', color: 'white', fontWeight: '600', fontSize: '12px' },
  
  empBadge: { display: 'inline-block', padding: '2px 6px', background: '#dbeafe', color: '#1e40af', borderRadius: '4px', fontSize: '9px', fontWeight: '600' },
  specBadge: { display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 10px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '8px' },
};

const modal = {
  container: { background: 'white', borderRadius: '18px', padding: '24px', maxWidth: '400px', width: '100%', boxShadow: '0 8px 32px rgba(0,0,0,.1)' },
  title: { fontSize: '20px', fontWeight: '800', color: '#1e293b', marginBottom: '4px', textAlign: 'center' },
  subtitle: { fontSize: '13px', color: '#64748b', marginBottom: '16px', textAlign: 'center' },
  label: { fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', marginBottom: '6px' },
  primaryBtn: { width: '100%', padding: '12px', background: 'linear-gradient(135deg,#3b82f6,#2563eb)', border: 'none', borderRadius: '12px', color: 'white', fontSize: '14px', fontWeight: '700', cursor: 'pointer' },
  secondaryBtn: { flex: 1, padding: '10px', background: '#f1f5f9', border: 'none', borderRadius: '10px', color: '#64748b', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
  specCard: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', padding: '12px 8px', background: 'white', border: '2px solid #e2e8f0', borderRadius: '10px', cursor: 'pointer', textAlign: 'center' },
  option: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', padding: '10px 8px', background: 'white', border: '2px solid #e2e8f0', borderRadius: '10px', cursor: 'pointer' },
  approachBtn: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', padding: '16px', background: 'white', border: '2px solid #e2e8f0', borderRadius: '12px', cursor: 'pointer' },
};

export default MarketingMillionaire;
